import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { Water } from 'three/addons/objects/Water.js';
import { Sky } from 'three/addons/objects/Sky.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { loadModel, loadCachedModel, updateAnimations } from './modelLoader.js';
import { createProceduralSand } from './proceduralSand.js';
import { initErrorHandlers } from './client-logger.js';
import { eventBus, GameEvents } from './systems/events.js';
import { loadGameState, getGameState, saveGameState } from './systems/state.js';
import { initInteractionSystem, registerInteractable, unregisterInteractable, getPointerNDC, updateInteractableMesh, updateInteraction } from './interaction.js';
import { initDiscovery, getDiscoveryCount, getDiscoveryMap } from './ui/discovery.js';
import { initJournal } from './ui/journal.js';
import { initSpeechBubble, showSpeechBubble, updateSpeechBubble } from './ui/speech-bubble.js';
import { initInventory } from './ui/inventory.js';
import { initCollectionAnimation } from './ui/collectionAnimation.js';
import { initCraftingMenu } from './ui/craftingMenu.js';
import { initUpgradeMenu } from './ui/upgradeMenu.js';
import { initUndoPlacement } from './ui/undoPlacement.js';
import { initSignCustomization } from './ui/signCustomization.js';
import { initSounds, playHum, playGuitarFlourish, playDolphinChirp, playSoundAt, playNoteCatch, playSound, updateListenerPosition } from './sounds.js';
import { CREATURE, INTERACTION, PARTICLES, DISCOVERY, BEACH_BUM, ANIMATION, EXPANSION } from './systems/balance.js';
import { initResourceSpawner, updateResourceSpawner, spawnResource, removeResource, updateSpawnerSandMesh, addExclusionZone } from './systems/resourceSpawner.js';
import { addToInventory } from './systems/resources.js';
import { registerCraftableCallbacks, updateWindChimes } from './meshes/craftables.js';
import { initPlacement, setPalmTreePositions, updateSandCastleErosion, updatePlacementSandMesh } from './systems/placement.js';
import { hasUpgrade } from './systems/upgrades.js';
import { createResourceMesh } from './meshes/resources.js';

// Initialize error logging
initErrorHandlers();

// Debug event logging — enable with: window.DEBUG_EVENTS = true
Object.values(GameEvents).forEach(eventName => {
  eventBus.on(eventName, (data) => {
    if (window.DEBUG_EVENTS) {
      console.log(`[Event] ${eventName}`, data);
    }
  });
});

// Track loaded Meshy models for animation updates
const meshyModels = [];

// Loading progress tracking
const loadingProgress = {
  total: 7, // sand, crabs, dolphins, seagulls, chest, palm trees, environment
  loaded: 0,
  messages: [
    'Preparing the beach...',
    'Summoning the crabs...',
    'Dolphins incoming...',
    'Seagulls taking flight...',
    'Burying treasure...',
    'Planting palm trees...',
    'Adding finishing touches...',
    'Paradise ready!'
  ]
};

function updateLoadingProgress(item) {
  loadingProgress.loaded++;
  const percent = Math.round((loadingProgress.loaded / loadingProgress.total) * 100);
  const progressBar = document.getElementById('progress-bar');
  const statusText = document.getElementById('loading-status');

  if (progressBar) {
    progressBar.style.width = `${percent}%`;
  }
  if (statusText) {
    const msgIndex = Math.min(loadingProgress.loaded, loadingProgress.messages.length - 1);
    statusText.textContent = loadingProgress.messages[msgIndex];
  }
  console.log(`Loading: ${item} (${percent}%)`);
}

// Raycaster for terrain following
const groundRaycaster = new THREE.Raycaster();
const downVector = new THREE.Vector3(0, -1, 0);
let sandModel = null;  // Reference to sand model for raycasting

// ============================================================================
// SCENE SETUP
// ============================================================================

const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 20000);

// WebGL 2.0 optimized renderer
const renderer = new THREE.WebGLRenderer({
  antialias: true,
  powerPreference: 'high-performance',
  stencil: false,  // Disable if not needed for slight perf gain
});

// Log WebGL version
if (renderer.capabilities.isWebGL2) {
  console.log('WebGL 2.0 enabled - using optimized rendering path');
} else {
  console.warn('WebGL 2.0 not available, falling back to WebGL 1.0');
}

renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 0.2;  // Lower exposure
document.body.appendChild(renderer.domElement);

// Camera position
camera.position.set(50, 30, 50);
camera.lookAt(0, 0, 0);

// Controls
const controls = new OrbitControls(camera, renderer.domElement);
controls.enableDamping = true;
controls.dampingFactor = 0.05;
controls.maxPolarAngle = Math.PI * 0.495;
controls.minDistance = 20;
controls.maxDistance = 200;

// Disable orbit rotation during placement mode (keep pan and zoom)
eventBus.on(GameEvents.PLACEMENT_STARTED, () => {
  controls.enableRotate = false;
});
eventBus.on(GameEvents.PLACEMENT_CANCELLED, () => {
  controls.enableRotate = true;
});
eventBus.on(GameEvents.OBJECT_PLACED, () => {
  controls.enableRotate = true;
});

// Post-processing
const composer = new EffectComposer(renderer);
const renderPass = new RenderPass(scene, camera);
composer.addPass(renderPass);

const bloomPass = new UnrealBloomPass(
  new THREE.Vector2(
    Math.round(window.innerWidth / 2),
    Math.round(window.innerHeight / 2)
  ),
  0.1,  // strength - reduced
  0.4,  // radius
  0.95  // threshold - higher to only bloom brightest things
);
composer.addPass(bloomPass);

// ============================================================================
// LIGHTING
// ============================================================================

const ambientLight = new THREE.AmbientLight(0xffffff, 1.2);  // Brighter ambient
scene.add(ambientLight);

const sunLight = new THREE.DirectionalLight(0xffffff, 2.5);  // Brighter sun
sunLight.position.set(100, 100, 50);
sunLight.castShadow = true;
sunLight.shadow.mapSize.width = 1024;
sunLight.shadow.mapSize.height = 1024;
sunLight.shadow.camera.near = 0.5;
sunLight.shadow.camera.far = 300;
sunLight.shadow.camera.left = -100;
sunLight.shadow.camera.right = 100;
sunLight.shadow.camera.top = 100;
sunLight.shadow.camera.bottom = -100;
scene.add(sunLight);

// Add hemisphere light for natural sky/ground color blending
const hemiLight = new THREE.HemisphereLight(0x87CEEB, 0xf4d03f, 0.8);
scene.add(hemiLight);

// Environment map for realistic reflections (generated from sky colors)
const pmremGenerator = new THREE.PMREMGenerator(renderer);
pmremGenerator.compileEquirectangularShader();

// Create a simple environment map from gradient
const envScene = new THREE.Scene();
const envGeo = new THREE.SphereGeometry(500, 32, 32);
const envMat = new THREE.MeshBasicMaterial({
  side: THREE.BackSide,
  vertexColors: true
});

// Add gradient colors to sphere
const colors = [];
const positionAttribute = envGeo.getAttribute('position');
for (let i = 0; i < positionAttribute.count; i++) {
  const y = positionAttribute.getY(i);
  const t = (y + 500) / 1000; // 0 to 1 from bottom to top
  // Gradient from sand color to sky blue
  const r = THREE.MathUtils.lerp(0.95, 0.53, t);
  const g = THREE.MathUtils.lerp(0.82, 0.81, t);
  const b = THREE.MathUtils.lerp(0.60, 0.92, t);
  colors.push(r, g, b);
}
envGeo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
const envMesh = new THREE.Mesh(envGeo, envMat);
envScene.add(envMesh);
const envMap = pmremGenerator.fromScene(envScene).texture;
scene.environment = envMap;

// ============================================================================
// SKY
// ============================================================================

const sky = new Sky();
sky.scale.setScalar(10000);
scene.add(sky);

const skyUniforms = sky.material.uniforms;
skyUniforms['turbidity'].value = 2;          // Lower = clearer sky
skyUniforms['rayleigh'].value = 1;           // Controls blue intensity
skyUniforms['mieCoefficient'].value = 0.001; // Reduce haze
skyUniforms['mieDirectionalG'].value = 0.7;

const sun = new THREE.Vector3();
const phi = THREE.MathUtils.degToRad(90 - 60);  // Lower sun = more blue visible
const theta = THREE.MathUtils.degToRad(180);
sun.setFromSphericalCoords(1, phi, theta);
skyUniforms['sunPosition'].value.copy(sun);

// ============================================================================
// OCEAN
// ============================================================================

const waterGeometry = new THREE.PlaneGeometry(10000, 10000);
const water = new Water(waterGeometry, {
  textureWidth: 512,
  textureHeight: 512,
  waterNormals: new THREE.TextureLoader().load(
    'https://threejs.org/examples/textures/waternormals.jpg',
    (texture) => {
      texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    }
  ),
  sunDirection: new THREE.Vector3(),
  sunColor: 0xffffff,
  waterColor: 0x006994,  // Tropical turquoise
  distortionScale: 2.0,  // Gentler waves
  fog: scene.fog !== undefined,
  alpha: 0.9
});
water.rotation.x = -Math.PI / 2;
water.position.y = 0;
scene.add(water);

// ============================================================================
// ISLAND
// ============================================================================

function createIsland() {
  const islandGroup = new THREE.Group();
  // Island is now just the Meshy sand model
  return islandGroup;
}

const island = createIsland();
scene.add(island);

// ============================================================================
// PALM TREES
// ============================================================================

function createPalmTree(height = 15) {
  const palmGroup = new THREE.Group();

  // Trunk
  const trunkCurve = new THREE.CatmullRomCurve3([
    new THREE.Vector3(0, 0, 0),
    new THREE.Vector3(0.5, height * 0.3, 0.3),
    new THREE.Vector3(0.3, height * 0.6, -0.2),
    new THREE.Vector3(0, height, 0)
  ]);

  const trunkGeometry = new THREE.TubeGeometry(trunkCurve, 20, 0.5, 8, false);
  const trunkMaterial = new THREE.MeshStandardMaterial({
    color: 0x8B4513,
    roughness: 0.9
  });
  const trunk = new THREE.Mesh(trunkGeometry, trunkMaterial);
  trunk.castShadow = true;
  palmGroup.add(trunk);

  // Palm fronds
  const frondCount = 8;
  for (let i = 0; i < frondCount; i++) {
    const angle = (i / frondCount) * Math.PI * 2;
    const frond = createPalmFrond();
    frond.position.set(0, height, 0);
    frond.rotation.y = angle;
    frond.rotation.z = Math.PI / 4 + Math.random() * 0.2;
    palmGroup.add(frond);
  }

  // Coconuts
  const coconutGeometry = new THREE.SphereGeometry(0.4, 8, 8);
  const coconutMaterial = new THREE.MeshStandardMaterial({ color: 0x654321 });
  for (let i = 0; i < 3; i++) {
    const coconut = new THREE.Mesh(coconutGeometry, coconutMaterial);
    coconut.position.set(
      Math.cos(i * 2) * 0.8,
      height - 0.5,
      Math.sin(i * 2) * 0.8
    );
    coconut.castShadow = false;
    palmGroup.add(coconut);
  }

  return palmGroup;
}

function createPalmFrond() {
  const frondGroup = new THREE.Group();

  // Main stem
  const stemCurve = new THREE.CatmullRomCurve3([
    new THREE.Vector3(0, 0, 0),
    new THREE.Vector3(2, -0.5, 0),
    new THREE.Vector3(4, -1.5, 0),
    new THREE.Vector3(6, -3, 0)
  ]);

  const stemGeometry = new THREE.TubeGeometry(stemCurve, 10, 0.08, 4, false);
  const stemMaterial = new THREE.MeshStandardMaterial({ color: 0x228B22 });
  const stem = new THREE.Mesh(stemGeometry, stemMaterial);
  frondGroup.add(stem);

  // Leaves along the stem - with translucency for light passing through
  const leafGeometry = new THREE.PlaneGeometry(0.3, 2);
  const leafMaterial = new THREE.MeshPhysicalMaterial({
    color: 0x32CD32,
    side: THREE.DoubleSide,
    roughness: 0.6,
    metalness: 0.0,
    transmission: 0.3,  // Light passes through leaves
    thickness: 0.1,
    transparent: true,
    opacity: 0.95
  });

  for (let i = 0; i < 15; i++) {
    const t = i / 15;
    const point = stemCurve.getPoint(t);

    // Left leaf
    const leftLeaf = new THREE.Mesh(leafGeometry, leafMaterial);
    leftLeaf.position.copy(point);
    leftLeaf.rotation.z = -Math.PI / 4;
    leftLeaf.rotation.y = -Math.PI / 6;
    frondGroup.add(leftLeaf);

    // Right leaf
    const rightLeaf = new THREE.Mesh(leafGeometry, leafMaterial);
    rightLeaf.position.copy(point);
    rightLeaf.rotation.z = Math.PI / 4;
    rightLeaf.rotation.y = Math.PI / 6;
    frondGroup.add(rightLeaf);
  }

  return frondGroup;
}

// Generate evenly distributed palm tree positions across circular island
function generatePalmTreePositions(islandRadius, safeRadius) {
  const positions = [];

  // Define concentric rings (radius, number of trees)
  const rings = [
    { radius: 8, count: 2 },      // Center cluster
    { radius: 20, count: 4 },     // Inner ring
    { radius: 35, count: 6 }      // Outer ring
  ];

  rings.forEach(ring => {
    const angleStep = (Math.PI * 2) / ring.count;
    const randomOffset = Math.random() * angleStep; // Offset start angle for natural look

    for (let i = 0; i < ring.count; i++) {
      const angle = angleStep * i + randomOffset;

      // Add some randomization to radius and angle for organic feel
      const radiusVariation = ring.radius + (Math.random() - 0.5) * 4;
      const angleVariation = angle + (Math.random() - 0.5) * 0.3;

      const x = Math.cos(angleVariation) * radiusVariation;
      const z = Math.sin(angleVariation) * radiusVariation;

      positions.push({ x, z });
    }
  });

  return positions;
}

// Add palm trees - spread evenly across the island
const palmTrees = [];
const islandRadius = 60;  // Match island size
const safeRadius = islandRadius * 0.4; // Match falloffStart
const treePositions = generatePalmTreePositions(islandRadius, safeRadius);

treePositions.forEach((pos, i) => {
  const tree = createPalmTree(12 + Math.random() * 5);
  tree.position.set(pos.x, 2, pos.z);
  tree.rotation.y = Math.random() * Math.PI * 2;
  scene.add(tree);
  palmTrees.push(tree);
});

// Pass palm tree positions to placement system for constraint checks
setPalmTreePositions(palmTrees.map(t => t.position.clone()));

// Register palm trees as exclusion zones for resource spawning
palmTrees.forEach(tree => addExclusionZone(tree.position, 3.0));

// ============================================================================
// CRABS
// ============================================================================

class Crab {
  constructor(position) {
    this.legs = [];
    this.targetDirection = Math.random() * Math.PI * 2;
    this.changeDirectionTimer = 0;
    this.legPhase = Math.random() * Math.PI * 2;
    this.velocity = new THREE.Vector3(
      (Math.random() - 0.5) * 0.1,
      0,
      (Math.random() - 0.5) * 0.1
    );
    this.scatterState = null; // { timer, duration, direction }
    this.waveState = { active: false, timer: 0 };
    this.claws = []; // References to claw meshes for wave animation
    this._rayOrigin = new THREE.Vector3(); // Perf: reusable raycast origin
    this.mesh = this.createMesh();
    this.mesh.position.copy(position);
  }

  createMesh() {
    const crabGroup = new THREE.Group();

    // Body - realistic shell with clearcoat
    const bodyGeometry = new THREE.SphereGeometry(0.5, 8, 6);
    bodyGeometry.scale(1.2, 0.6, 1);
    const bodyMaterial = new THREE.MeshPhysicalMaterial({
      color: 0xdc143c,
      roughness: 0.4,
      metalness: 0.0,
      clearcoat: 0.7,           // Shiny shell surface
      clearcoatRoughness: 0.2,
      sheen: 0.3,               // Subtle sheen for organic look
      sheenRoughness: 0.5,
      sheenColor: new THREE.Color(0xff6666)
    });
    const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
    body.castShadow = true;
    crabGroup.add(body);

    // Eyes - glossy
    const eyeGeometry = new THREE.SphereGeometry(0.1, 8, 8);
    const eyeMaterial = new THREE.MeshPhysicalMaterial({
      color: 0x000000,
      roughness: 0.1,
      metalness: 0.0,
      clearcoat: 1.0
    });

    const eyeStalkGeometry = new THREE.CylinderGeometry(0.03, 0.03, 0.3);
    const eyeStalkMaterial = new THREE.MeshPhysicalMaterial({
      color: 0xdc143c,
      roughness: 0.5,
      clearcoat: 0.5,
      clearcoatRoughness: 0.3
    });

    [-0.2, 0.2].forEach(x => {
      const eyeStalk = new THREE.Mesh(eyeStalkGeometry, eyeStalkMaterial);
      eyeStalk.position.set(x, 0.4, 0.3);
      crabGroup.add(eyeStalk);

      const eye = new THREE.Mesh(eyeGeometry, eyeMaterial);
      eye.position.set(x, 0.55, 0.3);
      crabGroup.add(eye);
    });

    // Claws - with shell-like clearcoat
    const clawGeometry = new THREE.SphereGeometry(0.25, 8, 8);
    clawGeometry.scale(1.5, 0.8, 0.6);
    const clawMaterial = new THREE.MeshPhysicalMaterial({
      color: 0xb22222,
      roughness: 0.35,
      metalness: 0.0,
      clearcoat: 0.8,
      clearcoatRoughness: 0.15
    });

    [-1, 1].forEach(side => {
      const claw = new THREE.Mesh(clawGeometry, clawMaterial);
      claw.position.set(side * 0.8, 0, 0.2);
      claw.castShadow = false;
      crabGroup.add(claw);
      this.claws.push(claw);
    });

    // Legs - with subtle clearcoat
    const legGeometry = new THREE.CylinderGeometry(0.03, 0.02, 0.5);
    const legMaterial = new THREE.MeshPhysicalMaterial({
      color: 0xdc143c,
      roughness: 0.5,
      clearcoat: 0.4,
      clearcoatRoughness: 0.3
    });

    for (let i = 0; i < 6; i++) {
      const side = i < 3 ? -1 : 1;
      const index = i % 3;

      const leg = new THREE.Mesh(legGeometry, legMaterial);
      leg.position.set(side * 0.5, -0.1, 0.2 - index * 0.3);
      leg.rotation.z = side * Math.PI / 4;
      crabGroup.add(leg);
      this.legs.push(leg);
    }

    crabGroup.scale.setScalar(1.6);  // 100% bigger
    return crabGroup;
  }

  update(deltaTime) {
    this.changeDirectionTimer += deltaTime;

    // Update wave animation if active (crab stays in place during wave)
    if (this.waveState.active) {
      this.waveState.timer += deltaTime;
      const t = this.waveState.timer;
      const waveDuration = this.waveState.duration || 1.1;
      const raiseEnd = 0.3;
      const holdEnd = waveDuration - 0.3; // hold for most of the duration
      const totalEnd = waveDuration;
      let clawRotation = 0;
      if (t < raiseEnd) {
        // Raise phase: lerp from 0 to -1.2 over 0.3s
        clawRotation = -1.2 * (t / raiseEnd);
      } else if (t < holdEnd) {
        // Hold phase: keep at -1.2
        clawRotation = -1.2;
      } else if (t < totalEnd) {
        // Lower phase: lerp from -1.2 to 0 over 0.3s
        clawRotation = -1.2 * (1 - (t - holdEnd) / 0.3);
      } else {
        // Animation complete
        clawRotation = 0;
        this.waveState.active = false;
        this.waveState.timer = 0;
      }
      this.claws.forEach(claw => {
        claw.rotation.x = clawRotation;
      });
      // Skip movement during wave - only animate legs gently and maintain position
      if (this.waveState.active) {
        if (!this.meshyModel) {
          this.legPhase += deltaTime * 3;
          this.legs.forEach((leg, i) => {
            leg.rotation.x = Math.sin(this.legPhase + i * 0.5) * 0.1;
          });
        }
        return;
      }
    }

    // Update scatter state if active
    if (this.scatterState) {
      this.scatterState.timer += deltaTime;
      if (this.scatterState.timer >= this.scatterState.duration) {
        // Scatter ended, return to normal wandering
        this.scatterState = null;
      }
    }

    // Change direction randomly (only when not scattering)
    if (!this.scatterState && this.changeDirectionTimer > 2 + Math.random() * 3) {
      this.changeDirectionTimer = 0;
      this.targetDirection = Math.random() * Math.PI * 2;
    }

    // Move towards target direction, 3x speed when scattering
    const baseSpeed = CREATURE.CRAB_BASE_SPEED;
    const speed = this.scatterState ? baseSpeed * CREATURE.CRAB_SCATTER_SPEED_MULTIPLIER : baseSpeed;
    const moveDir = this.scatterState ? this.scatterState.direction : this.targetDirection;
    this.velocity.x = Math.cos(moveDir) * speed * deltaTime;
    this.velocity.z = Math.sin(moveDir) * speed * deltaTime;

    // Get the active mesh (Meshy model or procedural)
    const activeMesh = this.meshyModel || this.mesh;
    activeMesh.position.add(this.velocity);

    // Occasionally spawn sand particles while walking (4x rate during scatter)
    this.sandCounter = (this.sandCounter || 0) + 1;
    const sandInterval = this.scatterState ? CREATURE.CRAB_SCATTER_SAND_INTERVAL : CREATURE.CRAB_NORMAL_SAND_INTERVAL;
    if (this.sandCounter >= sandInterval && this.onSand && typeof createSandPuff === 'function') {
      this.sandCounter = 0;
      const sandChance = this.scatterState ? CREATURE.CRAB_SCATTER_SAND_CHANCE : CREATURE.CRAB_NORMAL_SAND_CHANCE;
      if (Math.random() < sandChance) {
        createSandPuff(activeMesh.position);
      }
    }

    // Keep on island - constrain to sandy area
    const dist = Math.sqrt(
      activeMesh.position.x ** 2 + activeMesh.position.z ** 2
    );
    if (dist > CREATURE.CRAB_WANDER_RADIUS) {
      // Turn back towards center
      this.targetDirection = Math.atan2(-activeMesh.position.z, -activeMesh.position.x);
      // If too far out, teleport back
      if (dist > CREATURE.CRAB_TELEPORT_RADIUS) {
        const angle = Math.atan2(activeMesh.position.z, activeMesh.position.x);
        activeMesh.position.x = Math.cos(angle) * CREATURE.CRAB_TELEPORT_RETURN_RADIUS;
        activeMesh.position.z = Math.sin(angle) * CREATURE.CRAB_TELEPORT_RETURN_RADIUS;
      }
    }

    // Raycast down to find sand surface height (only every 10 frames to save performance)
    this.raycastCounter = (this.raycastCounter || 0) + 1;
    if (sandModel && this.raycastCounter >= 10) {
      this.raycastCounter = 0;
      this._rayOrigin.set(activeMesh.position.x, 20, activeMesh.position.z);
      groundRaycaster.set(this._rayOrigin, downVector);
      const intersects = groundRaycaster.intersectObject(sandModel, true);
      if (intersects.length > 0) {
        // On sand - smoothly interpolate to target height
        this.targetY = intersects[0].point.y + 0.5;
        this.onSand = true;

        // Get surface normal to tilt crab based on terrain slope
        if (intersects[0].face) {
          const normal = intersects[0].face.normal.clone();
          // Transform normal to world space
          normal.transformDirection(sandModel.matrixWorld);
          this.targetTiltX = Math.asin(-normal.z) * 0.5;  // Tilt forward/back
          this.targetTiltZ = Math.asin(normal.x) * 0.5;   // Tilt left/right
        }
      } else {
        // Over water - turn back towards island center
        this.onSand = false;
        this.targetDirection = Math.atan2(-activeMesh.position.z, -activeMesh.position.x);
        // Push back onto sand
        const angle = Math.atan2(activeMesh.position.z, activeMesh.position.x);
        activeMesh.position.x = Math.cos(angle) * CREATURE.CRAB_PUSHBACK_RADIUS;
        activeMesh.position.z = Math.sin(angle) * CREATURE.CRAB_PUSHBACK_RADIUS;
      }
    }
    // Smoothly move to target height
    if (this.targetY !== undefined) {
      activeMesh.position.y += (this.targetY - activeMesh.position.y) * 0.1;
    }

    // Face movement direction
    activeMesh.rotation.y = this.targetDirection + Math.PI / 2;

    // Apply terrain tilt smoothly
    if (this.targetTiltX !== undefined) {
      activeMesh.rotation.x += (this.targetTiltX - activeMesh.rotation.x) * 0.1;
    }
    if (this.targetTiltZ !== undefined) {
      activeMesh.rotation.z += (this.targetTiltZ - activeMesh.rotation.z) * 0.1;
    }

    // Animate legs (only for procedural), double frequency during scatter
    if (!this.meshyModel) {
      const legSpeed = this.scatterState ? 20 : 10;
      this.legPhase += deltaTime * legSpeed;
      this.legs.forEach((leg, i) => {
        leg.rotation.x = Math.sin(this.legPhase + i * 0.5) * 0.3;
      });
    }
  }

  scatter(clickPoint) {
    const activeMesh = this.meshyModel || this.mesh;
    const dx = activeMesh.position.x - clickPoint.x;
    const dz = activeMesh.position.z - clickPoint.z;
    const awayAngle = Math.atan2(dz, dx);
    this.scatterState = {
      timer: 0,
      duration: CREATURE.CRAB_SCATTER_DURATION,
      direction: awayAngle,
    };
    // Override the wandering direction timer so it does not fight scatter
    this.changeDirectionTimer = 0;
  }
}

let totalCrabClicks = 0;
const crabs = [];
for (let i = 0; i < CREATURE.CRAB_COUNT; i++) {
  const angle = (i / CREATURE.CRAB_COUNT) * Math.PI * 2 + Math.random() * 0.5;  // Evenly spaced around island
  const radius = CREATURE.CRAB_SPAWN_RADIUS_MIN + Math.random() * (CREATURE.CRAB_SPAWN_RADIUS_MAX - CREATURE.CRAB_SPAWN_RADIUS_MIN);  // Spread out
  const crab = new Crab(new THREE.Vector3(
    Math.cos(angle) * radius,
    2.3,
    Math.sin(angle) * radius
  ));
  scene.add(crab.mesh);
  crabs.push(crab);
}

// ============================================================================
// DOLPHINS
// ============================================================================

// Module-level tracking for dolphin interaction bonuses
let barrelRollDiscovered = false;
let showOffDiscovered = false;
let synchronizedSplashDiscovered = false;
let recentDolphinClicks = [];

class Dolphin {
  constructor(position) {
    this.mesh = this.createMesh();
    this.mesh.position.copy(position);
    this.phase = Math.random() * Math.PI * 2;
    this.orbitRadius = CREATURE.DOLPHIN_ORBIT_RADIUS_MIN + Math.random() * (CREATURE.DOLPHIN_ORBIT_RADIUS_MAX - CREATURE.DOLPHIN_ORBIT_RADIUS_MIN);
    this.orbitSpeed = CREATURE.DOLPHIN_ORBIT_SPEED_MIN + Math.random() * (CREATURE.DOLPHIN_ORBIT_SPEED_MAX - CREATURE.DOLPHIN_ORBIT_SPEED_MIN);
    this.jumpPhase = Math.random() * Math.PI * 2;
    this.baseY = CREATURE.DOLPHIN_BASE_Y;
    this.trickState = null; // { type, timer, duration, flipRotation }
    // Interaction bonus properties
    this.clickCount = 0;
    this.barrelRollPending = false;
    this.syncJumpPending = false;
    this.showOffState = { active: false, timer: 0, trickIndex: 0 };
  }

  createMesh() {
    const dolphinGroup = new THREE.Group();

    // Body - wet skin with glossy clearcoat and subtle iridescence
    const bodyGeometry = new THREE.CapsuleGeometry(0.8, 3, 8, 16);
    const bodyMaterial = new THREE.MeshPhysicalMaterial({
      color: 0x4682b4,
      roughness: 0.08,           // Very smooth wet skin
      metalness: 0.0,
      clearcoat: 0.9,            // Wet surface sheen
      clearcoatRoughness: 0.05,
      sheen: 0.4,                // Subtle sheen for organic look
      sheenRoughness: 0.3,
      sheenColor: new THREE.Color(0x6699cc),
      iridescence: 0.15,         // Subtle rainbow effect like wet skin
      iridescenceIOR: 1.3
    });
    const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
    body.rotation.z = Math.PI / 2;
    body.castShadow = true;
    dolphinGroup.add(body);

    // Belly (lighter color) - wet skin
    const bellyGeometry = new THREE.CapsuleGeometry(0.6, 2.5, 8, 16);
    const bellyMaterial = new THREE.MeshPhysicalMaterial({
      color: 0xb0c4de,
      roughness: 0.1,
      metalness: 0.0,
      clearcoat: 0.85,
      clearcoatRoughness: 0.08
    });
    const belly = new THREE.Mesh(bellyGeometry, bellyMaterial);
    belly.rotation.z = Math.PI / 2;
    belly.position.y = -0.2;
    dolphinGroup.add(belly);

    // Snout
    const snoutGeometry = new THREE.ConeGeometry(0.4, 1.5, 8);
    const snout = new THREE.Mesh(snoutGeometry, bodyMaterial);
    snout.rotation.z = -Math.PI / 2;
    snout.position.x = 2.2;
    dolphinGroup.add(snout);

    // Dorsal fin
    const dorsalGeometry = new THREE.ConeGeometry(0.3, 1, 4);
    const dorsal = new THREE.Mesh(dorsalGeometry, bodyMaterial);
    dorsal.position.set(0, 1, 0);
    dorsal.rotation.x = -0.2;
    dolphinGroup.add(dorsal);

    // Tail
    const tailGeometry = new THREE.BoxGeometry(1.5, 0.1, 0.8);
    const tail = new THREE.Mesh(tailGeometry, bodyMaterial);
    tail.position.set(-2.2, 0, 0);
    dolphinGroup.add(tail);
    this.tailMesh = tail;

    // Flippers
    const flipperGeometry = new THREE.ConeGeometry(0.2, 0.8, 4);
    const flippers = [];
    [-1, 1].forEach(side => {
      const flipper = new THREE.Mesh(flipperGeometry, bodyMaterial);
      flipper.position.set(0.5, -0.5, side * 0.6);
      flipper.rotation.z = Math.PI / 2;
      flipper.rotation.y = side * 0.3;
      dolphinGroup.add(flipper);
      flippers.push(flipper);
    });
    this.leftFlipper = flippers[0];
    this.rightFlipper = flippers[1];

    // Eye
    const eyeGeometry = new THREE.SphereGeometry(0.1, 8, 8);
    const eyeMaterial = new THREE.MeshStandardMaterial({ color: 0x000000 });
    [0.3, -0.3].forEach(z => {
      const eye = new THREE.Mesh(eyeGeometry, eyeMaterial);
      eye.position.set(1.2, 0.2, z);
      dolphinGroup.add(eye);
    });

    return dolphinGroup;
  }

  update(time) {
    this.phase += this.orbitSpeed * 0.016;

    // Get the active mesh (Meshy model or procedural)
    const activeMesh = this.meshyModel || this.mesh;

    // Track previous water state for splash detection
    const wasAboveWater = this.lastJumpHeight > 0;
    const wasUnderwater = this.lastJumpHeight <= 0;

    // Orbit around island
    activeMesh.position.x = Math.cos(this.phase) * this.orbitRadius;
    activeMesh.position.z = Math.sin(this.phase) * this.orbitRadius;

    // Update trick state
    if (this.trickState) {
      this.trickState.timer += 0.016;
      if (this.trickState.timer >= this.trickState.duration) {
        this.trickState = null;
      }
    }

    // Show-off mode: force rapid consecutive jumps with alternating tricks
    if (this.showOffState.active) {
      this.showOffState.timer += 0.016;
      if (this.showOffState.timer >= CREATURE.DOLPHIN_SHOW_OFF_DURATION) {
        // End show-off mode
        this.showOffState.active = false;
        this.showOffState.timer = 0;
        this.showOffState.trickIndex = 0;
      } else if (!this.trickState && this.lastJumpHeight <= 0) {
        // Force rapid jumps during show-off: start a new jump immediately when underwater
        this.jumpPhase = 0;
        const showOffTricks = ['backflip', 'barrelRoll', 'spin'];
        const currentTrickType = showOffTricks[this.showOffState.trickIndex % showOffTricks.length];
        this.showOffState.trickIndex++;
        if (currentTrickType === 'barrelRoll') {
          this.trickState = { type: 'barrelRoll', timer: 0, duration: 1.0, flipRotation: 0 };
        } else if (currentTrickType === 'spin') {
          this.trickState = { type: 'spin', timer: 0, duration: 1.0, flipRotation: 0 };
        } else {
          this.trickState = { type: 'backflip', timer: 0, duration: 0.7, flipRotation: 0 };
        }
      }
    }

    // Synchronized jump: when pending and dolphin is underwater, trigger a high sync jump
    if (this.syncJumpPending && this.lastJumpHeight <= 0) {
      this.jumpPhase = 0;
      this.syncJumpPending = false;
      this.trickState = { type: 'syncJump', timer: 0, duration: 2.5, flipRotation: 0 };
    }

    // Barrel roll: when pending and dolphin starts a new jump (transitions from underwater to airborne)
    if (this.barrelRollPending && wasUnderwater) {
      const jumpHeightCheck = Math.sin(this.jumpPhase + 0.02) * 8;
      if (jumpHeightCheck > 0) {
        this.barrelRollPending = false;
        this.trickState = { type: 'barrelRoll', timer: 0, duration: 1.0, flipRotation: 0 };
      }
    }

    // Jumping motion - extra height during highJump or syncJump tricks
    this.jumpPhase += 0.02;
    let jumpMultiplier = 1.0;
    if (this.trickState && this.trickState.type === 'highJump') {
      jumpMultiplier = CREATURE.DOLPHIN_JUMP_MULTIPLIER_HIGH;
    } else if (this.trickState && this.trickState.type === 'syncJump') {
      jumpMultiplier = CREATURE.DOLPHIN_JUMP_MULTIPLIER_HIGH;
    }
    const jumpHeight = Math.sin(this.jumpPhase) * CREATURE.DOLPHIN_JUMP_HEIGHT * jumpMultiplier;
    activeMesh.position.y = this.baseY + Math.max(0, jumpHeight);

    // Detect water entry/exit and create splash (enhanced during trick)
    const isAboveWater = jumpHeight > 0;
    if (wasAboveWater !== isAboveWater && typeof createWaterSplash === 'function') {
      const splashPos = activeMesh.position.clone();
      splashPos.y = 0; // Water level
      const direction = new THREE.Vector3(
        -Math.sin(this.phase),
        0,
        Math.cos(this.phase)
      );
      createWaterSplash(splashPos, direction);
      // Extra splash on re-entry during trick
      if (this.trickState && !isAboveWater) {
        createWaterSplash(splashPos, direction);
      }
      // 3x total splash on sync jump re-entry (1 base + 1 trick + 1 extra = 3x)
      if (this.trickState && this.trickState.type === 'syncJump' && !isAboveWater) {
        createWaterSplash(splashPos, direction);
      }
    }
    this.lastJumpHeight = jumpHeight;

    // Rotation based on jump (pitch up when jumping out of water)
    let pitchRotation = jumpHeight > 0 ? Math.sin(this.jumpPhase) * 0.5 : 0;

    // Backflip: 360-degree rotation on X axis
    if (this.trickState && this.trickState.type === 'backflip') {
      const progress = this.trickState.timer / this.trickState.duration;
      const eased = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      this.trickState.flipRotation = eased * Math.PI * 2;
      pitchRotation += this.trickState.flipRotation;
    }

    activeMesh.rotation.x = pitchRotation;

    // Barrel roll: 360-degree rotation on Z axis (NOT X - that is backflip)
    let rollRotation = 0;
    if (this.trickState && this.trickState.type === 'barrelRoll') {
      const progress = this.trickState.timer / this.trickState.duration;
      const eased = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      rollRotation = eased * Math.PI * 2;
    }

    // Spin: 360-degree rotation on Y axis (pirouette)
    let spinRotation = 0;
    if (this.trickState && this.trickState.type === 'spin') {
      const progress = this.trickState.timer / this.trickState.duration;
      const eased = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      spinRotation = eased * Math.PI * 2;
    }

    // Face direction of movement (tangent to orbit, plus spin if active)
    activeMesh.rotation.y = -this.phase - Math.PI / 2 + spinRotation;

    // Apply barrel roll on Z axis
    activeMesh.rotation.z = rollRotation;

    // Procedural mesh body animations (skip if using Meshy model)
    if (!this.meshyModel) {
      // Tail flex
      this.tailMesh.rotation.y = Math.sin(time * 3) * 0.4;

      // Flipper paddle - alternating sine waves
      this.leftFlipper.rotation.x = Math.sin(time * 3) * 0.3;
      this.rightFlipper.rotation.x = Math.sin(time * 3 + Math.PI) * 0.3;

      // Body undulation - add to existing pitch
      activeMesh.rotation.x += Math.sin(time * 2) * 0.05;

      // Banking - gentle roll (add to existing barrel roll)
      activeMesh.rotation.z += Math.sin(this.phase) * 0.1;
    }
  }

  doTrick(type) {
    const isAirborne = this.lastJumpHeight > 0;
    if (type === 'click') {
      if (isAirborne) {
        this.trickState = {
          type: 'backflip',
          timer: 0,
          duration: 0.7,
          flipRotation: 0,
        };
      } else {
        this.jumpPhase = 0;
        this.trickState = {
          type: 'highJump',
          timer: 0,
          duration: 2.0,
          flipRotation: 0,
        };
      }
    }
  }
}

const dolphins = [];
for (let i = 0; i < CREATURE.DOLPHIN_COUNT; i++) {
  const angle = (i / CREATURE.DOLPHIN_COUNT) * Math.PI * 2;
  const dolphin = new Dolphin(new THREE.Vector3(
    Math.cos(angle) * 50,
    -2,
    Math.sin(angle) * 50
  ));
  dolphin.phase = angle;
  dolphin.jumpPhase = i * 1.2;
  scene.add(dolphin.mesh);
  dolphins.push(dolphin);
}

// ============================================================================
// SEAGULLS (Boids)
// ============================================================================

class Seagull {
  constructor(position) {
    this.wings = [];
    this.wingPhase = Math.random() * Math.PI * 2;
    this.velocity = new THREE.Vector3(
      (Math.random() - 0.5) * 2,
      (Math.random() - 0.5) * 0.5,
      (Math.random() - 0.5) * 2
    );
    this.diveState = null; // { phase, timer, duration, startPos, targetWaterPos, returnAltitude }
    this.isGolden = false;
    this.mesh = this.createMesh();
    this.mesh.position.copy(position);
  }

  createMesh() {
    const birdGroup = new THREE.Group();

    // Body - feathered with sheen
    const bodyGeometry = new THREE.CapsuleGeometry(0.2, 0.8, 4, 8);
    const bodyMaterial = new THREE.MeshPhysicalMaterial({
      color: 0xffffff,
      roughness: 0.6,
      metalness: 0.0,
      sheen: 0.5,              // Feather-like sheen
      sheenRoughness: 0.4,
      sheenColor: new THREE.Color(0xeeeeee)
    });
    const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
    body.rotation.z = Math.PI / 2;
    birdGroup.add(body);

    // Head
    const headGeometry = new THREE.SphereGeometry(0.2, 8, 8);
    const head = new THREE.Mesh(headGeometry, bodyMaterial);
    head.position.x = 0.6;
    birdGroup.add(head);

    // Beak - glossy
    const beakGeometry = new THREE.ConeGeometry(0.05, 0.3, 4);
    const beakMaterial = new THREE.MeshPhysicalMaterial({
      color: 0xffa500,
      roughness: 0.3,
      clearcoat: 0.6,
      clearcoatRoughness: 0.2
    });
    const beak = new THREE.Mesh(beakGeometry, beakMaterial);
    beak.rotation.z = -Math.PI / 2;
    beak.position.set(0.9, 0, 0);
    birdGroup.add(beak);

    // Wings - feathered with gray tips
    const wingGeometry = new THREE.PlaneGeometry(1.2, 0.4);
    const wingMaterial = new THREE.MeshPhysicalMaterial({
      color: 0xd8d8d8,         // Slightly gray for wing tips
      side: THREE.DoubleSide,
      roughness: 0.7,
      sheen: 0.4,
      sheenRoughness: 0.5,
      sheenColor: new THREE.Color(0xcccccc)
    });

    [-1, 1].forEach(side => {
      const wing = new THREE.Mesh(wingGeometry, wingMaterial);
      wing.position.set(0, 0, side * 0.6);
      wing.rotation.x = side * 0.2;
      birdGroup.add(wing);
      this.wings.push({ mesh: wing, side });
    });

    // Tail
    const tailGeometry = new THREE.PlaneGeometry(0.3, 0.2);
    const tail = new THREE.Mesh(tailGeometry, bodyMaterial);
    tail.position.set(-0.6, 0, 0);
    tail.rotation.x = Math.PI / 2;
    birdGroup.add(tail);

    return birdGroup;
  }

  update(deltaTime, seagulls) {
    const activeMesh = this.meshyModel || this.mesh;

    // Handle dive state - overrides boid behavior
    if (this.diveState) {
      this.diveState.timer += deltaTime;
      const t = this.diveState.timer / this.diveState.duration;

      if (this.diveState.phase === 'diving') {
        const progress = Math.min(t, 1.0);
        const eased = progress * progress;
        activeMesh.position.lerpVectors(this.diveState.startPos, this.diveState.targetWaterPos, eased);
        activeMesh.rotation.x = -Math.PI / 4 * Math.min(progress * 2, 1.0);
        const diveDir = this.diveState.targetWaterPos.clone().sub(this.diveState.startPos);
        activeMesh.rotation.y = Math.atan2(diveDir.x, diveDir.z);
        if (progress >= 1.0) {
          if (typeof createWaterSplash === 'function') {
            const splashPos = activeMesh.position.clone();
            splashPos.y = 0;
            createWaterSplash(splashPos, new THREE.Vector3(0, 1, 0));
          }
          // Fish catch logic: golden gull always catches, normal gull lower chance
          const catchChance = this.isGolden ? CREATURE.SEAGULL_GOLDEN_CATCH_CHANCE : CREATURE.SEAGULL_NORMAL_CATCH_CHANCE;
          if (Math.random() < catchChance) {
            this.diveState.caughtFish = true;
          }
          this.diveState.phase = 'pullup';
          this.diveState.timer = 0;
          this.diveState.pullupStart = activeMesh.position.clone();
          this.diveState.pullupTarget = new THREE.Vector3(
            activeMesh.position.x + this.velocity.x * 3, 20, activeMesh.position.z + this.velocity.z * 3
          );
        }
      } else if (this.diveState.phase === 'pullup') {
        const progress = Math.min(t, 1.0);
        const eased = 1 - Math.pow(1 - progress, 2);
        activeMesh.position.lerpVectors(this.diveState.pullupStart, this.diveState.pullupTarget, eased);
        activeMesh.rotation.x = -Math.PI / 4 * (1 - eased);
        if (progress >= 1.0) { this.diveState = null; }
      }
      if (!this.meshyModel) {
        this.wingPhase += deltaTime * 12;
        this.wings.forEach(({ mesh, side }) => { mesh.rotation.x = side * (0.2 + Math.sin(this.wingPhase) * 0.4); });
      }
      return;
    }

    // Boids algorithm
    const separation = new THREE.Vector3();
    const alignment = new THREE.Vector3();
    const cohesion = new THREE.Vector3();
    let neighborCount = 0;

    seagulls.forEach(other => {
      if (other === this) return;

      const distance = this.mesh.position.distanceTo(other.mesh.position);
      if (distance < CREATURE.SEAGULL_BOID_NEIGHBOR_RADIUS) {
        // Separation
        const diff = this.mesh.position.clone().sub(other.mesh.position);
        diff.divideScalar(distance);
        separation.add(diff);

        // Alignment
        alignment.add(other.velocity);

        // Cohesion
        cohesion.add(other.mesh.position);

        neighborCount++;
      }
    });

    if (neighborCount > 0) {
      separation.divideScalar(neighborCount).multiplyScalar(0.05);
      alignment.divideScalar(neighborCount).sub(this.velocity).multiplyScalar(0.02);
      cohesion.divideScalar(neighborCount).sub(this.mesh.position).multiplyScalar(0.01);
    }

    // Stay over water (orbit around island at distance)
    const horizontalDist = Math.sqrt(
      this.mesh.position.x ** 2 + this.mesh.position.z ** 2
    );

    // Keep seagulls flying close to island
    const distForce = new THREE.Vector3();
    if (horizontalDist < CREATURE.SEAGULL_ORBIT_INNER) {
      // Push away from island center
      distForce.set(this.mesh.position.x, 0, this.mesh.position.z)
        .normalize().multiplyScalar(0.05);
    } else if (horizontalDist > CREATURE.SEAGULL_ORBIT_OUTER) {
      // Pull back toward island
      distForce.set(-this.mesh.position.x, 0, -this.mesh.position.z)
        .normalize().multiplyScalar(0.02);
    }

    // Apply forces
    this.velocity.add(separation);
    this.velocity.add(alignment);
    this.velocity.add(cohesion);
    this.velocity.add(distForce);

    // Limit speed
    if (this.velocity.length() > CREATURE.SEAGULL_MAX_SPEED) {
      this.velocity.normalize().multiplyScalar(CREATURE.SEAGULL_MAX_SPEED);
    }

    // Update position
    activeMesh.position.add(this.velocity.clone().multiplyScalar(deltaTime));

    // Keep in bounds vertically
    if (activeMesh.position.y < CREATURE.SEAGULL_FLY_HEIGHT_MIN) activeMesh.position.y = CREATURE.SEAGULL_FLY_HEIGHT_MIN;
    if (activeMesh.position.y > CREATURE.SEAGULL_FLY_HEIGHT_MAX) activeMesh.position.y = CREATURE.SEAGULL_FLY_HEIGHT_MAX;

    // Face direction of flight
    if (this.velocity.length() > 0.1) {
      const angle = Math.atan2(this.velocity.x, this.velocity.z);
      activeMesh.rotation.y = angle;
      activeMesh.rotation.x = -this.velocity.y * 0.1;
    }

    // Wing animation (only for procedural)
    if (!this.meshyModel) {
      this.wingPhase += deltaTime * 8;
      this.wings.forEach(({ mesh, side }) => {
        mesh.rotation.x = side * (0.2 + Math.sin(this.wingPhase) * 0.4);
      });
    }
  }

  dive() {
    if (this.diveState) return;
    const activeMesh = this.meshyModel || this.mesh;
    const startPos = activeMesh.position.clone();
    const aheadDir = this.velocity.clone().normalize();
    this.diveState = {
      phase: 'diving',
      timer: 0,
      duration: CREATURE.SEAGULL_DIVE_DURATION,
      startPos,
      targetWaterPos: new THREE.Vector3(startPos.x + aheadDir.x * 15, 0.5, startPos.z + aheadDir.z * 15),
      returnAltitude: 20,
    };
  }
}

const seagulls = [];
for (let i = 0; i < CREATURE.SEAGULL_COUNT; i++) {
  const angle = (i / CREATURE.SEAGULL_COUNT) * Math.PI * 2 + Math.random() * 0.5;
  const radius = CREATURE.SEAGULL_SPAWN_RADIUS_MIN + Math.random() * (CREATURE.SEAGULL_SPAWN_RADIUS_MAX - CREATURE.SEAGULL_SPAWN_RADIUS_MIN);
  const seagull = new Seagull(new THREE.Vector3(
    Math.cos(angle) * radius,
    CREATURE.SEAGULL_FLY_HEIGHT_MIN + Math.random() * (CREATURE.SEAGULL_FLY_HEIGHT_MAX - CREATURE.SEAGULL_FLY_HEIGHT_MIN),
    Math.sin(angle) * radius
  ));
  scene.add(seagull.mesh);
  seagulls.push(seagull);
}

// --- Golden Gull tracking (pi-nzq.4) ---
let totalSeagullClicks = 0;
let goldenGullSpawned = false;
let goldenGullDiscovered = false;

// --- Formation Flight tracking (pi-nzq.6) ---
let recentSeagullClicks = [];
let formationFlightActive = false;
let formationFlightTimer = 0;
let formationFlightDiscovered = false;
let formationFlightStartPositions = []; // saved positions before formation
let formationFlightDirection = new THREE.Vector3(1, 0, 0); // direction of flight

// Restore persisted seagull state from saved game
(function restoreSeagullState() {
  const state = getGameState();
  if (state.seagullState) {
    totalSeagullClicks = state.seagullState.totalSeagullClicks || 0;
    goldenGullSpawned = !!state.seagullState.goldenGullSpawned;
    goldenGullDiscovered = !!state.seagullState.goldenGullDiscovered;
    formationFlightDiscovered = !!state.seagullState.formationFlightDiscovered;
    if (goldenGullSpawned && seagulls.length > 0) {
      // Re-apply golden gull visuals to seagull 0
      const gull = seagulls[0];
      gull.isGolden = true;
      gull.mesh.scale.multiplyScalar(CREATURE.SEAGULL_GOLDEN_SCALE);
      gull.mesh.traverse((child) => {
        if (child.isMesh && child.material) {
          child.material = child.material.clone();
          child.material.color.setHex(0xffd700);
        }
      });
    }
  }
})();

function saveSeagullState() {
  const state = getGameState();
  state.seagullState = {
    totalSeagullClicks,
    goldenGullSpawned,
    goldenGullDiscovered,
    formationFlightDiscovered,
  };
  saveGameState(state);
}

function spawnGoldenGull(seagull) {
  seagull.isGolden = true;
  goldenGullSpawned = true;

  // Scale up the active mesh (procedural or loaded model)
  const activeMesh = seagull.meshyModel || seagull.mesh;
  activeMesh.scale.multiplyScalar(CREATURE.SEAGULL_GOLDEN_SCALE);

  // Tint gold
  activeMesh.traverse((child) => {
    if (child.isMesh && child.material) {
      child.material = child.material.clone();
      child.material.color.setHex(0xffd700);
    }
  });

  saveSeagullState();
}

function triggerFormationFlight() {
  if (formationFlightActive) return;
  formationFlightActive = true;
  formationFlightTimer = 0;

  // Pick a random flight direction
  const angle = Math.random() * Math.PI * 2;
  formationFlightDirection.set(Math.cos(angle), 0, Math.sin(angle));

  // Save start positions for each seagull so we can smoothly transition
  formationFlightStartPositions = seagulls.map((s) => {
    const m = s.meshyModel || s.mesh;
    return m.position.clone();
  });

  // Emit discovery on first formation flight
  if (!formationFlightDiscovered) {
    formationFlightDiscovered = true;
    eventBus.emit(GameEvents.OBJECT_CLICKED, { id: 'formation_flight', type: 'creature' });
    saveSeagullState();
  }
}

// ============================================================================
// SEA TURTLE (pi-wfm.7) — unlocks after 15+ discoveries
// ============================================================================

let seaTurtle = null;       // SeaTurtle instance (null until spawned)
let seaTurtleSpawned = false; // Persist flag so it never de-spawns

class SeaTurtle {
  constructor() {
    this.mesh = this.createMesh();
    // Start at a random angle on the orbit
    this.orbitAngle = Math.random() * Math.PI * 2;
    this.orbitRadius = 30;            // shallow water orbit
    this.orbitSpeed = 0.15;           // units/s equivalent angular speed
    this.baseY = 0.5;                 // just above water surface
    this.bobPhase = Math.random() * Math.PI * 2;

    // Beach crawl state
    this.beachCrawlActive = false;
    this.beachCrawlTimer = 0;
    this.beachCrawlCooldown = 60 + Math.random() * 30; // 60-90s between crawls
    this.beachCrawlCooldownTimer = 0;
    this.beachCrawlPhase = 'none'; // 'toBeach' | 'onBeach' | 'toWater' | 'none'
    this.beachCrawlDuration = 10 + Math.random() * 5; // 10-15s on beach
    this.crawlTargetRadius = 22;      // how close to center on sand

    // Retreat state (on click)
    this.retreatActive = false;
    this.retreatTimer = 0;

    // Set initial position
    this.mesh.position.set(
      Math.cos(this.orbitAngle) * this.orbitRadius,
      this.baseY,
      Math.sin(this.orbitAngle) * this.orbitRadius
    );
  }

  createMesh() {
    const turtleGroup = new THREE.Group();

    // Shell (body) — flattened sphere
    const shellGeometry = new THREE.SphereGeometry(0.8, 10, 8);
    const shellMaterial = new THREE.MeshStandardMaterial({
      color: 0x2E8B57,     // sea green
      roughness: 0.7,
      metalness: 0.05,
    });
    const shell = new THREE.Mesh(shellGeometry, shellMaterial);
    shell.scale.set(1.0, 0.45, 1.0); // flatten vertically
    turtleGroup.add(shell);

    // Shell top — slightly darker pattern
    const shellTopGeometry = new THREE.SphereGeometry(0.75, 8, 6, 0, Math.PI * 2, 0, Math.PI * 0.5);
    const shellTopMaterial = new THREE.MeshStandardMaterial({
      color: 0x256D47,     // darker green for pattern
      roughness: 0.75,
      metalness: 0.05,
    });
    const shellTop = new THREE.Mesh(shellTopGeometry, shellTopMaterial);
    shellTop.scale.set(1.0, 0.5, 1.0);
    shellTop.position.y = 0.07;
    turtleGroup.add(shellTop);

    // Head — small sphere extending from front
    const headGeometry = new THREE.SphereGeometry(0.28, 8, 6);
    const headMaterial = new THREE.MeshStandardMaterial({
      color: 0x3CB371,     // medium sea green
      roughness: 0.6,
    });
    const head = new THREE.Mesh(headGeometry, headMaterial);
    head.position.set(0.87, 0.0, 0);
    turtleGroup.add(head);
    this.headMesh = head;

    // Eyes — tiny dark spheres
    const eyeGeometry = new THREE.SphereGeometry(0.06, 4, 4);
    const eyeMaterial = new THREE.MeshStandardMaterial({ color: 0x111111 });
    [-1, 1].forEach(side => {
      const eye = new THREE.Mesh(eyeGeometry, eyeMaterial);
      eye.position.set(1.07, 0.08, side * 0.13);
      turtleGroup.add(eye);
    });

    // 4 Flippers — flattened spheres (paddles)
    const flipperGeometry = new THREE.SphereGeometry(0.27, 6, 4);
    const flipperMaterial = new THREE.MeshStandardMaterial({
      color: 0x3CB371,
      roughness: 0.6,
    });
    // Front flippers
    const frontFlippers = [];
    [-1, 1].forEach(side => {
      const flipper = new THREE.Mesh(flipperGeometry, flipperMaterial);
      flipper.scale.set(1.4, 0.3, 0.8);
      flipper.position.set(0.47, -0.13, side * 0.73);
      flipper.rotation.x = side * 0.3;
      turtleGroup.add(flipper);
      frontFlippers.push(flipper);
    });
    this.frontLeftFlipper = frontFlippers[0];
    this.frontRightFlipper = frontFlippers[1];
    // Rear flippers (smaller)
    const rearFlippers = [];
    [-1, 1].forEach(side => {
      const flipper = new THREE.Mesh(flipperGeometry, flipperMaterial);
      flipper.scale.set(1.0, 0.3, 0.7);
      flipper.position.set(-0.6, -0.13, side * 0.6);
      flipper.rotation.x = side * 0.2;
      turtleGroup.add(flipper);
      rearFlippers.push(flipper);
    });
    this.rearLeftFlipper = rearFlippers[0];
    this.rearRightFlipper = rearFlippers[1];

    // Small tail
    const tailGeometry = new THREE.ConeGeometry(0.11, 0.33, 4);
    const tail = new THREE.Mesh(tailGeometry, flipperMaterial);
    tail.rotation.z = Math.PI / 2;
    tail.position.set(-0.93, 0, 0);
    turtleGroup.add(tail);

    turtleGroup.scale.setScalar(1.0);
    return turtleGroup;
  }

  update(deltaTime, time) {
    const speed = this.retreatActive ? 0.5 : (this.beachCrawlPhase === 'toBeach' || this.beachCrawlPhase === 'toWater' ? 0.05 : this.orbitSpeed);

    // Retreat timer
    if (this.retreatActive) {
      this.retreatTimer += deltaTime;
      if (this.retreatTimer >= 3.0) {
        this.retreatActive = false;
        this.retreatTimer = 0;
        // If was beach crawling, cancel and go back to water
        if (this.beachCrawlPhase !== 'none') {
          this.beachCrawlPhase = 'none';
          this.beachCrawlActive = false;
          this.orbitRadius = 30;
        }
      }
    }

    // Beach crawl logic
    if (!this.retreatActive) {
      if (this.beachCrawlPhase === 'none') {
        // Cooldown before next beach crawl
        this.beachCrawlCooldownTimer += deltaTime;
        if (this.beachCrawlCooldownTimer >= this.beachCrawlCooldown) {
          this.beachCrawlCooldownTimer = 0;
          this.beachCrawlCooldown = 60 + Math.random() * 30;
          this.beachCrawlPhase = 'toBeach';
          this.beachCrawlTimer = 0;
          this.crawlTargetRadius = 20 + Math.random() * 5;
          this.beachCrawlDuration = 10 + Math.random() * 5;
        }
      } else if (this.beachCrawlPhase === 'toBeach') {
        // Slowly shrink orbit radius toward beach
        this.orbitRadius -= deltaTime * 1.5; // move inward
        if (this.orbitRadius <= this.crawlTargetRadius) {
          this.orbitRadius = this.crawlTargetRadius;
          this.beachCrawlPhase = 'onBeach';
          this.beachCrawlTimer = 0;
        }
      } else if (this.beachCrawlPhase === 'onBeach') {
        this.beachCrawlTimer += deltaTime;
        if (this.beachCrawlTimer >= this.beachCrawlDuration) {
          this.beachCrawlPhase = 'toWater';
        }
      } else if (this.beachCrawlPhase === 'toWater') {
        // Slowly expand orbit radius back to water
        this.orbitRadius += deltaTime * 1.5;
        if (this.orbitRadius >= 30) {
          this.orbitRadius = 30;
          this.beachCrawlPhase = 'none';
        }
      }
    }

    // Orbital movement
    const angularSpeed = speed / this.orbitRadius;
    this.orbitAngle += angularSpeed * deltaTime;

    // Position
    const x = Math.cos(this.orbitAngle) * this.orbitRadius;
    const z = Math.sin(this.orbitAngle) * this.orbitRadius;

    // Y position: bob in water, sit on sand when on beach
    let y;
    if (this.orbitRadius < 26) {
      // On or near beach — sit on sand level
      y = 2.0 + Math.sin(time * 0.5 + this.bobPhase) * 0.02; // minimal bob on sand
    } else {
      // In water — gentle bobbing
      y = this.baseY + Math.sin(time * 1.2 + this.bobPhase) * 0.1;
    }

    this.mesh.position.set(x, y, z);

    // Face direction of travel (tangent to orbit)
    const tangentAngle = this.orbitAngle + Math.PI / 2;
    this.mesh.rotation.y = tangentAngle;

    // Flipper and body animations
    const onBeach = this.orbitRadius < 26;
    const flipperFreq = onBeach ? time * 0.5 : time * 2;

    // Front flippers - alternating paddle
    this.frontLeftFlipper.rotation.z = Math.sin(flipperFreq) * 0.4;
    this.frontRightFlipper.rotation.z = Math.sin(flipperFreq + Math.PI) * 0.4;

    // Rear flippers - smaller amplitude
    this.rearLeftFlipper.rotation.z = Math.sin(flipperFreq + 0.5) * 0.2;
    this.rearRightFlipper.rotation.z = Math.sin(flipperFreq + 0.5 + Math.PI) * 0.2;

    // Head bob
    this.headMesh.rotation.x = Math.sin(time * 1.5) * 0.1;

    // Shell wobble on beach
    if (onBeach) {
      this.mesh.rotation.z = Math.sin(time * 1.5) * 0.08;
    }
  }

  retreat() {
    this.retreatActive = true;
    this.retreatTimer = 0;
  }
}

// Spawn the sea turtle when player has 15+ discoveries
function trySpawnSeaTurtle() {
  if (seaTurtleSpawned) return;
  const { discovered } = getDiscoveryCount();
  if (discovered >= 15) {
    seaTurtleSpawned = true;
    seaTurtle = new SeaTurtle();
    scene.add(seaTurtle.mesh);

    // Register as interactable
    registerInteractable(seaTurtle.mesh, {
      type: 'creature',
      id: 'sea_turtle',
      boundingRadius: 2.0,
      cooldown: 500,
      onClick: () => {
        eventBus.emit(GameEvents.CREATURE_CLICKED, {
          id: 'sea_turtle',
          type: 'creature',
          species: 'sea_turtle',
          mesh: seaTurtle.mesh,
        });
        seaTurtle.retreat();
      },
    });

    console.log('[SeaTurtle] Spawned after 15+ discoveries');
  }
}

// Check on init (player may already have 15+ discoveries from a saved game)
trySpawnSeaTurtle();

// Check on each new discovery
eventBus.on(GameEvents.DISCOVERY_NEW, () => {
  trySpawnSeaTurtle();
});

// ============================================================================
// CLOUDS (Billboard planes with canvas-generated soft cloud textures)
// ============================================================================

function createCloudTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 64;
  const ctx = canvas.getContext('2d');

  const blobs = [
    { x: 64, y: 32, rx: 50, ry: 25 },
    { x: 40, y: 34, rx: 35, ry: 20 },
    { x: 88, y: 34, rx: 35, ry: 20 },
    { x: 54, y: 26, rx: 30, ry: 18 },
    { x: 74, y: 28, rx: 28, ry: 16 },
  ];

  blobs.forEach(({ x, y, rx, ry }) => {
    const grad = ctx.createRadialGradient(x, y, 0, x, y, Math.max(rx, ry));
    grad.addColorStop(0, 'rgba(255, 255, 255, 0.8)');
    grad.addColorStop(0.4, 'rgba(255, 255, 255, 0.4)');
    grad.addColorStop(1, 'rgba(255, 255, 255, 0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
    ctx.fill();
  });

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}

const cloudTexture = createCloudTexture();

const clouds = [];
for (let i = 0; i < 10; i++) {
  const width = 30 + Math.random() * 40;
  const height = 15 + Math.random() * 15;
  const geometry = new THREE.PlaneGeometry(width, height);
  const material = new THREE.MeshBasicMaterial({
    map: cloudTexture,
    transparent: true,
    opacity: 0.4 + Math.random() * 0.3,
    depthWrite: false,
    side: THREE.DoubleSide,
  });

  const cloud = new THREE.Mesh(geometry, material);
  const baseAngle = (i / 10) * Math.PI * 2 + Math.random() * 0.5;
  const radius = 150 + Math.random() * 200;
  const cloudY = 60 + Math.random() * 40;

  cloud.position.set(
    Math.cos(baseAngle) * radius,
    cloudY,
    Math.sin(baseAngle) * radius
  );
  cloud.userData.speed = 0.5 + Math.random() * 0.5;
  cloud.userData.baseAngle = baseAngle;
  cloud.userData.radius = radius;

  scene.add(cloud);
  clouds.push(cloud);
}

// ============================================================================
// TREASURE CHEST
// ============================================================================

function createTreasureChest() {
  const chestGroup = new THREE.Group();

  // Base
  const baseGeometry = new THREE.BoxGeometry(2, 1, 1.2);
  const woodMaterial = new THREE.MeshStandardMaterial({
    color: 0x8B4513,
    roughness: 0.8
  });
  const base = new THREE.Mesh(baseGeometry, woodMaterial);
  chestGroup.add(base);

  // Lid (open)
  const lidGeometry = new THREE.BoxGeometry(2, 0.2, 1.2);
  const lid = new THREE.Mesh(lidGeometry, woodMaterial);
  lid.position.set(0, 0.6, -0.5);
  lid.rotation.x = -Math.PI / 3;
  chestGroup.add(lid);

  // Gold trim
  const goldMaterial = new THREE.MeshStandardMaterial({
    color: 0xffd700,
    metalness: 0.8,
    roughness: 0.2
  });

  // Gold bands
  const bandGeometry = new THREE.BoxGeometry(2.1, 0.15, 1.25);
  [-0.3, 0.3].forEach(y => {
    const band = new THREE.Mesh(bandGeometry, goldMaterial);
    band.position.y = y;
    chestGroup.add(band);
  });

  // Lock
  const lockGeometry = new THREE.BoxGeometry(0.3, 0.3, 0.1);
  const lock = new THREE.Mesh(lockGeometry, goldMaterial);
  lock.position.set(0, 0.2, 0.65);
  chestGroup.add(lock);

  // Gold coins inside — track as array for removal on click
  const coinGeometry = new THREE.CylinderGeometry(0.15, 0.15, 0.05, 16);
  const coins = [];
  for (let i = 0; i < 30; i++) {
    const coin = new THREE.Mesh(coinGeometry, goldMaterial);
    coin.position.set(
      (Math.random() - 0.5) * 1.5,
      0.3 + Math.random() * 0.3,
      (Math.random() - 0.5) * 0.8
    );
    coin.rotation.x = Math.random() * Math.PI;
    coin.rotation.z = Math.random() * Math.PI;
    chestGroup.add(coin);
    coins.push(coin);
  }

  // Gems — track as array for extraction animation
  const gemColors = [0xff0000, 0x00ff00, 0x0000ff, 0xff00ff, 0x00ffff];
  const gemMaterials = gemColors.map(color => new THREE.MeshStandardMaterial({
    color: color,
    metalness: 0.3,
    roughness: 0.1,
    transparent: true,
    opacity: 0.8
  }));
  const gemGeometry = new THREE.OctahedronGeometry(0.15);
  const gems = [];
  for (let i = 0; i < 15; i++) {
    const gem = new THREE.Mesh(gemGeometry, gemMaterials[i % gemMaterials.length]);
    gem.position.set(
      (Math.random() - 0.5) * 1.5,
      0.4 + Math.random() * 0.3,
      (Math.random() - 0.5) * 0.8
    );
    gem.rotation.set(Math.random(), Math.random(), Math.random());
    chestGroup.add(gem);
    gems.push(gem);
  }

  // Store references for interaction
  chestGroup.userData.gems = gems;
  chestGroup.userData.coins = coins;

  return chestGroup;
}

const treasureChest = createTreasureChest();
treasureChest.position.set(28, 4.0, 20);  // Approximate height, will be corrected by raycast when sand loads
treasureChest.rotation.y = Math.PI / 4;
scene.add(treasureChest);
addExclusionZone(treasureChest.position, 3.0);

// ============================================================================
// BEACH BUM WITH GUITAR
// ============================================================================

function createBeachBum() {
  const beachBum = new THREE.Group();

  // Skin tone - with subsurface scattering-like effect
  const skinColor = 0xd4a574;
  const skinMaterial = new THREE.MeshPhysicalMaterial({
    color: skinColor,
    roughness: 0.6,
    metalness: 0.0,
    sheen: 0.3,                // Subtle skin sheen
    sheenRoughness: 0.5,
    sheenColor: new THREE.Color(0xffcc99),
    clearcoat: 0.1,            // Slight skin shine
    clearcoatRoughness: 0.4
  });

  // === HEAD ===
  const headGeometry = new THREE.SphereGeometry(0.5, 32, 32);
  const head = new THREE.Mesh(headGeometry, skinMaterial);
  head.position.y = 1.8;
  head.scale.set(1, 1.1, 0.95); // Slightly elongated
  beachBum.add(head);

  // Eyes - friendly and bright
  const eyeGeometry = new THREE.SphereGeometry(0.09, 16, 16);
  const eyeWhiteMaterial = new THREE.MeshStandardMaterial({ color: 0xffffff });
  const pupilMaterial = new THREE.MeshStandardMaterial({ color: 0x3a7a5a });

  const leftEye = new THREE.Mesh(eyeGeometry, eyeWhiteMaterial);
  leftEye.position.set(-0.18, 1.92, 0.42);
  leftEye.scale.set(1, 0.85, 1); // Slightly squinted for happy look
  beachBum.add(leftEye);

  const leftPupil = new THREE.Mesh(new THREE.SphereGeometry(0.035, 16, 16), pupilMaterial);
  leftPupil.position.set(-0.18, 1.92, 0.48);
  beachBum.add(leftPupil);

  const rightEye = new THREE.Mesh(eyeGeometry, eyeWhiteMaterial);
  rightEye.position.set(0.18, 1.92, 0.42);
  rightEye.scale.set(1, 0.85, 1); // Slightly squinted for happy look
  beachBum.add(rightEye);

  const rightPupil = new THREE.Mesh(new THREE.SphereGeometry(0.035, 16, 16), pupilMaterial);
  rightPupil.position.set(0.18, 1.92, 0.48);
  beachBum.add(rightPupil);

  // Happy eyebrows - raised and curved
  const leftEyebrowCurve = new THREE.QuadraticBezierCurve3(
    new THREE.Vector3(-0.28, 2.05, 0.48),
    new THREE.Vector3(-0.18, 2.08, 0.48),
    new THREE.Vector3(-0.08, 2.05, 0.48)
  );
  const leftEyebrowPoints = leftEyebrowCurve.getPoints(15);
  const leftEyebrowGeometry = new THREE.BufferGeometry().setFromPoints(leftEyebrowPoints);
  const eyebrowMaterial = new THREE.LineBasicMaterial({ color: 0x6b5233, linewidth: 3 });
  const leftEyebrow = new THREE.Line(leftEyebrowGeometry, eyebrowMaterial);
  beachBum.add(leftEyebrow);

  const rightEyebrowCurve = new THREE.QuadraticBezierCurve3(
    new THREE.Vector3(0.08, 2.05, 0.48),
    new THREE.Vector3(0.18, 2.08, 0.48),
    new THREE.Vector3(0.28, 2.05, 0.48)
  );
  const rightEyebrowPoints = rightEyebrowCurve.getPoints(15);
  const rightEyebrowGeometry = new THREE.BufferGeometry().setFromPoints(rightEyebrowPoints);
  const rightEyebrow = new THREE.Line(rightEyebrowGeometry, eyebrowMaterial);
  beachBum.add(rightEyebrow);

  // Big happy smile - wider and more curved
  const smileCurve = new THREE.EllipseCurve(
    0, 0,
    0.28, 0.18,
    Math.PI * 0.15, Math.PI * 0.85,
    false,
    0
  );
  const smilePoints = smileCurve.getPoints(25);
  const smileGeometry = new THREE.BufferGeometry().setFromPoints(smilePoints);
  const smileMaterial = new THREE.LineBasicMaterial({ color: 0x000000, linewidth: 4 });
  const smile = new THREE.Line(smileGeometry, smileMaterial);
  smile.position.set(0, 1.62, 0.48);
  beachBum.add(smile);

  // Nose
  const noseGeometry = new THREE.ConeGeometry(0.06, 0.15, 8);
  const nose = new THREE.Mesh(noseGeometry, skinMaterial);
  nose.position.set(0, 1.75, 0.5);
  nose.rotation.x = Math.PI / 2;
  beachBum.add(nose);

  // Messy beach hair - with anisotropic-like sheen
  const hairColor = 0x8b6f47;
  const hairMaterial = new THREE.MeshPhysicalMaterial({
    color: hairColor,
    roughness: 0.8,
    metalness: 0.0,
    sheen: 0.6,                // Hair sheen for light reflection
    sheenRoughness: 0.3,
    sheenColor: new THREE.Color(0xaa8855)
  });

  // Main hair volume
  const hairGeometry = new THREE.SphereGeometry(0.52, 16, 16);
  const hair = new THREE.Mesh(hairGeometry, hairMaterial);
  hair.position.y = 2.0;
  hair.scale.set(1.1, 0.8, 1.05);
  beachBum.add(hair);

  // Messy hair strands
  for (let i = 0; i < 8; i++) {
    const strandGeometry = new THREE.CylinderGeometry(0.03, 0.02, 0.3, 8);
    const strand = new THREE.Mesh(strandGeometry, hairMaterial);
    const angle = (Math.PI * 2 * i) / 8;
    strand.position.set(
      Math.cos(angle) * 0.45,
      2.1 + Math.random() * 0.1,
      Math.sin(angle) * 0.45
    );
    strand.rotation.z = (Math.random() - 0.5) * 0.5;
    strand.rotation.x = (Math.random() - 0.5) * 0.3;
    beachBum.add(strand);
  }

  // === TORSO ===
  const torsoGeometry = new THREE.CylinderGeometry(0.35, 0.4, 0.9, 16);
  const shirtMaterial = new THREE.MeshPhysicalMaterial({
    color: 0xff6b35, // Bright orange tank top
    roughness: 0.8,
    metalness: 0.0,
    sheen: 0.4,                // Fabric sheen
    sheenRoughness: 0.6,
    sheenColor: new THREE.Color(0xff8855)
  });
  const torso = new THREE.Mesh(torsoGeometry, shirtMaterial);
  torso.position.y = 1.0;
  beachBum.add(torso);

  // === ARMS ===
  const armGeometry = new THREE.CylinderGeometry(0.12, 0.08, 0.8, 16);
  const handGeometry = new THREE.SphereGeometry(0.1, 16, 16);

  // Left arm (holding guitar neck) - single piece
  const leftArm = new THREE.Mesh(armGeometry, skinMaterial);
  leftArm.position.set(-0.5, 0.9, 0.2);
  leftArm.rotation.z = Math.PI / 4;
  leftArm.rotation.x = -Math.PI / 6;
  beachBum.add(leftArm);

  // Left hand
  const leftHand = new THREE.Mesh(handGeometry, skinMaterial);
  leftHand.position.set(-0.7, 0.5, 0.4);
  leftHand.scale.set(0.8, 1, 0.6);
  beachBum.add(leftHand);

  // Right arm (strumming) - single piece
  const rightArm = new THREE.Mesh(armGeometry, skinMaterial);
  rightArm.position.set(0.5, 0.9, 0.2);
  rightArm.rotation.z = -Math.PI / 4;
  rightArm.rotation.x = -Math.PI / 6;
  beachBum.add(rightArm);

  // Right hand
  const rightHand = new THREE.Mesh(handGeometry, skinMaterial);
  rightHand.position.set(0.65, 0.55, 0.45);
  rightHand.scale.set(0.8, 1, 0.6);
  beachBum.add(rightHand);

  // === LEGS (sitting position) ===
  const upperLegGeometry = new THREE.CylinderGeometry(0.15, 0.13, 0.6, 16);
  const lowerLegGeometry = new THREE.CylinderGeometry(0.13, 0.11, 0.5, 16);
  const shortsMaterial = new THREE.MeshStandardMaterial({
    color: 0x4a90e2, // Blue board shorts
    roughness: 0.6
  });

  // Left leg
  const leftUpperLeg = new THREE.Mesh(upperLegGeometry, shortsMaterial);
  leftUpperLeg.position.set(-0.2, 0.3, 0);
  leftUpperLeg.rotation.x = Math.PI / 2.5;
  beachBum.add(leftUpperLeg);

  const leftLowerLeg = new THREE.Mesh(lowerLegGeometry, skinMaterial);
  leftLowerLeg.position.set(-0.2, 0.0, 0.3);
  leftLowerLeg.rotation.x = Math.PI / 1.8;
  beachBum.add(leftLowerLeg);

  // Right leg
  const rightUpperLeg = new THREE.Mesh(upperLegGeometry, shortsMaterial);
  rightUpperLeg.position.set(0.2, 0.3, 0);
  rightUpperLeg.rotation.x = Math.PI / 2.5;
  beachBum.add(rightUpperLeg);

  const rightLowerLeg = new THREE.Mesh(lowerLegGeometry, skinMaterial);
  rightLowerLeg.position.set(0.2, 0.0, 0.3);
  rightLowerLeg.rotation.x = Math.PI / 1.8;
  beachBum.add(rightLowerLeg);

  // Feet
  const footGeometry = new THREE.BoxGeometry(0.15, 0.1, 0.25);
  const leftFoot = new THREE.Mesh(footGeometry, skinMaterial);
  leftFoot.position.set(-0.2, -0.2, 0.55);
  beachBum.add(leftFoot);

  const rightFoot = new THREE.Mesh(footGeometry, skinMaterial);
  rightFoot.position.set(0.2, -0.2, 0.55);
  beachBum.add(rightFoot);

  // === GUITAR ===
  const guitarGroup = new THREE.Group();

  // Guitar body
  const guitarBodyGeometry = new THREE.SphereGeometry(0.35, 32, 32);
  const guitarWoodMaterial = new THREE.MeshStandardMaterial({
    color: 0xcd853f,
    roughness: 0.4,
    metalness: 0.1
  });
  const guitarBody = new THREE.Mesh(guitarBodyGeometry, guitarWoodMaterial);
  guitarBody.scale.set(1, 1.3, 0.4);
  guitarGroup.add(guitarBody);

  // Sound hole
  const soundHoleGeometry = new THREE.CircleGeometry(0.12, 32);
  const soundHoleMaterial = new THREE.MeshStandardMaterial({ color: 0x000000 });
  const soundHole = new THREE.Mesh(soundHoleGeometry, soundHoleMaterial);
  soundHole.position.z = 0.15;
  soundHole.rotation.y = 0;
  guitarGroup.add(soundHole);

  // Guitar neck
  const neckGeometry = new THREE.BoxGeometry(0.08, 0.8, 0.05);
  const neckMaterial = new THREE.MeshStandardMaterial({
    color: 0x8b4513,
    roughness: 0.3
  });
  const neck = new THREE.Mesh(neckGeometry, neckMaterial);
  neck.position.set(0, 0.6, 0);
  guitarGroup.add(neck);

  // Fretboard
  const fretboardGeometry = new THREE.BoxGeometry(0.075, 0.79, 0.01);
  const fretboardMaterial = new THREE.MeshStandardMaterial({ color: 0x2c1608 });
  const fretboard = new THREE.Mesh(fretboardGeometry, fretboardMaterial);
  fretboard.position.set(0, 0.6, 0.03);
  guitarGroup.add(fretboard);

  // Frets
  for (let i = 0; i < 12; i++) {
    const fretGeometry = new THREE.CylinderGeometry(0.005, 0.005, 0.08, 8);
    const fretMaterial = new THREE.MeshStandardMaterial({ color: 0xc0c0c0, metalness: 0.8 });
    const fret = new THREE.Mesh(fretGeometry, fretMaterial);
    fret.position.set(0, 0.15 + i * 0.065, 0.03);
    fret.rotation.z = Math.PI / 2;
    guitarGroup.add(fret);
  }

  // Strings
  for (let i = 0; i < 6; i++) {
    const stringGeometry = new THREE.CylinderGeometry(0.002, 0.002, 1.0, 8);
    const stringMaterial = new THREE.MeshStandardMaterial({
      color: 0xd4af37,
      metalness: 0.9,
      roughness: 0.1
    });
    const string = new THREE.Mesh(stringGeometry, stringMaterial);
    string.position.set(-0.025 + i * 0.01, 0.2, 0.035);
    guitarGroup.add(string);
  }

  // Headstock
  const headstockGeometry = new THREE.BoxGeometry(0.15, 0.15, 0.04);
  const headstock = new THREE.Mesh(headstockGeometry, neckMaterial);
  headstock.position.set(0, 1.05, 0);
  guitarGroup.add(headstock);

  // Tuning pegs
  for (let i = 0; i < 6; i++) {
    const pegGeometry = new THREE.CylinderGeometry(0.015, 0.015, 0.06, 8);
    const pegMaterial = new THREE.MeshStandardMaterial({
      color: 0xc0c0c0,
      metalness: 0.8
    });
    const peg = new THREE.Mesh(pegGeometry, pegMaterial);
    const side = i < 3 ? -1 : 1;
    peg.position.set(side * 0.06, 0.98 + (i % 3) * 0.045, 0);
    peg.rotation.z = Math.PI / 2;
    guitarGroup.add(peg);
  }

  guitarGroup.position.set(0.15, 0.7, 0.3);
  guitarGroup.rotation.x = -Math.PI / 8;
  guitarGroup.rotation.y = -Math.PI / 6;
  guitarGroup.rotation.z = -Math.PI / 12;
  beachBum.add(guitarGroup);

  // Store references for animation
  beachBum.userData.guitar = guitarGroup;
  beachBum.userData.rightHand = rightHand;
  beachBum.userData.leftHand = leftHand;
  beachBum.userData.head = head;

  return beachBum;
}

// Music notes for animation
const musicNotes = [];

// Music note catching state (pi-nzq.7)
let notesCaught = 0;
let musicNoteFirstCatchDiscovered = false;
let melodyMakerDiscovered = false;

// Restore persisted music note state from saved game
(function restoreMusicNoteState() {
  const state = getGameState();
  if (state.musicNoteState) {
    notesCaught = state.musicNoteState.notesCaught || 0;
    musicNoteFirstCatchDiscovered = !!state.musicNoteState.musicNoteFirstCatchDiscovered;
    melodyMakerDiscovered = !!state.musicNoteState.melodyMakerDiscovered;
  }
})();

function saveMusicNoteState() {
  const state = getGameState();
  state.musicNoteState = {
    notesCaught,
    musicNoteFirstCatchDiscovered,
    melodyMakerDiscovered,
  };
  if (!state.stats) state.stats = {};
  state.stats.notesCaught = notesCaught;
  saveGameState(state);
}

// Raycaster for music note magnetism
const magnetismRaycaster = new THREE.Raycaster();

function handleMusicNoteCatch(note) {
  // Remove from scene and musicNotes array
  const idx = musicNotes.indexOf(note);
  if (idx !== -1) {
    musicNotes.splice(idx, 1);
  }
  unregisterInteractable(note);
  scene.remove(note);

  // Sparkle particles at note position
  emitSparkle(note.position.clone(), PARTICLES.NOTE_CATCH_SPARKLE_COUNT);

  // Play note_catch sound directly (pi-3pk.4)
  playNoteCatch();

  // Increment counter
  notesCaught++;

  // Discovery events
  if (!musicNoteFirstCatchDiscovered) {
    musicNoteFirstCatchDiscovered = true;
    eventBus.emit(GameEvents.OBJECT_CLICKED, { id: 'music_note_catch', type: 'object' });
  }
  if (notesCaught >= INTERACTION.MELODY_MAKER_THRESHOLD && !melodyMakerDiscovered) {
    melodyMakerDiscovered = true;
    eventBus.emit(GameEvents.OBJECT_CLICKED, { id: 'melody_maker', type: 'object' });
  }

  // Save state
  saveMusicNoteState();
}

function createMusicNote() {
  const noteGroup = new THREE.Group();

  // Note head (filled circle)
  const headGeometry = new THREE.SphereGeometry(0.08, 16, 16);
  const noteMaterial = new THREE.MeshStandardMaterial({
    color: 0x000000,  // Black
    roughness: 0.8,
    emissive: 0x222222,
    emissiveIntensity: 0.8,
  });
  const head = new THREE.Mesh(headGeometry, noteMaterial);
  head.scale.set(1, 0.8, 0.3);
  noteGroup.add(head);

  // Note stem
  const stemGeometry = new THREE.CylinderGeometry(0.02, 0.02, 0.25, 8);
  const stem = new THREE.Mesh(stemGeometry, noteMaterial);
  stem.position.set(0.07, 0.12, 0);
  noteGroup.add(stem);

  // Random note type variation
  if (Math.random() > 0.5) {
    // Add flag for eighth note
    const flagGeometry = new THREE.BoxGeometry(0.08, 0.08, 0.02);
    const flag = new THREE.Mesh(flagGeometry, noteMaterial);
    flag.position.set(0.1, 0.22, 0);
    noteGroup.add(flag);
  }

  noteGroup.scale.setScalar(3.0);  // Large for visibility and clickability
  return noteGroup;
}

const beachBum = createBeachBum();
// Position next to sign, on opposite side from treasure chest
beachBum.position.set(14, 2.5, 22);  // Left of sign (chest is at 28, sign at 20)
beachBum.rotation.y = Math.PI / 3;  // Angle towards viewer/sign
beachBum.scale.setScalar(2.0);  // Make him bigger
scene.add(beachBum);
addExclusionZone(beachBum.position, 3.0);

// ============================================================================
// EMPTY BEER BOTTLES
// ============================================================================

function createBeerBottle() {
  const bottleGroup = new THREE.Group();

  // Bottle body using LatheGeometry for realistic bottle shape
  const bottlePoints = [];
  // Bottom curve
  bottlePoints.push(new THREE.Vector2(0, 0));
  bottlePoints.push(new THREE.Vector2(0.12, 0.02));
  bottlePoints.push(new THREE.Vector2(0.14, 0.08));
  // Main body
  bottlePoints.push(new THREE.Vector2(0.14, 0.35));
  // Shoulder
  bottlePoints.push(new THREE.Vector2(0.13, 0.42));
  bottlePoints.push(new THREE.Vector2(0.10, 0.48));
  // Neck
  bottlePoints.push(new THREE.Vector2(0.05, 0.55));
  bottlePoints.push(new THREE.Vector2(0.045, 0.70));
  // Lip
  bottlePoints.push(new THREE.Vector2(0.055, 0.72));
  bottlePoints.push(new THREE.Vector2(0.055, 0.75));
  bottlePoints.push(new THREE.Vector2(0.045, 0.75));
  bottlePoints.push(new THREE.Vector2(0, 0.75));

  const bottleGeometry = new THREE.LatheGeometry(bottlePoints, 24);

  // Green glass material using MeshPhysicalMaterial for transparency
  const glassMaterial = new THREE.MeshPhysicalMaterial({
    color: 0x1a4d1a,
    metalness: 0.0,
    roughness: 0.1,
    transmission: 0.85,
    thickness: 0.5,
    envMapIntensity: 1.0,
    clearcoat: 0.3,
    clearcoatRoughness: 0.1,
    ior: 1.5
  });

  const bottle = new THREE.Mesh(bottleGeometry, glassMaterial);
  bottleGroup.add(bottle);

  // Simple label band
  const labelGeometry = new THREE.CylinderGeometry(0.145, 0.145, 0.12, 16, 1, true);
  const labelMaterial = new THREE.MeshStandardMaterial({
    color: 0xf5deb3,
    roughness: 0.9,
    side: THREE.DoubleSide
  });
  const label = new THREE.Mesh(labelGeometry, labelMaterial);
  label.position.y = 0.25;
  bottleGroup.add(label);

  return bottleGroup;
}

// Create scattered beer bottles near the beach bum
const beerBottles = [];
const bottlePositions = [
  { x: 12.5, z: 21.5, rot: 0.3, tilt: 0.1 },      // Standing upright
  { x: 13.8, z: 23.5, rot: 1.2, tilt: Math.PI / 2 - 0.2 },  // Fallen over
  { x: 15.2, z: 21.0, rot: 2.1, tilt: 0.15 },     // Slightly tilted
  { x: 11.8, z: 22.8, rot: 0.8, tilt: Math.PI / 2 + 0.1 },  // Fallen over
];

bottlePositions.forEach(pos => {
  const bottle = createBeerBottle();
  bottle.position.set(pos.x, 2.5, pos.z);
  bottle.rotation.y = pos.rot;
  bottle.rotation.x = pos.tilt;
  bottle.scale.setScalar(2.5);  // Scale to match scene
  scene.add(bottle);
  beerBottles.push(bottle);
});

// ============================================================================
// WELCOME SIGN
// ============================================================================

function createWelcomeSign() {
  const signGroup = new THREE.Group();

  // Weathered wood post material
  const postMaterial = new THREE.MeshStandardMaterial({
    color: 0x6B4423,
    roughness: 0.95,
    metalness: 0.0
  });

  // Rustic wooden posts with slight taper
  const postGeometry = new THREE.CylinderGeometry(0.12, 0.18, 4.5, 8);
  [-1.6, 1.6].forEach(x => {
    const post = new THREE.Mesh(postGeometry, postMaterial);
    post.position.set(x, 2.25, 0);
    signGroup.add(post);
  });

  // Create carved wooden sign board with procedural wood grain
  const boardCanvas = document.createElement('canvas');
  boardCanvas.width = 1024;
  boardCanvas.height = 384;
  const boardCtx = boardCanvas.getContext('2d');

  // Base wood color with grain
  const woodGradient = boardCtx.createLinearGradient(0, 0, 1024, 0);
  woodGradient.addColorStop(0, '#C4A574');
  woodGradient.addColorStop(0.3, '#D4B584');
  woodGradient.addColorStop(0.5, '#BA9564');
  woodGradient.addColorStop(0.7, '#D4B584');
  woodGradient.addColorStop(1, '#C4A574');
  boardCtx.fillStyle = woodGradient;
  boardCtx.fillRect(0, 0, 1024, 384);

  // Add horizontal wood grain lines
  boardCtx.strokeStyle = 'rgba(101, 67, 33, 0.15)';
  boardCtx.lineWidth = 1;
  for (let y = 0; y < 384; y += 6 + Math.random() * 8) {
    boardCtx.beginPath();
    boardCtx.moveTo(0, y);
    // Wavy grain lines
    for (let x = 0; x < 1024; x += 20) {
      boardCtx.lineTo(x, y + (Math.random() - 0.5) * 3);
    }
    boardCtx.stroke();
  }

  // Add knots randomly
  for (let i = 0; i < 3; i++) {
    const knotX = 100 + Math.random() * 824;
    const knotY = 50 + Math.random() * 284;
    const knotGradient = boardCtx.createRadialGradient(knotX, knotY, 0, knotX, knotY, 15);
    knotGradient.addColorStop(0, 'rgba(80, 50, 20, 0.6)');
    knotGradient.addColorStop(0.5, 'rgba(101, 67, 33, 0.3)');
    knotGradient.addColorStop(1, 'rgba(101, 67, 33, 0)');
    boardCtx.fillStyle = knotGradient;
    boardCtx.beginPath();
    boardCtx.ellipse(knotX, knotY, 18, 12, Math.random() * Math.PI, 0, Math.PI * 2);
    boardCtx.fill();
  }

  // Save the wood grain background so we can repaint text on click
  const woodBackground = boardCtx.getImageData(0, 0, 1024, 384);

  // Helper to draw carved text onto the board canvas
  function drawSignText(ctx, text) {
    // Restore wood grain background
    ctx.putImageData(woodBackground, 0, 0);

    // Auto-size font to fit within board width
    let fontSize = 140;
    ctx.font = `bold ${fontSize}px "Times New Roman", serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    while (ctx.measureText(text).width > 900 && fontSize > 40) {
      fontSize -= 4;
      ctx.font = `bold ${fontSize}px "Times New Roman", serif`;
    }

    // Outer shadow (light from top-left, creates depth illusion)
    ctx.fillStyle = 'rgba(255, 245, 220, 0.5)';
    ctx.fillText(text, 514, 190);

    // Inner shadow (carved inset effect)
    ctx.fillStyle = 'rgba(60, 35, 10, 0.7)';
    ctx.fillText(text, 510, 194);

    // Main carved text (darker, inset color)
    ctx.fillStyle = '#5C3317';
    ctx.fillText(text, 512, 192);

    // Weathered edge effect
    ctx.strokeStyle = 'rgba(80, 50, 20, 0.4)';
    ctx.lineWidth = 8;
    ctx.strokeRect(4, 4, 1016, 376);
  }

  // Initial text render
  drawSignText(boardCtx, 'SEND BEER');

  const boardTexture = new THREE.CanvasTexture(boardCanvas);
  boardTexture.anisotropy = 16;

  // Thicker, more rustic board
  const boardGeometry = new THREE.BoxGeometry(4.2, 1.6, 0.25);
  const boardMaterial = new THREE.MeshStandardMaterial({
    map: boardTexture,
    roughness: 0.85,
    metalness: 0.0,
    bumpScale: 0.02
  });
  const board = new THREE.Mesh(boardGeometry, boardMaterial);
  board.position.y = 3.5;
  signGroup.add(board);

  // Add rope detail hanging from posts
  const ropeMaterial = new THREE.MeshStandardMaterial({
    color: 0x8B7355,
    roughness: 1.0
  });
  const ropeGeometry = new THREE.TorusGeometry(0.08, 0.025, 8, 16, Math.PI);
  [-1.6, 1.6].forEach(x => {
    const rope = new THREE.Mesh(ropeGeometry, ropeMaterial);
    rope.position.set(x, 4.3, 0.12);
    rope.rotation.z = Math.PI;
    signGroup.add(rope);
  });

  // Store references for text cycling interaction
  signGroup.userData.boardCanvas = boardCanvas;
  signGroup.userData.boardCtx = boardCtx;
  signGroup.userData.boardTexture = boardTexture;
  signGroup.userData.drawSignText = drawSignText;
  signGroup.userData.board = board;

  return signGroup;
}

const welcomeSign = createWelcomeSign();
welcomeSign.position.set(20, 2, 20);  // Position in front of initial camera view
welcomeSign.rotation.y = Math.PI * 0.25;  // Face the camera at (50, 30, 50)
welcomeSign.scale.setScalar(2.0);  // 100% bigger
scene.add(welcomeSign);

// ============================================================================
// INTERACTION SYSTEM SETUP
// ============================================================================

initInteractionSystem(renderer, camera, scene);

// Register crabs as interactables
crabs.forEach((crab, i) => {
  registerInteractable(crab.mesh, {
    type: 'creature',
    id: `crab_${i}`,
    boundingRadius: 1.5,
    cooldown: 500,
    onClick: () => {
      const activeMesh = crab.meshyModel || crab.mesh;
      const clickPoint = activeMesh.position.clone();
      eventBus.emit(GameEvents.CREATURE_CLICKED, { id: `crab_${i}`, type: 'creature', species: 'crab', index: i, mesh: activeMesh });

      // 30% chance crab drops a seashell at its position
      if (Math.random() < 0.3) {
        const dropPos = clickPoint.clone();
        dropPos.x += (Math.random() - 0.5) * 1.5;
        dropPos.z += (Math.random() - 0.5) * 1.5;
        spawnResource('seashell', dropPos);
      }

      // Track total crab clicks for wave bonus
      totalCrabClicks++;
      if (totalCrabClicks % INTERACTION.CRAB_WAVE_CLICK_INTERVAL === 0) {
        // Every 5th click triggers crab wave animation
        crab.waveState.active = true;
        crab.waveState.timer = 0;
        eventBus.emit(GameEvents.OBJECT_CLICKED, { id: 'crab_wave', type: 'creature' });
      } else {
        // Scatter the clicked crab (not during wave)
        crab.scatter(clickPoint);
      }

      // Herd panic: scatter all crabs within proximity
      crabs.forEach((other, j) => {
        if (j === i) return;
        const otherMesh = other.meshyModel || other.mesh;
        const dist = activeMesh.position.distanceTo(otherMesh.position);
        if (dist < INTERACTION.CRAB_SCATTER_RADIUS) {
          other.scatter(clickPoint);
        }
      });

      // Crab Stampede detection
      const scatteringCount = crabs.filter(c => c.scatterState).length;
      if (scatteringCount >= CREATURE.CRAB_STAMPEDE_THRESHOLD) {
        eventBus.emit(GameEvents.OBJECT_CLICKED, { id: 'crab_stampede', type: 'creature' });
      }

      // pi-d7w.5: Crab Orchestra — clicked crab near beach bum, crabs within proximity
      if (activeMesh.position.distanceTo(beachBum.position) < INTERACTION.CRAB_ORCHESTRA_RADIUS) {
        const crabsNearBum = crabs.filter(c => {
          const m = c.meshyModel || c.mesh;
          return m.position.distanceTo(beachBum.position) < INTERACTION.CRAB_ORCHESTRA_RADIUS;
        });
        if (crabsNearBum.length >= INTERACTION.CRAB_ORCHESTRA_MIN_CRABS) {
          // Synchronized claw-raise for all nearby crabs
          crabsNearBum.forEach(c => {
            c.waveState.active = true;
            c.waveState.timer = 0;
            c.waveState.duration = INTERACTION.CRAB_ORCHESTRA_WAVE_DURATION;
          });
          // Emit discovery on first occurrence
          if (!crabOrchestraDiscovered) {
            crabOrchestraDiscovered = true;
            eventBus.emit(GameEvents.OBJECT_CLICKED, { id: 'crab_orchestra', type: 'object' });
          }
        }
      }
    },
  });
});

// Register dolphins as interactables
dolphins.forEach((dolphin, i) => {
  registerInteractable(dolphin.mesh, {
    type: 'creature',
    id: `dolphin_${i}`,
    boundingRadius: 3.0,
    cooldown: 500,
    onClick: () => {
      const activeMesh = dolphin.meshyModel || dolphin.mesh;
      eventBus.emit(GameEvents.CREATURE_CLICKED, { id: `dolphin_${i}`, type: 'creature', species: 'dolphin', index: i, mesh: activeMesh });

      // Track clicks per dolphin for barrel roll / show-off
      dolphin.clickCount++;

      // Nth click on same dolphin: barrel roll + show-off
      if (dolphin.clickCount === CREATURE.DOLPHIN_BARREL_ROLL_CLICK) {
        dolphin.barrelRollPending = true;
        playDolphinChirp(); // pi-3pk.2: Dolphin chirp on barrel roll + show-off

        // Emit barrel roll discovery
        if (!barrelRollDiscovered) {
          barrelRollDiscovered = true;
          eventBus.emit(GameEvents.OBJECT_CLICKED, { id: 'barrel_roll', type: 'creature' });
        }

        // Enter show-off mode after barrel roll
        dolphin.showOffState = { active: true, timer: 0, trickIndex: 0 };

        // Emit show-off discovery
        if (!showOffDiscovered) {
          showOffDiscovered = true;
          eventBus.emit(GameEvents.OBJECT_CLICKED, { id: 'show_off', type: 'creature' });
        }
      } else {
        // Normal trick behavior for non-3rd clicks
        dolphin.doTrick('click');
      }

      // Synchronized splash detection: track recent dolphin clicks
      const now = performance.now();
      recentDolphinClicks.push({ dolphinIndex: i, timestamp: now });
      // Filter out entries older than the sync window
      recentDolphinClicks = recentDolphinClicks.filter(entry => now - entry.timestamp < CREATURE.DOLPHIN_SYNC_CLICK_WINDOW);
      // Check if 2+ unique dolphins were clicked within the window
      const uniqueIndices = new Set(recentDolphinClicks.map(entry => entry.dolphinIndex));
      if (uniqueIndices.size >= 2) {
        // Force synchronized jump on all recently clicked dolphins
        uniqueIndices.forEach(idx => {
          dolphins[idx].syncJumpPending = true;
        });
        // Emit synchronized splash discovery
        if (!synchronizedSplashDiscovered) {
          synchronizedSplashDiscovered = true;
          eventBus.emit(GameEvents.OBJECT_CLICKED, { id: 'synchronized_splash', type: 'creature' });
        }
        // Clear recent clicks to avoid re-triggering immediately
        recentDolphinClicks = [];
      }

      // pi-d7w.6: Dolphin Serenade — dolphin clicked within window of beach bum click
      if (lastBeachBumClickTime > 0 && (now - lastBeachBumClickTime) < INTERACTION.DOLPHIN_SERENADE_WINDOW) {
        // Enhanced jump: 2x height via highJump trick with extra multiplier
        dolphin.jumpPhase = 0;
        dolphin.trickState = {
          type: 'highJump',
          timer: 0,
          duration: 2.0,
          flipRotation: 0,
        };
        // Musical burst at dolphin position
        const dolphinPos = activeMesh.position.clone();
        dolphinPos.y = Math.max(dolphinPos.y, 0);
        emitMusicalBurst(dolphinPos, 8);
        // Discovery
        if (!dolphinSerenadeDiscovered) {
          dolphinSerenadeDiscovered = true;
          eventBus.emit(GameEvents.OBJECT_CLICKED, { id: 'dolphin_serenade', type: 'object' });
        }
        // Reset so it only triggers once per beach bum click
        lastBeachBumClickTime = 0;
      }
    },
  });
});

// Register seagulls as interactables
seagulls.forEach((seagull, i) => {
  registerInteractable(seagull.mesh, {
    type: 'creature',
    id: `seagull_${i}`,
    boundingRadius: 2.5,
    cooldown: 500,
    onClick: () => {
      const activeMesh = seagull.meshyModel || seagull.mesh;
      eventBus.emit(GameEvents.CREATURE_CLICKED, { id: `seagull_${i}`, type: 'creature', species: 'seagull', index: i, mesh: activeMesh });
      seagull.dive();

      // --- Golden Gull tracking (pi-nzq.4) ---
      totalSeagullClicks++;
      if (totalSeagullClicks >= CREATURE.SEAGULL_GOLDEN_CLICK_THRESHOLD && !goldenGullSpawned) {
        spawnGoldenGull(seagulls[0]);
      }
      // Golden gull discovery on first click
      if (seagull.isGolden && !goldenGullDiscovered) {
        goldenGullDiscovered = true;
        eventBus.emit(GameEvents.OBJECT_CLICKED, { id: 'golden_gull', type: 'creature' });
        saveSeagullState();
      }
      saveSeagullState();

      // --- Formation Flight tracking (pi-nzq.6) ---
      const now = performance.now();
      recentSeagullClicks.push({ seagullIndex: i, timestamp: now });
      // Filter clicks older than the window
      recentSeagullClicks = recentSeagullClicks.filter(c => now - c.timestamp < CREATURE.FORMATION_FLIGHT_CLICK_WINDOW);
      // Check for enough unique seagulls clicked within window
      const uniqueIndices = new Set(recentSeagullClicks.map(c => c.seagullIndex));
      if (uniqueIndices.size >= CREATURE.FORMATION_FLIGHT_UNIQUE_THRESHOLD && !formationFlightActive) {
        triggerFormationFlight();
        recentSeagullClicks = []; // reset after triggering
      }
    },
  });
});

// ============================================================================
// OBJECT INTERACTION STATE & HANDLERS (M3)
// ============================================================================

// --- Treasure chest state ---
const chestState = {
  gemsRemaining: 15, coinsRemaining: 30,
  lastDailyReset: parseInt(localStorage.getItem('chest_daily_reset') || '0', 10),
  wobbleTimer: 0, wobbleActive: false, floatingGems: [],
};
// Daily refill check
(function() {
  const now = Date.now();
  if (now - chestState.lastDailyReset > ANIMATION.CHEST_DAILY_RESET_MS) {
    chestState.gemsRemaining = 15; chestState.coinsRemaining = 30;
    chestState.lastDailyReset = now;
    localStorage.setItem('chest_daily_reset', String(now));
    treasureChest.userData.gems.forEach(g => { g.visible = true; });
    treasureChest.userData.coins.forEach(c => { c.visible = true; });
  }
})();

// --- Palm tree state ---
const palmTreeState = palmTrees.map(() => ({
  coconutsRemaining: 3, shakeTimer: 0, shakeActive: false,
  baseRotZ: 0, baseRotX: 0, lastHarvestTime: 0, fallingCoconuts: [],
}));

// --- Welcome sign state ---
const signMessages = ['SEND BEER', 'WISH YOU WERE HERE', 'PARADISE FOUND', 'NO WIFI NO PROBLEMS'];
let signMessageIndex = 0;
let signWobbleTimer = 0;
let signWobbleActive = false;

// --- Beer bottle state ---
const bottleState = beerBottles.map((b, i) => ({
  isStanding: bottlePositions[i].tilt < 0.5,
  tiltTarget: bottlePositions[i].tilt,
  rollTimer: 0, rollActive: false,
  flashTimer: 0, flashActive: false,
  originalMetalness: 0.0, originalRoughness: 0.1,
  emissivePulseActive: false,
}));

// --- Beer bottle recycling tracking ---
let recentBottleClicks = [];
let bottlesClickedThisSession = new Set();
let recyclerDiscovered = false;
const floatingShards = []; // glass shard float-up effects
let bottleResetTimer = 0;
let bottleResetActive = false;

// --- Beach bum state ---
const beachBumState = {
  strumSpeedMultiplier: 1.0, speedUpTimer: 0, speedUpActive: false,
  noteSpawnMultiplier: 1.0, headTiltTimer: 0, headTiltActive: false,
  guitarSoloActive: false, guitarSoloTimer: 0, guitarSoloBurstTimer: 0,
  // Idle behavior cycle (pi-d7w.2)
  idleBehavior: 'strum',
  idleTimer: 0,
  idleStates: ['strum', 'stretch', 'look', 'point', 'sleep'],
  idleStateDuration: 20,
  // Reaction animations (pi-d7w.7)
  reactionActive: false,
  reactionTimer: 0,
  reactionType: null, // 'thumbsUp' or 'applause'
  reactionDuration: 0,
  // Gesture cues toward undiscovered content (pi-d7w.3)
  gestureCueActive: false,
  gestureCueTimer: 0,
  gestureCueDuration: INTERACTION.GESTURE_CUE_DURATION,
  gestureCueTargetRotY: 0,
};

// --- Beach bum session tracking ---
let beachBumClicksThisSession = 0;
let guitarSoloDiscovered = false;
let jamSessionDiscovered = false;

// --- Combination secrets tracking (pi-d7w) ---
let crabOrchestraDiscovered = false;
let dolphinSerenadeDiscovered = false;
let lastBeachBumClickTime = 0; // For dolphin serenade timing

// --- Player idle tracking (pi-d7w.2) ---
let lastPlayerClickTime = 0;
let idleSpeechBubbleShown = false;

// --- Gesture cue tracking (pi-d7w.3) ---
let lastGestureCueTime = 0;  // Timestamp of last gesture cue shown
const GESTURE_CUE_IDLE_THRESHOLD = INTERACTION.GESTURE_IDLE_THRESHOLD;
const GESTURE_CUE_COOLDOWN = INTERACTION.GESTURE_COOLDOWN;
const gestureCueHints = {
  creatures: [
    "Did you see that scuttle?",
    "Something moved over there!",
  ],
  secrets: [
    "I feel like we're missing something...",
    "The sand looks different over there",
  ],
  objects: [
    "Hey, check that out!",
    "Have you tried clicking on that?",
  ],
  bonuses: [
    "There's gotta be more to discover...",
    "Try something different!",
  ],
  combos: [
    "What if things worked together?",
    "I wonder what happens when...",
  ],
};

// Map discovery keys to functions that return world positions of scene objects.
// Only includes items with physical scene presence (not bonuses/combos).
function getDiscoverablePositions() {
  const positions = [];
  const discoveryMap = getDiscoveryMap();
  const state = getGameState();

  // Helper to add undiscovered items
  function addIfUndiscovered(key, positionFn) {
    if (!state.discoveries[key] && discoveryMap[key]) {
      const pos = positionFn();
      if (pos) {
        positions.push({ key, position: pos, category: discoveryMap[key].category });
      }
    }
  }

  // Crabs — use the first crab's position as representative
  addIfUndiscovered('crab', () => crabs.length > 0 ? crabs[0].mesh.position.clone() : null);
  // Dolphins
  addIfUndiscovered('dolphin', () => dolphins.length > 0 ? dolphins[0].mesh.position.clone() : null);
  // Seagulls
  addIfUndiscovered('seagull', () => seagulls.length > 0 ? seagulls[0].mesh.position.clone() : null);
  // Treasure chest
  addIfUndiscovered('treasure_chest', () => treasureChest.position.clone());
  // Palm trees
  addIfUndiscovered('palmtree', () => palmTrees.length > 0 ? palmTrees[0].position.clone() : null);
  // Welcome sign
  addIfUndiscovered('welcome_sign', () => welcomeSign.position.clone());
  // Beer bottles
  addIfUndiscovered('beer_bottle', () => beerBottles.length > 0 ? beerBottles[0].position.clone() : null);
  // Buried seashell (secret)
  addIfUndiscovered('buried_seashell', () => seashell ? seashell.position.clone() : null);
  // Tide pool (secret)
  addIfUndiscovered('tide_pool', () => tidePoolGroup ? tidePoolGroup.position.clone() : null);
  // Message in a bottle (secret)
  addIfUndiscovered('message_in_bottle', () => messageBottleGroup ? messageBottleGroup.position.clone() : null);
  // Hermit crab (secret)
  addIfUndiscovered('hermit_crab', () => hermitCrabGroup ? hermitCrabGroup.position.clone() : null);

  return positions;
}

// --- Object click handlers ---
function onTreasureChestClick() {
  eventBus.emit(GameEvents.OBJECT_CLICKED, { id: 'treasure_chest', type: 'object' });
  chestState.wobbleActive = true; chestState.wobbleTimer = 0;
  if (chestState.gemsRemaining > 0) {
    const gemIdx = treasureChest.userData.gems.length - chestState.gemsRemaining;
    const gem = treasureChest.userData.gems[gemIdx];
    if (gem) {
      chestState.gemsRemaining--;
      const worldPos = new THREE.Vector3(); gem.getWorldPosition(worldPos);
      const floatingGem = gem.clone(); floatingGem.position.copy(worldPos);
      floatingGem.scale.setScalar(treasureChest.scale.x * 1.5);
      floatingGem.material = gem.material.clone(); floatingGem.material.transparent = true;
      scene.add(floatingGem);
      chestState.floatingGems.push({ mesh: floatingGem, startY: worldPos.y, elapsed: 0, duration: 1.5 });
      gem.visible = false;
    }
  } else if (chestState.coinsRemaining > 0) {
    const coinIdx = treasureChest.userData.coins.length - chestState.coinsRemaining;
    const coin = treasureChest.userData.coins[coinIdx];
    if (coin) { chestState.coinsRemaining--; coin.visible = false; }
  }
}

function onPalmTreeClick(treeIndex) {
  const state = palmTreeState[treeIndex]; const tree = palmTrees[treeIndex];
  eventBus.emit(GameEvents.OBJECT_CLICKED, { id: `palmtree_${treeIndex}`, type: 'object' });
  const now = Date.now();
  if (state.coconutsRemaining <= 0 && now - state.lastHarvestTime > ANIMATION.COCONUT_REFILL_MS) {
    state.coconutsRemaining = 3;
    tree.children.filter(c => c.geometry && c.geometry.type === 'SphereGeometry' && c.geometry.parameters && Math.abs(c.geometry.parameters.radius - 0.4) < 0.01).forEach(c => { c.visible = true; });
  }
  state.shakeActive = true; state.shakeTimer = 0;
  state.baseRotZ = tree.rotation.z; state.baseRotX = tree.rotation.x;
  if (state.coconutsRemaining > 0) {
    state.lastHarvestTime = now;
    const numToDrop = Math.min(state.coconutsRemaining, 1 + Math.floor(Math.random() * 2));
    const coconuts = tree.children.filter(c => c.visible && c.geometry && c.geometry.type === 'SphereGeometry' && c.geometry.parameters && Math.abs(c.geometry.parameters.radius - 0.4) < 0.01);
    for (let d = 0; d < numToDrop && d < coconuts.length; d++) {
      const coconut = coconuts[d]; coconut.visible = false; state.coconutsRemaining--;
      const worldPos = new THREE.Vector3(); coconut.getWorldPosition(worldPos);
      const fallingCoconut = createResourceMesh('coconut');
      fallingCoconut.position.copy(worldPos); fallingCoconut.castShadow = false; scene.add(fallingCoconut);
      state.fallingCoconuts.push({ mesh: fallingCoconut, velocity: new THREE.Vector3((Math.random() - 0.5) * 2, 0, (Math.random() - 0.5) * 2), bounces: 0, elapsed: 0, restY: null });
    }
  }
}

function onWelcomeSignClick() {
  eventBus.emit(GameEvents.OBJECT_CLICKED, { id: 'welcome_sign', type: 'object' });
  signMessageIndex = (signMessageIndex + 1) % signMessages.length;
  const { boardCtx, boardTexture, drawSignText } = welcomeSign.userData;
  drawSignText(boardCtx, signMessages[signMessageIndex]); boardTexture.needsUpdate = true;
  signWobbleActive = true; signWobbleTimer = 0;
}

function onBeerBottleClick(bottleIndex) {
  const state = bottleState[bottleIndex];
  const bottle = beerBottles[bottleIndex];
  eventBus.emit(GameEvents.OBJECT_CLICKED, { id: `beer_bottle_${bottleIndex}`, type: 'object' });

  // Original tip/roll behavior
  if (state.isStanding) { state.isStanding = false; state.tiltTarget = Math.PI / 2 - 0.2; }
  state.rollActive = true; state.rollTimer = 0;

  // 1. Specular flash on click - briefly increase metalness and decrease roughness
  state.flashActive = true; state.flashTimer = 0;
  const glassMesh = bottle.children[0]; // The bottle mesh (first child of group)
  if (glassMesh && glassMesh.material) {
    glassMesh.material.metalness = 1.0;
    glassMesh.material.roughness = 0.0;
  }

  // 2. "Cheers!" combo - track recent bottle clicks
  const now = Date.now();
  recentBottleClicks.push({ bottleIndex, timestamp: now });
  // Prune old clicks beyond window
  recentBottleClicks = recentBottleClicks.filter(c => now - c.timestamp <= INTERACTION.CHEERS_CLICK_WINDOW);
  // Check if enough different bottles clicked within window
  const uniqueBottles = new Set(recentBottleClicks.map(c => c.bottleIndex));
  if (uniqueBottles.size >= INTERACTION.CHEERS_MIN_BOTTLES) {
    eventBus.emit(GameEvents.OBJECT_CLICKED, { id: 'cheers', type: 'object' });
    eventBus.emit(GameEvents.OBJECT_CLICKED, { id: 'beer_bottle', type: 'object' }); // clink sound
    recentBottleClicks = []; // reset after cheers
  }

  // 3. "Recycler" combo - track all bottles clicked this session
  bottlesClickedThisSession.add(bottleIndex);
  if (bottlesClickedThisSession.size >= INTERACTION.RECYCLER_MIN_BOTTLES && !recyclerDiscovered) {
    recyclerDiscovered = true;

    // All bottles glow (emissive pulse)
    bottleState.forEach(bs => { bs.emissivePulseActive = true; });

    // Pick a random bottle to disappear
    const disappearIdx = Math.floor(Math.random() * beerBottles.length);
    const disappearBottle = beerBottles[disappearIdx];
    const worldPos = new THREE.Vector3();
    disappearBottle.getWorldPosition(worldPos);

    // Remove the bottle from the scene
    scene.remove(disappearBottle);

    // Create glass shard mesh (small triangular geometry, transparent green material)
    const shardGeo = new THREE.BufferGeometry();
    const vertices = new Float32Array([
      0, 0.5, 0,
      -0.3, -0.2, 0.1,
      0.3, -0.2, -0.1,
    ]);
    shardGeo.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    shardGeo.computeVertexNormals();
    const shardMat = new THREE.MeshPhysicalMaterial({
      color: 0x1a4d1a,
      transparent: true,
      opacity: 0.8,
      transmission: 0.6,
      roughness: 0.1,
      metalness: 0.0,
      side: THREE.DoubleSide,
    });
    const shard = new THREE.Mesh(shardGeo, shardMat);
    shard.position.copy(worldPos);
    shard.scale.setScalar(2.0);
    scene.add(shard);
    floatingShards.push({ mesh: shard, startY: worldPos.y, elapsed: 0, duration: 1.5 });

    // Remaining bottles reset to standing over 120 seconds
    bottleResetActive = true;
    bottleResetTimer = 0;

    // Emit recycler discovery
    eventBus.emit(GameEvents.OBJECT_CLICKED, { id: 'recycler', type: 'object' });
  }
}

function onBeachBumClick() {
  eventBus.emit(GameEvents.OBJECT_CLICKED, { id: 'beach_bum', type: 'object' });

  // Reset idle tracking on click
  lastPlayerClickTime = performance.now() / 1000;
  idleSpeechBubbleShown = false;

  // pi-d7w.6: Track beach bum click time for dolphin serenade
  lastBeachBumClickTime = performance.now();

  // Increment click counter
  beachBumClicksThisSession++;

  // Show context-aware speech bubble (pi-d7w.1)
  const bumPos = new THREE.Vector3();
  beachBum.getWorldPosition(bumPos);
  bumPos.y += 3;

  const { discovered } = getDiscoveryCount();
  if (discovered <= 1) {
    showSpeechBubble("Try clicking something... anything!", bumPos, camera);
  } else if (discovered <= 3) {
    showSpeechBubble("Ha! You're getting the hang of it!", bumPos, camera);
  } else if (discovered >= 5) {
    showSpeechBubble("You're a natural explorer!", bumPos, camera);
  } else {
    showSpeechBubble("Keep exploring, friend!", bumPos, camera);
  }

  // Original speed-up behavior
  beachBumState.speedUpActive = true; beachBumState.speedUpTimer = 0;
  beachBumState.strumSpeedMultiplier = BEACH_BUM.CLICK_STRUM_MULTIPLIER; beachBumState.noteSpawnMultiplier = BEACH_BUM.CLICK_NOTE_MULTIPLIER;
  beachBumState.headTiltActive = true; beachBumState.headTiltTimer = 0;
  const guitarWorldPos = new THREE.Vector3();
  beachBum.userData.guitar.getWorldPosition(guitarWorldPos);
  for (let n = 0; n < BEACH_BUM.NOTE_BURST_COUNT; n++) {
    const angle = (n / BEACH_BUM.NOTE_BURST_COUNT) * Math.PI * 2;
    const note = createMusicNote();
    note.position.set(guitarWorldPos.x + Math.cos(angle) * 1.5, guitarWorldPos.y + 0.5, guitarWorldPos.z + Math.sin(angle) * 1.5);
    note.userData.velocity = new THREE.Vector3(Math.cos(angle) * 0.2, 0.15 + Math.random() * 0.1, Math.sin(angle) * 0.2);
    note.userData.life = 0; note.userData.maxLife = BEACH_BUM.NOTE_MAX_LIFE_MIN + Math.random() * (BEACH_BUM.NOTE_MAX_LIFE_MAX - BEACH_BUM.NOTE_MAX_LIFE_MIN);
    note.userData.rotationSpeed = (Math.random() - 0.5) * 0.5;
    scene.add(note); musicNotes.push(note);
    // Register burst notes as interactable (pi-nzq.7)
    registerInteractable(note, {
      type: 'object',
      id: 'music_note',
      boundingRadius: 0.8,
      cooldown: 100,
      onClick: () => handleMusicNoteCatch(note),
    });
  }

  // Guitar solo on Nth click
  if (beachBumClicksThisSession === BEACH_BUM.GUITAR_SOLO_CLICK_THRESHOLD && !guitarSoloDiscovered) {
    guitarSoloDiscovered = true;
    beachBumState.guitarSoloActive = true;
    beachBumState.guitarSoloTimer = 0;
    beachBumState.guitarSoloBurstTimer = 0;
    beachBumState.strumSpeedMultiplier = BEACH_BUM.GUITAR_SOLO_STRUM_MULTIPLIER;
    beachBumState.noteSpawnMultiplier = BEACH_BUM.GUITAR_SOLO_NOTE_MULTIPLIER;
    playGuitarFlourish(); // pi-3pk.1: Enhanced guitar strum flourish
    eventBus.emit(GameEvents.OBJECT_CLICKED, { id: 'guitar_solo', type: 'object' });
  }

  // "Jam Session" timing discovery - check if any dolphin is mid-jump (airborne)
  if (!jamSessionDiscovered) {
    const anyDolphinAirborne = dolphins.some(dolphin => {
      const activeMesh = dolphin.meshyModel || dolphin.mesh;
      return activeMesh.position.y > 0.5;
    });
    if (anyDolphinAirborne) {
      jamSessionDiscovered = true;
      eventBus.emit(GameEvents.OBJECT_CLICKED, { id: 'jam_session', type: 'object' });
    }
  }
}

// Register palm trees as interactables
palmTrees.forEach((tree, i) => {
  registerInteractable(tree, {
    type: 'object',
    id: `palmtree_${i}`,
    boundingRadius: 3.0,
    cooldown: 1000,
    onClick: () => onPalmTreeClick(i),
  });
});

// Register treasure chest
registerInteractable(treasureChest, {
  type: 'object',
  id: 'treasure_chest',
  boundingRadius: 2.5,
  cooldown: 1000,
  onClick: onTreasureChestClick,
});

// Register welcome sign
registerInteractable(welcomeSign, {
  type: 'object',
  id: 'welcome_sign',
  boundingRadius: 2.0,
  cooldown: 1000,
  onClick: onWelcomeSignClick,
});

// Register beer bottles
beerBottles.forEach((bottle, i) => {
  registerInteractable(bottle, {
    type: 'object',
    id: `beer_bottle_${i}`,
    boundingRadius: 0.8,
    cooldown: 1000,
    onClick: () => onBeerBottleClick(i),
  });
});

// Register beach bum
registerInteractable(beachBum, {
  type: 'object',
  id: 'beach_bum',
  boundingRadius: 2.5,
  cooldown: 1000,
  onClick: onBeachBumClick,
});

// ============================================================================
// RESOURCE GROUND PICKUP (pi-3ao.5)
// ============================================================================

// Track resource interactable counter for unique IDs
let resourceInteractableCounter = 0;

/**
 * Handle clicking a resource on the ground.
 * - Coconuts crack into 2 coconut shells
 * - All other resources are collected directly
 */
function onResourceClick(mesh) {
  const resourceType = mesh.userData.resourceType;
  if (!resourceType) return;

  const position = mesh.position.clone();

  if (resourceType === 'coconut') {
    // --- Coconut cracking special case ---
    // Remove the coconut
    unregisterInteractable(mesh);
    removeResource('coconut', mesh);

    // Spawn 2 coconut shells at slight offsets
    const offset1 = position.clone();
    offset1.x += 0.3;
    const offset2 = position.clone();
    offset2.x -= 0.3;

    spawnResource('coconut_shell', offset1);
    spawnResource('coconut_shell', offset2);

    // Add 2 coconut shells to inventory
    addToInventory('coconut_shell', 2);

    // Play clink sound
    playSound('clink');

    // Emit collected event for the coconut (cracked)
    eventBus.emit(GameEvents.RESOURCE_COLLECTED, {
      resourceType: 'coconut',
      position: { x: position.x, y: position.y, z: position.z },
      mesh,
      cracked: true,
    });

    // Sparkle at crack position
    emitSparkle(position, 6);
  } else {
    // --- Standard resource collection ---
    unregisterInteractable(mesh);
    removeResource(resourceType, mesh);
    addToInventory(resourceType);

    // Emit collected event
    eventBus.emit(GameEvents.RESOURCE_COLLECTED, {
      resourceType,
      position: { x: position.x, y: position.y, z: position.z },
      mesh,
    });

    // Sparkle at pickup position
    emitSparkle(position, 4);

    // Play a small pickup sound
    playSound('clink');
  }
}

// Listen for RESOURCE_SPAWNED to register each new resource as interactable
eventBus.on(GameEvents.RESOURCE_SPAWNED, (data) => {
  const { mesh, resourceType } = data;
  if (!mesh) return;

  const id = `resource_${resourceType}_${resourceInteractableCounter++}`;

  registerInteractable(mesh, {
    type: 'object',
    id,
    boundingRadius: 1.0,
    cooldown: 300,
    onClick: () => onResourceClick(mesh),
  });
});

// ============================================================================
// HIDDEN SECRETS
// ============================================================================

// --- pi-3rt.1: Buried Seashell ---
const seashellGeometry = new THREE.TorusGeometry(0.4, 0.15, 8, 16, Math.PI);
const seashellMaterial = new THREE.MeshStandardMaterial({
  color: 0xffe4c4,
  roughness: 0.6,
  metalness: 0.1,
});
const seashell = new THREE.Mesh(seashellGeometry, seashellMaterial);
// Random position on sand each session
const seashellX = -15 + Math.random() * 30;
const seashellZ = -15 + Math.random() * 30;
seashell.position.set(seashellX, 2.0 - 0.15, seashellZ);
seashell.rotation.x = Math.PI / 2; // Half buried
seashell.visible = false; // Start hidden until camera is close
scene.add(seashell);

let seashellUnearthed = false;

function onSeashellClick() {
  if (seashellUnearthed) return;
  seashellUnearthed = true;

  // Animate unearthing: raise Y by 0.3 over 0.5s
  const startY = seashell.position.y;
  const targetY = startY + 0.3;
  const startTime = performance.now();

  function animateUnearth() {
    const elapsed = (performance.now() - startTime) / 1000;
    const t = Math.min(elapsed / 0.5, 1.0);
    seashell.position.y = startY + (targetY - startY) * t;
    if (t < 1.0) requestAnimationFrame(animateUnearth);
  }
  animateUnearth();

  // Sand puff
  createSandPuff(seashell.position);

  // Emit discovery
  eventBus.emit(GameEvents.OBJECT_CLICKED, { id: 'buried_seashell', type: 'object' });
}

registerInteractable(seashell, {
  type: 'object',
  id: 'buried_seashell',
  boundingRadius: 1.0,
  cooldown: 1000,
  onClick: onSeashellClick,
});

// --- pi-3rt.2: Hermit Crab Secret ---
let hermitCrabGroup = null;
if (Math.random() < 0.5) {
  hermitCrabGroup = new THREE.Group();

  // Body: brown sphere
  const crabBodyGeo = new THREE.SphereGeometry(0.2, 6, 6);
  const crabBodyMat = new THREE.MeshStandardMaterial({ color: 0x8B4513, roughness: 0.7 });
  const crabBody = new THREE.Mesh(crabBodyGeo, crabBodyMat);
  hermitCrabGroup.add(crabBody);

  // Shell: light brown sphere on top
  const shellGeo = new THREE.SphereGeometry(0.25, 6, 6);
  const shellMat = new THREE.MeshStandardMaterial({ color: 0xDEB887, roughness: 0.6 });
  const shell = new THREE.Mesh(shellGeo, shellMat);
  shell.position.y = 0.2;
  hermitCrabGroup.add(shell);

  // Two tiny eyes on stalks
  const eyeGeo = new THREE.SphereGeometry(0.05, 4, 4);
  const eyeMat = new THREE.MeshStandardMaterial({ color: 0x000000 });
  const stalkGeo = new THREE.CylinderGeometry(0.02, 0.02, 0.12, 4);
  const stalkMat = new THREE.MeshStandardMaterial({ color: 0x8B4513 });

  [-0.1, 0.1].forEach(x => {
    const stalk = new THREE.Mesh(stalkGeo, stalkMat);
    stalk.position.set(x, 0.2, 0.17);
    hermitCrabGroup.add(stalk);
    const eye = new THREE.Mesh(eyeGeo, eyeMat);
    eye.position.set(x, 0.3, 0.17);
    hermitCrabGroup.add(eye);
  });

  // Position 0.5-1.0 units away from seashell in random direction
  const hermitAngle = Math.random() * Math.PI * 2;
  const hermitDist = 0.5 + Math.random() * 0.5;
  hermitCrabGroup.position.set(
    seashell.position.x + Math.cos(hermitAngle) * hermitDist,
    seashell.position.y + 0.1,
    seashell.position.z + Math.sin(hermitAngle) * hermitDist
  );

  // Same visibility rules as seashell (camera distance)
  hermitCrabGroup.visible = false;
  scene.add(hermitCrabGroup);

  let hermitRetreatAnimating = false;

  function onHermitCrabClick() {
    eventBus.emit(GameEvents.OBJECT_CLICKED, { id: 'hermit_crab', type: 'object' });

    if (hermitRetreatAnimating) return;
    hermitRetreatAnimating = true;

    // Retreat: shrink to 0.3 over 0.3s
    const startScale = hermitCrabGroup.scale.x;
    const retreatStart = performance.now();

    function animateRetreat() {
      const elapsed = (performance.now() - retreatStart) / 1000;
      const t = Math.min(elapsed / 0.3, 1.0);
      const s = startScale + (0.3 - startScale) * t;
      hermitCrabGroup.scale.setScalar(s);
      if (t < 1.0) {
        requestAnimationFrame(animateRetreat);
      } else {
        // Peek out after 2 seconds
        setTimeout(() => {
          const peekStart = performance.now();
          function animatePeek() {
            const elapsed2 = (performance.now() - peekStart) / 1000;
            const t2 = Math.min(elapsed2 / 0.5, 1.0);
            const s2 = 0.3 + (1.0 - 0.3) * t2;
            hermitCrabGroup.scale.setScalar(s2);
            if (t2 < 1.0) {
              requestAnimationFrame(animatePeek);
            } else {
              hermitRetreatAnimating = false;
            }
          }
          animatePeek();
        }, 2000);
      }
    }
    animateRetreat();
  }

  registerInteractable(hermitCrabGroup, {
    type: 'object',
    id: 'hermit_crab',
    boundingRadius: 0.8,
    cooldown: 500,
    onClick: onHermitCrabClick,
  });
}

// --- pi-3rt.3: Tide Pool ---
const tidePoolGroup = new THREE.Group();

// Tide pool disc
const tidePoolGeometry = new THREE.CylinderGeometry(1.5, 1.5, 0.15, 16);
const tidePoolMaterial = new THREE.MeshStandardMaterial({
  color: 0x4682B4,
  transparent: true,
  opacity: 0.6,
  roughness: 0.2,
  metalness: 0.1,
});
const tidePool = new THREE.Mesh(tidePoolGeometry, tidePoolMaterial);
tidePoolGroup.add(tidePool);

// Starfish (pentagon = 5 arms)
const starfishGeometry = new THREE.CircleGeometry(0.3, 5);
const starfishMaterial = new THREE.MeshStandardMaterial({
  color: 0xff6347,
  side: THREE.DoubleSide,
  roughness: 0.7,
});
const starfish = new THREE.Mesh(starfishGeometry, starfishMaterial);
starfish.position.set(0.3, 0.09, 0.2);
starfish.rotation.x = -Math.PI / 2;
tidePoolGroup.add(starfish);

// Small fish (elongated sphere)
const fishGeometry = new THREE.SphereGeometry(0.15, 4, 4);
fishGeometry.scale(2.0, 1.0, 1.0);
const fishMaterial = new THREE.MeshStandardMaterial({
  color: 0x87ceeb,
  roughness: 0.3,
});
const tideFish = new THREE.Mesh(fishGeometry, fishMaterial);
tideFish.position.set(-0.3, 0.09, -0.3);
tidePoolGroup.add(tideFish);

// Position near water edge
tidePoolGroup.position.set(12, 1.9, -8);
scene.add(tidePoolGroup);

let starfishRotating = false;
let fishDarting = false;

function onTidePoolClick() {
  // Starfish rotation: 72 degrees (one arm) over 0.3s
  if (!starfishRotating) {
    starfishRotating = true;
    const startRot = starfish.rotation.z;
    const targetRot = startRot + (72 * Math.PI / 180);
    const startTime = performance.now();

    function animateStarfish() {
      const elapsed = (performance.now() - startTime) / 1000;
      const t = Math.min(elapsed / 0.3, 1.0);
      starfish.rotation.z = startRot + (targetRot - startRot) * t;
      if (t < 1.0) {
        requestAnimationFrame(animateStarfish);
      } else {
        starfishRotating = false;
      }
    }
    animateStarfish();
  }

  // Fish dart behavior
  if (!fishDarting) {
    fishDarting = true;
    const randomX = (Math.random() - 0.5) * 2.0;
    const randomZ = (Math.random() - 0.5) * 2.0;
    const startPos = tideFish.position.clone();
    const targetPos = new THREE.Vector3(randomX, 0.09, randomZ);
    const startTime = performance.now();

    function animateFishDart() {
      const elapsed = (performance.now() - startTime) / 1000;
      const t = Math.min(elapsed / 0.2, 1.0);
      tideFish.position.lerpVectors(startPos, targetPos, t);
      if (t < 1.0) {
        requestAnimationFrame(animateFishDart);
      } else {
        // Hold for 1s, then dart again
        setTimeout(() => {
          const secondX = (Math.random() - 0.5) * 2.0;
          const secondZ = (Math.random() - 0.5) * 2.0;
          const secondStart = tideFish.position.clone();
          const secondTarget = new THREE.Vector3(secondX, 0.09, secondZ);
          const secondStartTime = performance.now();

          function animateFishDart2() {
            const elapsed2 = (performance.now() - secondStartTime) / 1000;
            const t2 = Math.min(elapsed2 / 0.2, 1.0);
            tideFish.position.lerpVectors(secondStart, secondTarget, t2);
            if (t2 < 1.0) {
              requestAnimationFrame(animateFishDart2);
            } else {
              fishDarting = false;
            }
          }
          animateFishDart2();
        }, 1000);
      }
    }
    animateFishDart();
  }

  eventBus.emit(GameEvents.OBJECT_CLICKED, { id: 'tide_pool', type: 'object' });
}

registerInteractable(tidePoolGroup, {
  type: 'object',
  id: 'tide_pool',
  boundingRadius: 2.0,
  cooldown: 1000,
  onClick: onTidePoolClick,
});

// --- pi-3rt.4: Message in a Bottle ---
const messageBottleGroup = new THREE.Group();

// Bottle body (cylinder)
const bottleCylinderGeometry = new THREE.CylinderGeometry(0.1, 0.1, 0.5, 8);
const bottleGlassMaterial = new THREE.MeshPhysicalMaterial({
  color: 0x90EE90,
  transparent: true,
  opacity: 0.7,
  roughness: 0.1,
  metalness: 0.0,
  clearcoat: 0.5,
});
const bottleCylinder = new THREE.Mesh(bottleCylinderGeometry, bottleGlassMaterial);
messageBottleGroup.add(bottleCylinder);

// Hemisphere cap
const bottleCapGeometry = new THREE.SphereGeometry(0.1, 8, 8, 0, Math.PI * 2, 0, Math.PI / 2);
const bottleCap = new THREE.Mesh(bottleCapGeometry, bottleGlassMaterial);
bottleCap.position.y = 0.25;
messageBottleGroup.add(bottleCap);

// Cork
const corkGeometry = new THREE.CylinderGeometry(0.06, 0.08, 0.08, 8);
const corkMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513, roughness: 0.9 });
const cork = new THREE.Mesh(corkGeometry, corkMaterial);
cork.position.y = 0.3;
messageBottleGroup.add(cork);

// Tilt bottle on its side for floating
messageBottleGroup.rotation.z = Math.PI / 2;
messageBottleGroup.position.set(18, 0.1, 0);
scene.add(messageBottleGroup);

let messageBottleClickCount = 0;
let messageBottleAshore = false;
let messageBottleAnimating = false;
let messagePaper = null;

function onMessageBottleClick() {
  if (messageBottleAnimating) return;

  if (messageBottleClickCount === 0) {
    // First click: wash ashore over 3 seconds
    messageBottleClickCount = 1;
    messageBottleAnimating = true;

    const startPos = messageBottleGroup.position.clone();
    const shoreTarget = new THREE.Vector3(10, 2.0, 0);
    const startTime = performance.now();

    function animateWashAshore() {
      const elapsed = (performance.now() - startTime) / 1000;
      const t = Math.min(elapsed / 3.0, 1.0);
      // Smooth easing
      const eased = t * (2 - t);
      messageBottleGroup.position.lerpVectors(startPos, shoreTarget, eased);
      // Bobbing during wash
      messageBottleGroup.position.y = shoreTarget.y * eased + (1 - eased) * (0.1 + Math.sin(elapsed * 3) * 0.15);
      if (t < 1.0) {
        requestAnimationFrame(animateWashAshore);
      } else {
        messageBottleAshore = true;
        messageBottleAnimating = false;
        messageBottleGroup.position.copy(shoreTarget);
      }
    }
    animateWashAshore();

    eventBus.emit(GameEvents.OBJECT_CLICKED, { id: 'message_bottle_found', type: 'object' });

  } else if (messageBottleClickCount === 1 && messageBottleAshore) {
    // Second click: open the bottle and show paper
    messageBottleClickCount = 2;
    messageBottleAnimating = true;

    // Create paper mesh with canvas texture
    const paperCanvas = document.createElement('canvas');
    paperCanvas.width = 512;
    paperCanvas.height = 256;
    const ctx = paperCanvas.getContext('2d');

    // Parchment background
    ctx.fillStyle = '#f5e6c8';
    ctx.fillRect(0, 0, 512, 256);

    // Aged edges
    ctx.strokeStyle = '#c4a265';
    ctx.lineWidth = 4;
    ctx.strokeRect(10, 10, 492, 236);

    // Text
    ctx.fillStyle = '#5a3e1b';
    ctx.font = 'bold 28px Georgia';
    ctx.textAlign = 'center';
    ctx.fillText('HELP!', 256, 80);
    ctx.font = '22px Georgia';
    ctx.fillText('Just kidding, the wifi', 256, 130);
    ctx.fillText('is great here.', 256, 165);
    ctx.font = '16px Georgia';
    ctx.fillStyle = '#8a6e3b';
    ctx.fillText('- A happy castaway', 256, 220);

    const paperTexture = new THREE.CanvasTexture(paperCanvas);
    const paperGeometry = new THREE.PlaneGeometry(1.5, 0.75);
    const paperMaterial = new THREE.MeshStandardMaterial({
      map: paperTexture,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0,
    });
    messagePaper = new THREE.Mesh(paperGeometry, paperMaterial);
    messagePaper.position.copy(messageBottleGroup.position);
    messagePaper.position.y += 0.5;
    scene.add(messagePaper);

    // Animate paper unfurling upward and fading in
    const paperStartY = messagePaper.position.y;
    const paperTargetY = paperStartY + 1.5;
    const startTime = performance.now();

    function animateUnfurl() {
      const elapsed = (performance.now() - startTime) / 1000;
      const t = Math.min(elapsed / 1.0, 1.0);
      messagePaper.position.y = paperStartY + (paperTargetY - paperStartY) * t;
      messagePaper.material.opacity = t;
      // Slight scale-up for unfurling effect
      const scaleX = 0.2 + 0.8 * t;
      const scaleY = 0.2 + 0.8 * t;
      messagePaper.scale.set(scaleX, scaleY, 1);
      // Face the camera
      messagePaper.lookAt(camera.position);
      if (t < 1.0) {
        requestAnimationFrame(animateUnfurl);
      } else {
        messageBottleAnimating = false;
      }
    }
    animateUnfurl();

    eventBus.emit(GameEvents.OBJECT_CLICKED, { id: 'message_in_bottle', type: 'object' });
  }
}

registerInteractable(messageBottleGroup, {
  type: 'object',
  id: 'message_bottle',
  boundingRadius: 1.5,
  cooldown: 1000,
  onClick: onMessageBottleClick,
});

// ============================================================================
// GAME STATE & DISCOVERY SYSTEM
// ============================================================================

loadGameState();
initDiscovery();
initJournal();
initInventory();
initCraftingMenu();
initUpgradeMenu();
initUndoPlacement();
initCollectionAnimation(camera);
initSpeechBubble();
initSignCustomization();
initSounds();

// Update 3D sign text when customization panel saves
eventBus.on('sign:updated', ({ text }) => {
  const { boardCtx, boardTexture, drawSignText } = welcomeSign.userData;
  drawSignText(boardCtx, text);
  boardTexture.needsUpdate = true;
});

// Track any player click for idle detection (pi-d7w.2)
eventBus.on(GameEvents.OBJECT_CLICKED, () => {
  lastPlayerClickTime = performance.now() / 1000;
  idleSpeechBubbleShown = false;
});

// pi-d7w.7: Beach Bum reaction — thumbs up on new discoveries
eventBus.on(GameEvents.DISCOVERY_NEW, (data) => {
  if (!beachBumState.reactionActive) {
    beachBumState.reactionActive = true;
    beachBumState.reactionTimer = 0;
    beachBumState.reactionType = 'thumbsUp';
    beachBumState.reactionDuration = BEACH_BUM.THUMBS_UP_DURATION;
    // Show speech bubble
    const bumPos = new THREE.Vector3();
    beachBum.getWorldPosition(bumPos);
    bumPos.y += 3;
    const phrases = ['Nice find!', 'Awesome!'];
    showSpeechBubble(phrases[Math.floor(Math.random() * phrases.length)], bumPos, camera);
  }
});

// pi-d7w.7: Beach Bum reaction — applause on combination secrets
eventBus.on(GameEvents.OBJECT_CLICKED, (data) => {
  if (data && (data.id === 'crab_orchestra' || data.id === 'dolphin_serenade')) {
    beachBumState.reactionActive = true;
    beachBumState.reactionTimer = 0;
    beachBumState.reactionType = 'applause';
    beachBumState.reactionDuration = BEACH_BUM.APPLAUSE_DURATION;
    // Show speech bubble
    const bumPos = new THREE.Vector3();
    beachBum.getWorldPosition(bumPos);
    bumPos.y += 3;
    const phrases = ["Now THAT'S what I call music!", 'Bravo!'];
    showSpeechBubble(phrases[Math.floor(Math.random() * phrases.length)], bumPos, camera);
  }
});

// pi-d7w.8: Speech bubble triggers for player actions and discovery milestones
// Tracks which creature types have already triggered a "first discovery" bubble
const firstDiscoveryBubbleShown = new Set();
// Tracks whether the first combo secret bubble has been shown
let firstComboBubbleShown = false;
// Tracks milestone thresholds already celebrated
const milestoneBubblesShown = new Set();

eventBus.on(GameEvents.DISCOVERY_NEW, (data) => {
  // Guard: skip if another speech bubble animation is active
  if (beachBumState.reactionActive || beachBumState.gestureCueActive) return;

  const bumPos = new THREE.Vector3();
  beachBum.getWorldPosition(bumPos);
  bumPos.y += 3;
  const totalDiscovered = data.totalDiscovered;

  // --- 1. First click on a creature type ---
  if (data.category === 'creatures' && !firstDiscoveryBubbleShown.has(data.key)) {
    firstDiscoveryBubbleShown.add(data.key);
    const creaturePhrases = [
      `Hey, you found a ${data.name}!`,
      `A ${data.name}! Click it again!`,
      `Look at that ${data.name}!`,
    ];
    const phrase = creaturePhrases[Math.floor(Math.random() * creaturePhrases.length)];
    setTimeout(() => {
      if (!beachBumState.reactionActive && !beachBumState.gestureCueActive) {
        const pos = new THREE.Vector3();
        beachBum.getWorldPosition(pos);
        pos.y += 3;
        showSpeechBubble(phrase, pos, camera, DISCOVERY.CREATURE_BUBBLE_DURATION);
      }
    }, DISCOVERY.MILESTONE_BUBBLE_DELAY);
    return; // Don't also show a milestone bubble on the same event
  }

  // --- 2. First combination secret discovered ---
  if (data.category === 'combos' && !firstComboBubbleShown) {
    firstComboBubbleShown = true;
    setTimeout(() => {
      if (!beachBumState.reactionActive && !beachBumState.gestureCueActive) {
        const pos = new THREE.Vector3();
        beachBum.getWorldPosition(pos);
        pos.y += 3;
        showSpeechBubble("Whoa! Things are combining! What else can you try?", pos, camera, DISCOVERY.COMBO_BUBBLE_DURATION);
      }
    }, DISCOVERY.MILESTONE_BUBBLE_DELAY);
    return;
  }

  // --- 3. All secrets found (4 hidden secrets) ---
  const state = getGameState();
  const secretKeys = ['buried_seashell', 'tide_pool', 'message_in_bottle', 'hermit_crab'];
  const allSecretsFound = secretKeys.every(k => state.discoveries[k]);
  if (data.category === 'secrets' && allSecretsFound && !milestoneBubblesShown.has('all_secrets')) {
    milestoneBubblesShown.add('all_secrets');
    setTimeout(() => {
      if (!beachBumState.reactionActive && !beachBumState.gestureCueActive) {
        const pos = new THREE.Vector3();
        beachBum.getWorldPosition(pos);
        pos.y += 3;
        showSpeechBubble("You found all my hidden spots!", pos, camera, DISCOVERY.ALL_SECRETS_BUBBLE_DURATION);
      }
    }, DISCOVERY.MILESTONE_BUBBLE_DELAY);
    return;
  }

  // --- 4. Discovery count milestones ---
  const milestones = DISCOVERY.MILESTONES;
  for (const milestone of milestones) {
    if (totalDiscovered >= milestone.count && !milestoneBubblesShown.has(milestone.count)) {
      milestoneBubblesShown.add(milestone.count);
      setTimeout(() => {
        if (!beachBumState.reactionActive && !beachBumState.gestureCueActive) {
          const pos = new THREE.Vector3();
          beachBum.getWorldPosition(pos);
          pos.y += 3;
          showSpeechBubble(milestone.phrase, pos, camera, DISCOVERY.MILESTONE_BUBBLE_DURATION);
        }
      }, DISCOVERY.MILESTONE_BUBBLE_DELAY);
      break; // Only show one milestone bubble per event
    }
  }
});

// ============================================================================
// MESHY.AI MODEL LOADING
// ============================================================================

// Store references to loaded Meshy model templates
const meshyModelTemplates = {
  crab: null,
  dolphin: null,
  seagull: null,
  treasureChest: null,
  palmTree: null
};

/**
 * Load a 3D model from Meshy.ai or any GLB URL
 */
async function loadMeshyModel(url, options = {}) {
  try {
    const model = await loadModel(url, {
      scale: options.scale || 1,
      position: options.position || { x: 0, y: 0, z: 0 },
      rotation: options.rotation || { x: 0, y: 0, z: 0 },
      castShadow: true,
      receiveShadow: true
    });

    scene.add(model);
    meshyModels.push(model);
    console.log('Loaded Meshy model:', url);
    return model;
  } catch (error) {
    console.error('Failed to load Meshy model:', error);
    return null;
  }
}

// Create procedural sand island early for progressive rendering
// (visible in first frame while models are still loading)
const isExpanded = hasUpgrade('island_expansion_1');
const terrainRadius = isExpanded ? EXPANSION.EXPANDED_RADIUS : EXPANSION.BASE_RADIUS;
const terrainSize = terrainRadius * 2;

function getSandParams(radius, size) {
  return {
    seed: getGameState().spawners.terrainSeed,
    radius,
    size,
    heightScale: 5.0,
    baseHeight: 3.0,
    noiseScale: 0.05,
    octaves: 3,
    persistence: 0.5,
    lacunarity: 2.0,
    segments: 96,
    falloffStart: 0.4,
    falloffPower: 4.5,
  };
}

sandModel = createProceduralSand(getSandParams(terrainRadius, terrainSize));
scene.add(sandModel);
initResourceSpawner(scene, sandModel);
registerCraftableCallbacks(scene);
initPlacement(scene, camera, renderer, sandModel);
updateLoadingProgress('Sand island');

// ---------------------------------------------------------------------------
// Island Expansion — regenerate terrain at 1.2x radius when upgrade purchased
// ---------------------------------------------------------------------------
function expandIsland() {
  // Remove old terrain
  scene.remove(sandModel);
  if (sandModel.geometry) sandModel.geometry.dispose();

  // Create expanded terrain
  sandModel = createProceduralSand(getSandParams(EXPANSION.EXPANDED_RADIUS, EXPANSION.EXPANDED_SIZE));
  scene.add(sandModel);

  // Update references in resource spawner and placement system
  updateSpawnerSandMesh(sandModel);
  updatePlacementSandMesh(sandModel);

  // Spawn new palm trees in the expanded zone
  spawnExpansionTrees();

  // Update sea turtle orbit to match expanded terrain
  if (seaTurtle) {
    seaTurtle.orbitRadius = 30 * EXPANSION.MULTIPLIER;
    seaTurtle.crawlTargetRadius = 22 * EXPANSION.MULTIPLIER;
  }

  console.log('[Expansion] Island expanded to', EXPANSION.EXPANDED_RADIUS, 'radius');
}

// Spawn expansion palm trees (used both on purchase and on load if already owned)
function spawnExpansionTrees() {
  const angleStep = (Math.PI * 2) / EXPANSION.NEW_PALM_TREE_COUNT;
  const startAngle = Math.random() * angleStep;
  for (let i = 0; i < EXPANSION.NEW_PALM_TREE_COUNT; i++) {
    const angle = startAngle + angleStep * i + (Math.random() - 0.5) * 0.4;
    const r = EXPANSION.NEW_PALM_TREE_RING_RADIUS + (Math.random() - 0.5) * 6;
    const tree = createPalmTree(12 + Math.random() * 5);
    tree.position.set(Math.cos(angle) * r, 2, Math.sin(angle) * r);
    tree.rotation.y = Math.random() * Math.PI * 2;
    scene.add(tree);
    palmTrees.push(tree);

    // If Meshy palm tree model is loaded, replace fallback mesh
    if (meshyModelTemplates.palmTree) {
      const meshyTree = meshyModelTemplates.palmTree.clone();
      meshyTree.position.copy(tree.position);
      meshyTree.rotation.y = tree.rotation.y;
      const bbox = new THREE.Box3().setFromObject(meshyModelTemplates.palmTree);
      const modelH = bbox.max.y - bbox.min.y;
      const targetH = tree.userData.height || 14;
      meshyTree.scale.setScalar(targetH / modelH);
      scene.add(meshyTree);
      scene.remove(tree);
      tree.userData.meshyModel = meshyTree;
    }
  }
  setPalmTreePositions(palmTrees.map(t => {
    const m = t.userData?.meshyModel || t;
    return m.position.clone();
  }));
  // Register expansion trees as exclusion zones for resource spawning
  palmTrees.forEach(tree => addExclusionZone(tree.position, 3.0));
}

// If island was already expanded on load, spawn the extra trees + update turtle
if (isExpanded) {
  spawnExpansionTrees();
  if (seaTurtle) {
    seaTurtle.orbitRadius = 30 * EXPANSION.MULTIPLIER;
    seaTurtle.crawlTargetRadius = 22 * EXPANSION.MULTIPLIER;
  }
}

// Listen for expansion upgrade purchase
eventBus.on(GameEvents.UPGRADE_PURCHASED, ({ upgradeId }) => {
  if (upgradeId === 'island_expansion_1') {
    expandIsland();
  }
});

async function initMeshyModels() {
  console.log('Loading Meshy.ai models...');

  // Load all 5 GLB models in parallel for faster startup
  const [crabResult, dolphinResult, seagullResult, chestResult, palmTreeResult] =
    await Promise.allSettled([
      loadModel('/models/crab.glb', { scale: 1.6 }),
      loadModel('/models/dolphin.glb', { scale: 1.5 }),
      loadModel('/models/seagull.glb', { scale: 1.0 }),
      loadModel('/models/treasure-chest.glb', {
        scale: 4.8,
        position: { x: 28, y: 0, z: 20 }
      }),
      loadModel('/models/palm-tree.glb', { scale: 1.0 })
    ]);

  // Process crab model
  if (crabResult.status === 'fulfilled') {
    const crabModel = crabResult.value;
    meshyModelTemplates.crab = crabModel;
    crabs.forEach((crab, i) => {
      const meshyCrab = crabModel.clone();
      meshyCrab.position.copy(crab.mesh.position);
      meshyCrab.scale.setScalar(1.6);
      scene.add(meshyCrab);
      scene.remove(crab.mesh);
      crab.meshyModel = meshyCrab;
      updateInteractableMesh(`crab_${i}`, meshyCrab);
    });
    updateLoadingProgress('Crabs');
  } else {
    updateLoadingProgress('Crabs (fallback)');
    console.warn('Could not load crab model:', crabResult.reason?.message);
  }

  // Process dolphin model
  if (dolphinResult.status === 'fulfilled') {
    const dolphinModel = dolphinResult.value;
    meshyModelTemplates.dolphin = dolphinModel;
    dolphins.forEach((dolphin, i) => {
      const meshyDolphin = dolphinModel.clone();
      meshyDolphin.position.copy(dolphin.mesh.position);
      meshyDolphin.scale.setScalar(1.5);
      scene.add(meshyDolphin);
      scene.remove(dolphin.mesh);
      dolphin.meshyModel = meshyDolphin;
      updateInteractableMesh(`dolphin_${i}`, meshyDolphin);
    });
    updateLoadingProgress('Dolphins');
  } else {
    updateLoadingProgress('Dolphins (fallback)');
    console.warn('Could not load dolphin model:', dolphinResult.reason?.message);
  }

  // Process seagull model
  if (seagullResult.status === 'fulfilled') {
    const seagullModel = seagullResult.value;
    meshyModelTemplates.seagull = seagullModel;
    seagulls.forEach((seagull, i) => {
      const meshySeagull = seagullModel.clone();
      meshySeagull.position.copy(seagull.mesh.position);
      meshySeagull.scale.setScalar(1.0);
      scene.add(meshySeagull);
      scene.remove(seagull.mesh);
      seagull.meshyModel = meshySeagull;
      updateInteractableMesh(`seagull_${i}`, meshySeagull);
      // Re-apply golden gull visuals to loaded model if needed
      if (seagull.isGolden) {
        meshySeagull.scale.multiplyScalar(CREATURE.SEAGULL_GOLDEN_SCALE);
        meshySeagull.traverse((child) => {
          if (child.isMesh && child.material) {
            child.material = child.material.clone();
            child.material.color.setHex(0xffd700);
          }
        });
      }
    });
    updateLoadingProgress('Seagulls');
  } else {
    updateLoadingProgress('Seagulls (fallback)');
    console.warn('Could not load seagull model:', seagullResult.reason?.message);
  }

  // Process treasure chest model
  if (chestResult.status === 'fulfilled') {
    const chestModel = chestResult.value;
    meshyModelTemplates.treasureChest = chestModel;
    scene.remove(treasureChest);
    chestModel.rotation.y = Math.PI / 4;

    // Raycast to find sand height at chest position and place on surface
    const chestRayOrigin = new THREE.Vector3(chestModel.position.x, 50, chestModel.position.z);
    groundRaycaster.set(chestRayOrigin, downVector);
    if (sandModel) {
      const chestHits = groundRaycaster.intersectObject(sandModel, true);
      if (chestHits.length > 0) {
        // Place chest base on sand surface (compute bounding box bottom)
        const chestBBox = new THREE.Box3().setFromObject(chestModel);
        const chestBottomOffset = chestBBox.min.y - chestModel.position.y;
        chestModel.position.y = chestHits[0].point.y - chestBottomOffset + 0.05;
      }
    }

    scene.add(chestModel);
    updateLoadingProgress('Treasure chest');
  } else {
    updateLoadingProgress('Treasure chest (fallback)');
    console.warn('Could not load treasure chest model:', chestResult.reason?.message);
  }

  // Position beach bum on sand using raycasting
  const beachBumRayOrigin = new THREE.Vector3(beachBum.position.x, 50, beachBum.position.z);
  groundRaycaster.set(beachBumRayOrigin, downVector);

  let beachBumSandY = 2.5;  // Default fallback
  if (sandModel) {
    const intersects = groundRaycaster.intersectObject(sandModel, true);
    if (intersects.length > 0) {
      beachBumSandY = intersects[0].point.y;
    }
  }

  // Position beach bum so he sits on the sand surface
  beachBum.position.y = beachBumSandY + 1.0;  // Offset for sitting height

  // Process palm tree model
  if (palmTreeResult.status === 'fulfilled') {
    const palmTreeModel = palmTreeResult.value;
    meshyModelTemplates.palmTree = palmTreeModel;

    // Compute the model's bounding box to find its base
    const bbox = new THREE.Box3().setFromObject(palmTreeModel);
    const modelBottomY = bbox.min.y;  // How far below origin the model extends

    // Replace procedural palm trees with Meshy ones
    palmTrees.forEach((tree, i) => {
      const meshyTree = palmTreeModel.clone();
      const treeScale = 3.0 + Math.random() * 1.5;  // Smaller trees
      meshyTree.scale.setScalar(treeScale);

      // Use raycasting to find sand surface height
      const rayOrigin = new THREE.Vector3(tree.position.x, 50, tree.position.z);
      groundRaycaster.set(rayOrigin, downVector);

      let sandY = 2.0;  // Default fallback
      if (sandModel) {
        const intersects = groundRaycaster.intersectObject(sandModel, true);
        if (intersects.length > 0) {
          sandY = intersects[0].point.y;
        }
      }

      // Position so the bottom of the scaled model sits on sand surface
      const yPosition = sandY - (modelBottomY * treeScale) + 0.5;
      meshyTree.position.set(tree.position.x, yPosition, tree.position.z);
      meshyTree.rotation.y = Math.random() * Math.PI * 2;
      scene.add(meshyTree);

      scene.remove(tree);
    });
    updateLoadingProgress('Palm trees');
  } else {
    updateLoadingProgress('Palm trees (fallback)');
    console.warn('Could not load palm tree model:', palmTreeResult.reason?.message);
  }

  // Final loading step
  updateLoadingProgress('Environment');

  console.log('Meshy models initialized!');

  // Brief delay to ensure final status text renders before fade-out
  await new Promise(resolve => setTimeout(resolve, 50));

  // Signal that scene is fully loaded - fade in
  const loadingScreen = document.getElementById('loading');
  const canvas = document.querySelector('canvas');
  if (loadingScreen && canvas) {
    loadingScreen.classList.add('hidden');
    canvas.classList.add('loaded');
  }
}

// Initialize Meshy models after scene is ready
requestAnimationFrame(() => initMeshyModels());

// Make loadMeshyModel available globally for console testing
window.loadMeshyModel = loadMeshyModel;

// ============================================================================
// GPU-OPTIMIZED PARTICLE SYSTEMS (WebGL 2.0 InstancedMesh)
// ============================================================================

// Water splash particle pool using InstancedMesh for efficient GPU rendering
const MAX_SPLASH_PARTICLES = PARTICLES.MAX_SPLASH_PARTICLES;
const splashGeometry = new THREE.SphereGeometry(0.15, 4, 4);
const splashMaterial = new THREE.MeshPhysicalMaterial({
  color: 0xaaddff,
  transparent: true,
  opacity: 0.7,
  roughness: 0.1,
  metalness: 0.0,
  transmission: 0.6,
  thickness: 0.1
});

// Create instanced mesh for water splashes (single draw call for all particles!)
const splashInstancedMesh = new THREE.InstancedMesh(
  splashGeometry,
  splashMaterial,
  MAX_SPLASH_PARTICLES
);
splashInstancedMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
splashInstancedMesh.count = 0; // Start with no visible particles
scene.add(splashInstancedMesh);

// Particle data stored in typed arrays for GPU efficiency
const splashParticleData = {
  positions: new Float32Array(MAX_SPLASH_PARTICLES * 3),
  velocities: new Float32Array(MAX_SPLASH_PARTICLES * 3),
  scales: new Float32Array(MAX_SPLASH_PARTICLES),
  lives: new Float32Array(MAX_SPLASH_PARTICLES),
  maxLives: new Float32Array(MAX_SPLASH_PARTICLES),
  active: new Uint8Array(MAX_SPLASH_PARTICLES),
  count: 0
};

const splashMatrix = new THREE.Matrix4();
const splashPosition = new THREE.Vector3();
const splashScale = new THREE.Vector3();
const splashQuaternion = new THREE.Quaternion();

function createWaterSplash(position, direction) {
  const particleCount = 8 + Math.floor(Math.random() * 6);

  for (let i = 0; i < particleCount; i++) {
    // Find an inactive slot or use next available
    let idx = -1;
    for (let j = 0; j < MAX_SPLASH_PARTICLES; j++) {
      if (!splashParticleData.active[j]) {
        idx = j;
        break;
      }
    }
    if (idx === -1) continue; // Pool full

    // Initialize particle
    const px = position.x;
    const py = position.y;
    const pz = position.z;
    splashParticleData.positions[idx * 3] = px;
    splashParticleData.positions[idx * 3 + 1] = py;
    splashParticleData.positions[idx * 3 + 2] = pz;

    splashParticleData.velocities[idx * 3] = (Math.random() - 0.5) * 4 + direction.x * 2;
    splashParticleData.velocities[idx * 3 + 1] = 3 + Math.random() * 4;
    splashParticleData.velocities[idx * 3 + 2] = (Math.random() - 0.5) * 4 + direction.z * 2;

    splashParticleData.scales[idx] = 0.5 + Math.random() * 0.5;
    splashParticleData.lives[idx] = 0;
    splashParticleData.maxLives[idx] = 0.8 + Math.random() * 0.4;
    splashParticleData.active[idx] = 1;

    if (idx >= splashParticleData.count) {
      splashParticleData.count = idx + 1;
    }
  }
}

// Sand particle pool using InstancedMesh
const MAX_SAND_PARTICLES = PARTICLES.MAX_SAND_PARTICLES;
const sandGrainGeometry = new THREE.SphereGeometry(0.05, 3, 3);
const sandGrainMaterial = new THREE.MeshStandardMaterial({
  color: 0xf4d03f,
  roughness: 1.0,
  transparent: true,
  opacity: 1.0
});

const sandInstancedMesh = new THREE.InstancedMesh(
  sandGrainGeometry,
  sandGrainMaterial,
  MAX_SAND_PARTICLES
);
sandInstancedMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
sandInstancedMesh.count = 0;
scene.add(sandInstancedMesh);

const sandParticleData = {
  positions: new Float32Array(MAX_SAND_PARTICLES * 3),
  velocities: new Float32Array(MAX_SAND_PARTICLES * 3),
  scales: new Float32Array(MAX_SAND_PARTICLES),
  lives: new Float32Array(MAX_SAND_PARTICLES),
  maxLives: new Float32Array(MAX_SAND_PARTICLES),
  active: new Uint8Array(MAX_SAND_PARTICLES),
  count: 0
};

const sandMatrix = new THREE.Matrix4();
const sandPosition = new THREE.Vector3();
const sandScale = new THREE.Vector3();

function createSandPuff(position) {
  const particleCount = 4 + Math.floor(Math.random() * 3);

  for (let i = 0; i < particleCount; i++) {
    let idx = -1;
    for (let j = 0; j < MAX_SAND_PARTICLES; j++) {
      if (!sandParticleData.active[j]) {
        idx = j;
        break;
      }
    }
    if (idx === -1) continue;

    sandParticleData.positions[idx * 3] = position.x;
    sandParticleData.positions[idx * 3 + 1] = position.y + 0.1;
    sandParticleData.positions[idx * 3 + 2] = position.z;

    sandParticleData.velocities[idx * 3] = (Math.random() - 0.5) * 1.5;
    sandParticleData.velocities[idx * 3 + 1] = 0.5 + Math.random() * 1;
    sandParticleData.velocities[idx * 3 + 2] = (Math.random() - 0.5) * 1.5;

    sandParticleData.scales[idx] = 0.3 + Math.random() * 0.4;
    sandParticleData.lives[idx] = 0;
    sandParticleData.maxLives[idx] = 0.5 + Math.random() * 0.3;
    sandParticleData.active[idx] = 1;

    if (idx >= sandParticleData.count) {
      sandParticleData.count = idx + 1;
    }
  }
}

// Sparkle particle pool using InstancedMesh (for discoveries and treasure)
const MAX_SPARKLE_PARTICLES = PARTICLES.MAX_SPARKLE_PARTICLES;
const sparkleGeometry = new THREE.OctahedronGeometry(0.08, 0);
const sparkleMaterial = new THREE.MeshStandardMaterial({
  color: 0xffd700,
  emissive: 0xffa500,
  emissiveIntensity: 0.8,
  transparent: true,
  opacity: 1.0,
  roughness: 0.2,
  metalness: 0.8
});

const sparkleInstancedMesh = new THREE.InstancedMesh(
  sparkleGeometry,
  sparkleMaterial,
  MAX_SPARKLE_PARTICLES
);
sparkleInstancedMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
sparkleInstancedMesh.count = 0;
scene.add(sparkleInstancedMesh);

const sparkleParticleData = {
  positions: new Float32Array(MAX_SPARKLE_PARTICLES * 3),
  velocities: new Float32Array(MAX_SPARKLE_PARTICLES * 3),
  scales: new Float32Array(MAX_SPARKLE_PARTICLES),
  lives: new Float32Array(MAX_SPARKLE_PARTICLES),
  maxLives: new Float32Array(MAX_SPARKLE_PARTICLES),
  active: new Uint8Array(MAX_SPARKLE_PARTICLES),
  count: 0
};

const sparkleMatrix = new THREE.Matrix4();
const sparklePosition = new THREE.Vector3();
const sparkleScale = new THREE.Vector3();

function emitSparkle(position, count) {
  const particleCount = count || (8 + Math.floor(Math.random() * 5));

  for (let i = 0; i < particleCount; i++) {
    let idx = -1;
    for (let j = 0; j < MAX_SPARKLE_PARTICLES; j++) {
      if (!sparkleParticleData.active[j]) {
        idx = j;
        break;
      }
    }
    if (idx === -1) continue; // Pool full

    sparkleParticleData.positions[idx * 3] = position.x + (Math.random() - 0.5) * 0.5;
    sparkleParticleData.positions[idx * 3 + 1] = position.y + (Math.random() - 0.5) * 0.5;
    sparkleParticleData.positions[idx * 3 + 2] = position.z + (Math.random() - 0.5) * 0.5;

    sparkleParticleData.velocities[idx * 3] = (Math.random() - 0.5) * 1.5;
    sparkleParticleData.velocities[idx * 3 + 1] = 1 + Math.random() * 2;
    sparkleParticleData.velocities[idx * 3 + 2] = (Math.random() - 0.5) * 1.5;

    sparkleParticleData.scales[idx] = 0.5 + Math.random() * 0.5;
    sparkleParticleData.lives[idx] = 0;
    sparkleParticleData.maxLives[idx] = 0.5 + Math.random() * 0.5;
    sparkleParticleData.active[idx] = 1;

    if (idx >= sparkleParticleData.count) {
      sparkleParticleData.count = idx + 1;
    }
  }
}

// Sparkle event listeners
eventBus.on(GameEvents.OBJECT_CLICKED, (data) => {
  if (data && data.id && data.id.startsWith('treasure_chest')) {
    const chestPos = new THREE.Vector3();
    treasureChest.getWorldPosition(chestPos);
    chestPos.y += 2;
    emitSparkle(chestPos, PARTICLES.TREASURE_SPARKLE_COUNT);
  }
});

// pi-3pk.5: Tier-differentiated sparkle feedback on discoveries
eventBus.on(GameEvents.DISCOVERY_NEW, (data) => {
  const isHighTier = data && (data.category === 'secrets' || data.category === 'combos');
  const sparkleCount = isHighTier
    ? PARTICLES.SECRET_SPARKLE_COUNT.min + Math.floor(Math.random() * (PARTICLES.SECRET_SPARKLE_COUNT.max - PARTICLES.SECRET_SPARKLE_COUNT.min + 1))
    : PARTICLES.STANDARD_SPARKLE_COUNT;
  emitSparkle(controls.target.clone(), sparkleCount);
});

// pi-3rt.5: Gold sparkle burst for secret discoveries
eventBus.on(GameEvents.OBJECT_CLICKED, (data) => {
  if (data && data.id && (data.id === 'buried_seashell' || data.id === 'tide_pool' || data.id === 'message_in_bottle')) {
    // Use a gold sparkle burst — emit more sparkles at camera target
    emitSparkle(controls.target.clone(), PARTICLES.SECRET_OBJECT_SPARKLE_COUNT);
  }
});

// pi-3ao.7: Shimmer particle effect for newly spawned resources
// Emits subtle sparkle particles over a duration, reusing the existing sparkle pool.
function emitShimmerSparkle(position, count) {
  const particleCount = count || 2;
  for (let i = 0; i < particleCount; i++) {
    let idx = -1;
    for (let j = 0; j < MAX_SPARKLE_PARTICLES; j++) {
      if (!sparkleParticleData.active[j]) {
        idx = j;
        break;
      }
    }
    if (idx === -1) continue; // Pool full

    // Small random offset around the resource position (±0.3 units)
    sparkleParticleData.positions[idx * 3] = position.x + (Math.random() - 0.5) * 0.6;
    sparkleParticleData.positions[idx * 3 + 1] = position.y + Math.random() * 0.3;
    sparkleParticleData.positions[idx * 3 + 2] = position.z + (Math.random() - 0.5) * 0.6;

    // Gentle upward drift with very slight horizontal movement
    sparkleParticleData.velocities[idx * 3] = (Math.random() - 0.5) * 0.4;
    sparkleParticleData.velocities[idx * 3 + 1] = 0.3 + Math.random() * 0.5;
    sparkleParticleData.velocities[idx * 3 + 2] = (Math.random() - 0.5) * 0.4;

    // Small scale for subtlety, short lifespan so only 2-3 visible at a time
    sparkleParticleData.scales[idx] = 0.2 + Math.random() * 0.2;
    sparkleParticleData.lives[idx] = 0;
    sparkleParticleData.maxLives[idx] = 0.4 + Math.random() * 0.3;
    sparkleParticleData.active[idx] = 1;

    if (idx >= sparkleParticleData.count) {
      sparkleParticleData.count = idx + 1;
    }
  }
}

function startShimmerEffect(position, duration) {
  const shimmerDuration = duration || 3000;
  const emitInterval = 250; // Emit every 250ms
  const startTime = performance.now();

  const intervalId = setInterval(() => {
    const elapsed = performance.now() - startTime;
    if (elapsed >= shimmerDuration) {
      clearInterval(intervalId);
      return;
    }
    // Emit 1-2 small shimmer particles per tick
    const count = 1 + Math.floor(Math.random() * 2);
    emitShimmerSparkle(position, count);
  }, emitInterval);
}

// pi-3ao.7: Listen for RESOURCE_SPAWNED to trigger shimmer effect
eventBus.on(GameEvents.RESOURCE_SPAWNED, (data) => {
  if (data.position) {
    startShimmerEffect(data.position, 3000);
  }
});

// Musical burst particle pool using InstancedMesh (for beach bum interactions)
const MAX_MUSIC_PARTICLES = PARTICLES.MAX_MUSIC_PARTICLES;
const musicGeometry = new THREE.CircleGeometry(0.12, 6);
const musicMaterial = new THREE.MeshStandardMaterial({
  color: 0xff7f50,
  emissive: 0xffd700,
  emissiveIntensity: 0.6,
  transparent: true,
  opacity: 1.0,
  side: THREE.DoubleSide,
  roughness: 0.3,
  metalness: 0.5
});

const musicInstancedMesh = new THREE.InstancedMesh(
  musicGeometry,
  musicMaterial,
  MAX_MUSIC_PARTICLES
);
musicInstancedMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
musicInstancedMesh.count = 0;
scene.add(musicInstancedMesh);

const musicParticleData = {
  positions: new Float32Array(MAX_MUSIC_PARTICLES * 3),
  velocities: new Float32Array(MAX_MUSIC_PARTICLES * 3),
  scales: new Float32Array(MAX_MUSIC_PARTICLES),
  lives: new Float32Array(MAX_MUSIC_PARTICLES),
  maxLives: new Float32Array(MAX_MUSIC_PARTICLES),
  active: new Uint8Array(MAX_MUSIC_PARTICLES),
  spiralAngles: new Float32Array(MAX_MUSIC_PARTICLES),
  count: 0
};

const musicMatrix = new THREE.Matrix4();
const musicPosition = new THREE.Vector3();
const musicScale = new THREE.Vector3();

function emitMusicalBurst(position, count) {
  const particleCount = count || 5;

  for (let i = 0; i < particleCount; i++) {
    let idx = -1;
    for (let j = 0; j < MAX_MUSIC_PARTICLES; j++) {
      if (!musicParticleData.active[j]) {
        idx = j;
        break;
      }
    }
    if (idx === -1) continue; // Pool full

    musicParticleData.positions[idx * 3] = position.x + (Math.random() - 0.5) * 0.5;
    musicParticleData.positions[idx * 3 + 1] = position.y + (Math.random() - 0.5) * 0.5;
    musicParticleData.positions[idx * 3 + 2] = position.z + (Math.random() - 0.5) * 0.5;

    musicParticleData.velocities[idx * 3] = 0;
    musicParticleData.velocities[idx * 3 + 1] = 2 + Math.random() * 1.5;
    musicParticleData.velocities[idx * 3 + 2] = 0;

    musicParticleData.spiralAngles[idx] = (i / particleCount) * Math.PI * 2;
    musicParticleData.scales[idx] = 0.4 + Math.random() * 0.4;
    musicParticleData.lives[idx] = 0;
    musicParticleData.maxLives[idx] = 0.5 + Math.random() * 0.5;
    musicParticleData.active[idx] = 1;

    if (idx >= musicParticleData.count) {
      musicParticleData.count = idx + 1;
    }
  }
}

// Musical burst on beach bum click
eventBus.on(GameEvents.OBJECT_CLICKED, (data) => {
  if (data && data.id === 'beach_bum') {
    const bumPos = new THREE.Vector3();
    beachBum.getWorldPosition(bumPos);
    bumPos.y += 3;
    emitMusicalBurst(bumPos, 5);
  }
});

// ============================================================================
// BUTTERFLY PARTICLE SYSTEM (pi-t0j.10)
// ============================================================================

// Persistent butterflies that orbit flower gardens
const activeButterflies = [];

// Spawn 2-3 butterflies around a newly placed flower garden
function spawnButterflies(gardenPosition, gardenId) {
  const colors = [0xFF69B4, 0xFFFF00, 0xFF8C00]; // pink, yellow, orange
  const count = 2 + Math.floor(Math.random() * 2); // 2 or 3

  for (let i = 0; i < count; i++) {
    const geo = new THREE.PlaneGeometry(0.15, 0.1);
    const mat = new THREE.MeshBasicMaterial({
      color: colors[i % colors.length],
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.8,
    });
    const butterfly = new THREE.Mesh(geo, mat);

    const cx = gardenPosition.x;
    const cy = gardenPosition.y;
    const cz = gardenPosition.z;

    butterfly.position.set(cx, cy + 0.5 + Math.random() * 0.5, cz);
    scene.add(butterfly);

    activeButterflies.push({
      mesh: butterfly,
      gardenId: gardenId,
      center: new THREE.Vector3(cx, cy, cz),
      angle: Math.random() * Math.PI * 2,
      radius: 0.5 + Math.random() * 0.8,
      speed: 0.3 + Math.random() * 0.4,
      heightOffset: 0.5 + Math.random() * 0.5,
      bobPhase: Math.random() * Math.PI * 2,
    });
  }
}

// Remove all butterflies associated with a garden
function removeButterflies(gardenId) {
  for (let i = activeButterflies.length - 1; i >= 0; i--) {
    if (activeButterflies[i].gardenId === gardenId) {
      const b = activeButterflies[i];
      scene.remove(b.mesh);
      b.mesh.geometry.dispose();
      b.mesh.material.dispose();
      activeButterflies.splice(i, 1);
    }
  }
}

// Update butterfly orbits, bobbing, and wing flutter each frame
function updateButterflies(time) {
  for (let i = 0; i < activeButterflies.length; i++) {
    const b = activeButterflies[i];
    b.angle += b.speed * 0.016; // ~60fps step
    b.mesh.position.x = b.center.x + Math.cos(b.angle) * b.radius;
    b.mesh.position.z = b.center.z + Math.sin(b.angle) * b.radius;
    b.mesh.position.y = b.center.y + b.heightOffset + Math.sin(time * 2 + b.bobPhase) * 0.15;

    // Billboard: face camera (throttled to every 10 frames)
    if (frameCount % 10 === 0) {
      b.mesh.lookAt(camera.position);
    }

    // Wing flutter: oscillate scaleX for a flapping effect
    b.mesh.scale.x = 0.8 + Math.sin(time * 15 + b.bobPhase) * 0.4;
  }
}

// Spawn butterflies when a flower garden is placed
eventBus.on(GameEvents.OBJECT_PLACED, (data) => {
  if (data.recipeId === 'flower_garden') {
    spawnButterflies(data.position, data.id);
  }
});

// Add exclusion zone for placed objects so resources don't spawn on top of them
eventBus.on(GameEvents.OBJECT_PLACED, (data) => {
  if (data.position) {
    addExclusionZone(new THREE.Vector3(data.position.x, data.position.y, data.position.z), 2.0);
  }
});

// Clean up butterflies when a flower garden is removed
eventBus.on(GameEvents.OBJECT_REMOVED, (data) => {
  if (data.recipeId === 'flower_garden') {
    removeButterflies(data.id);
  }
});

// ============================================================================
// BEACH BUM HUM PROXIMITY HINTS (pi-d7w.4)
// ============================================================================

let humFrameCounter = 0;
let lastHumTime = 0;
const HUM_PROXIMITY_RADIUS = INTERACTION.HUM_PROXIMITY_RADIUS;
const HUM_COOLDOWN_MS = INTERACTION.HUM_COOLDOWN_MS;
const HUM_CHECK_INTERVAL = INTERACTION.HUM_CHECK_INTERVAL;
const HUM_MAX_VOLUME = INTERACTION.HUM_VOLUME_MAX;

function checkHumProximity() {
  const now = performance.now();
  if (now - lastHumTime < HUM_COOLDOWN_MS) return;

  const undiscovered = getDiscoverablePositions();
  if (undiscovered.length === 0) return;

  // Find the closest undiscovered item
  let closestDist = Infinity;
  for (let i = 0; i < undiscovered.length; i++) {
    const dist = camera.position.distanceTo(undiscovered[i].position);
    if (dist < closestDist) closestDist = dist;
  }

  if (closestDist <= HUM_PROXIMITY_RADIUS) {
    // Scale volume: closer = louder (linear interpolation)
    // At distance 0 -> HUM_MAX_VOLUME, at HUM_PROXIMITY_RADIUS -> near-silent
    const proximity = 1 - (closestDist / HUM_PROXIMITY_RADIUS);
    const volume = INTERACTION.HUM_VOLUME_MIN + proximity * (HUM_MAX_VOLUME - INTERACTION.HUM_VOLUME_MIN);
    playHum(volume);
    lastHumTime = now;
  }
}

// ============================================================================
// ANIMATION LOOP
// ============================================================================

const clock = new THREE.Clock();

// Perf: module-level temp vectors to avoid per-frame allocations in formation flight
const _tv1 = new THREE.Vector3();
const _tv2 = new THREE.Vector3();
const _tv3 = new THREE.Vector3();
const _tv4 = new THREE.Vector3();
const _tv5 = new THREE.Vector3();

// Perf: cached emissive color for beer bottle pulse (avoid per-frame allocation)
const _bottleEmissiveColor = new THREE.Color(0x1a4d1a);

let frameCount = 0;

function animate() {
  requestAnimationFrame(animate);
  frameCount++;

  const deltaTime = clock.getDelta();
  const time = clock.getElapsedTime();

  // Update listener position for spatial audio (pi-3pk.3)
  updateListenerPosition(camera.position);

  // Update water
  water.material.uniforms['time'].value += deltaTime * 0.5;

  // Update craftable animations (wind chimes)
  updateWindChimes(time);

  // Update sand castle erosion (pi-t0j.3)
  updateSandCastleErosion();

  // Update crabs
  crabs.forEach(crab => crab.update(deltaTime));

  // Update dolphins
  dolphins.forEach(dolphin => dolphin.update(time));

  // Update sea turtle (pi-wfm.7)
  if (seaTurtle) {
    seaTurtle.update(deltaTime, time);
  }

  // Update seagulls
  if (formationFlightActive) {
    // --- Formation Flight override (pi-nzq.6) ---
    formationFlightTimer += deltaTime;
    const formationDuration = CREATURE.FORMATION_FLIGHT_DURATION;

    if (formationFlightTimer >= formationDuration) {
      // Release back to normal boid flocking
      formationFlightActive = false;
      formationFlightTimer = 0;
      // Reset velocities so they resume flocking naturally
      seagulls.forEach((s) => {
        s.velocity.set(
          (Math.random() - 0.5) * 4,
          (Math.random() - 0.5) * 0.5,
          (Math.random() - 0.5) * 4
        );
      });
    } else {
      // V-formation positions: leader in center, wings at 45-degree angles
      const leaderIndex = Math.floor(seagulls.length / 2);
      const formationSpeed = CREATURE.FORMATION_FLIGHT_SPEED;
      const spacing = CREATURE.FORMATION_FLIGHT_SPACING;
      const formationAltitude = CREATURE.FORMATION_FLIGHT_ALTITUDE;
      const progress = formationFlightTimer / formationDuration;

      // Calculate formation center — moves across the island (reuse temp vectors)
      const startCenter = _tv1.set(
        -formationFlightDirection.x * 60,
        formationAltitude,
        -formationFlightDirection.z * 60
      );
      const endCenter = _tv2.set(
        formationFlightDirection.x * 60,
        formationAltitude,
        formationFlightDirection.z * 60
      );
      const currentCenter = _tv3.lerpVectors(startCenter, endCenter, progress);

      // Right vector perpendicular to flight direction
      const rightVec = _tv4.set(-formationFlightDirection.z, 0, formationFlightDirection.x);

      seagulls.forEach((s, idx) => {
        const activeMesh = s.meshyModel || s.mesh;
        // Calculate V-formation offset relative to leader
        const offset = idx - leaderIndex;
        const absOffset = Math.abs(offset);
        const side = offset === 0 ? 0 : (offset > 0 ? 1 : -1);

        // V-shape: each bird behind and to the side of the one in front
        const lateralOffset = side * absOffset * spacing;
        const backOffset = -absOffset * spacing * 0.7; // 45-degree angle => back distance

        const targetPos = _tv5.copy(currentCenter)
          .addScaledVector(rightVec, lateralOffset)
          .addScaledVector(formationFlightDirection, backOffset);

        // Smoothly interpolate to formation position (fast lerp at start)
        const lerpFactor = Math.min(1.0, progress * 5); // snap into formation quickly
        if (lerpFactor < 1.0 && formationFlightStartPositions[idx]) {
          activeMesh.position.lerpVectors(formationFlightStartPositions[idx], targetPos, lerpFactor);
        } else {
          activeMesh.position.copy(targetPos);
        }

        // Face direction of flight
        activeMesh.rotation.y = Math.atan2(formationFlightDirection.x, formationFlightDirection.z);
        activeMesh.rotation.x = 0;

        // Faster wing flap during formation
        if (!s.meshyModel) {
          s.wingPhase += deltaTime * 10;
          s.wings.forEach(({ mesh, side: wingSide }) => {
            mesh.rotation.x = wingSide * (0.2 + Math.sin(s.wingPhase) * 0.35);
          });
        }
      });
    }
  } else {
    if (frameCount % 3 === 0) {
      seagulls.forEach(seagull => seagull.update(deltaTime * 3, seagulls));
    }
  }

  // Update clouds: orbital drift + billboard facing camera
  clouds.forEach(cloud => {
    cloud.userData.baseAngle += deltaTime * cloud.userData.speed * 0.002;
    const r = cloud.userData.radius;
    cloud.position.x = Math.cos(cloud.userData.baseAngle) * r;
    cloud.position.z = Math.sin(cloud.userData.baseAngle) * r;
    if (frameCount % 30 === 0) {
      cloud.lookAt(camera.position);
    }
  });

  // Realistic palm tree sway with multiple wind frequencies + shake interaction
  palmTrees.forEach((tree, i) => {
    const state = palmTreeState[i];
    // Primary wind wave (slow, large movement)
    const primaryWave = Math.sin(time * 0.4 + i * 0.7) * 0.025;
    // Secondary wave (faster, smaller)
    const secondaryWave = Math.sin(time * 1.2 + i * 1.3) * 0.01;
    // Micro-movements (fast, very small rustling)
    const microWave = Math.sin(time * 3.5 + i * 2.1) * 0.003;

    let windZ = primaryWave + secondaryWave + microWave;
    let windX = Math.sin(time * 0.35 + i * 0.5) * 0.015 + Math.sin(time * 2.8 + i * 1.8) * 0.004;

    // Shake animation (amplified wind for 1 second, decaying)
    if (state.shakeActive) {
      state.shakeTimer += deltaTime;
      if (state.shakeTimer < 1.0) {
        const shakeIntensity = (1.0 - state.shakeTimer) * 3.0;
        windZ += Math.sin(state.shakeTimer * 30) * 0.08 * shakeIntensity;
        windX += Math.cos(state.shakeTimer * 25) * 0.06 * shakeIntensity;
      } else {
        state.shakeActive = false;
      }
    }

    tree.rotation.z = windZ;
    tree.rotation.x = windX;

    // Update falling coconuts (pi-wfm.9: gravity drop, multi-bounce, settle as collectible)
    for (let c = state.fallingCoconuts.length - 1; c >= 0; c--) {
      const fc = state.fallingCoconuts[c];
      fc.elapsed += deltaTime;

      // Apply gravity (9.8 m/s²)
      fc.velocity.y -= 9.8 * deltaTime;

      // Move coconut
      fc.mesh.position.x += fc.velocity.x * deltaTime;
      fc.mesh.position.y += fc.velocity.y * deltaTime;
      fc.mesh.position.z += fc.velocity.z * deltaTime;

      // Rotate while falling
      fc.mesh.rotation.x += deltaTime * 3;
      fc.mesh.rotation.z += deltaTime * 2;

      // Find rest height on sand via raycast
      if (fc.restY === null && sandModel) {
        const rayOrigin = new THREE.Vector3(fc.mesh.position.x, 50, fc.mesh.position.z);
        groundRaycaster.set(rayOrigin, downVector);
        const intersects = groundRaycaster.intersectObject(sandModel, true);
        fc.restY = intersects.length > 0 ? intersects[0].point.y + 0.4 : 2.0;
      }

      const groundY = fc.restY || 2.0;
      if (fc.mesh.position.y <= groundY) {
        fc.mesh.position.y = groundY;
        fc.bounces++;

        // Play thunk sound on each bounce
        playSound('collect_thunk');

        if (fc.bounces >= 2 || Math.abs(fc.velocity.y) < 0.5) {
          // Settle: remove falling mesh and spawn as collectible resource
          const finalPos = fc.mesh.position.clone();
          scene.remove(fc.mesh);
          fc.mesh.traverse(child => {
            if (child.geometry) child.geometry.dispose();
            if (child.material) {
              if (Array.isArray(child.material)) child.material.forEach(m => m.dispose());
              else child.material.dispose();
            }
          });
          spawnResource('coconut', finalPos);
          state.fallingCoconuts.splice(c, 1);
        } else {
          // Bounce with 0.2 restitution
          fc.velocity.y = Math.abs(fc.velocity.y) * 0.2;
          fc.velocity.x *= 0.5;
          fc.velocity.z *= 0.5;
        }
      }
    }
  });

  // Beach bum guitar strumming animation (with speed-up interaction)
  if (beachBumState.speedUpActive) {
    beachBumState.speedUpTimer += deltaTime;
    if (beachBumState.speedUpTimer >= BEACH_BUM.SPEED_UP_DURATION) {
      beachBumState.speedUpActive = false;
      // Only reset to normal if guitar solo is not active
      if (!beachBumState.guitarSoloActive) {
        beachBumState.strumSpeedMultiplier = 1.0;
        beachBumState.noteSpawnMultiplier = 1.0;
      }
    }
  }
  // Guitar solo animation (pi-nzq.9)
  if (beachBumState.guitarSoloActive) {
    beachBumState.guitarSoloTimer += deltaTime;
    if (beachBumState.guitarSoloTimer >= BEACH_BUM.GUITAR_SOLO_DURATION) {
      beachBumState.guitarSoloActive = false;
      beachBumState.strumSpeedMultiplier = 1.0;
      beachBumState.noteSpawnMultiplier = 1.0;
    } else {
      // Emit musical burst every 0.5s during solo
      beachBumState.guitarSoloBurstTimer += deltaTime;
      if (beachBumState.guitarSoloBurstTimer >= BEACH_BUM.GUITAR_SOLO_BURST_INTERVAL) {
        beachBumState.guitarSoloBurstTimer -= BEACH_BUM.GUITAR_SOLO_BURST_INTERVAL;
        const soloGuitarPos = new THREE.Vector3();
        beachBum.userData.guitar.getWorldPosition(soloGuitarPos);
        emitMusicalBurst(soloGuitarPos, 8);
        playGuitarFlourish(); // pi-3pk.1: Strum flourish on each solo burst
      }
    }
  }
  if (beachBumState.headTiltActive) {
    beachBumState.headTiltTimer += deltaTime;
    if (beachBumState.headTiltTimer >= BEACH_BUM.HEAD_TILT_DURATION) {
      beachBumState.headTiltActive = false;
    }
  }
  // --- Idle behavior cycle (pi-d7w.2) ---
  // Only advance idle state when no speed-up or guitar solo is active
  if (!beachBumState.speedUpActive && !beachBumState.guitarSoloActive) {
    beachBumState.idleTimer += deltaTime;
    if (beachBumState.idleTimer >= beachBumState.idleStateDuration) {
      const states = beachBumState.idleStates;
      const currentIdx = states.indexOf(beachBumState.idleBehavior);
      beachBumState.idleBehavior = states[(currentIdx + 1) % states.length];
      beachBumState.idleTimer = 0;
      beachBumState.idleStateDuration = 15 + Math.random() * 15;
    }
  }

  // Determine strum speed modifier based on idle behavior
  let idleStrumFactor = 1.0;
  let idleHeadRotX = 0;
  let idleHeadRotY = 0;
  const idleBehavior = beachBumState.idleBehavior;

  if (!beachBumState.speedUpActive && !beachBumState.guitarSoloActive) {
    switch (idleBehavior) {
      case 'stretch':
        idleStrumFactor = 0.3;
        idleHeadRotX = -0.15;  // Tilt head back
        break;
      case 'look':
        idleHeadRotY = Math.sin(time * 0.8) * 0.4;  // Slow look left/right
        break;
      case 'point':
        // Left hand extends outward (handled below)
        break;
      case 'sleep':
        idleStrumFactor = 0.1;
        idleHeadRotX = 0.2;  // Head tilts forward
        break;
      case 'strum':
      default:
        break;
    }
  }

  if (beachBum.userData.rightHand) {
    const effectiveStrumSpeed = beachBumState.strumSpeedMultiplier * idleStrumFactor;
    beachBum.userData.rightHand.rotation.z = Math.sin(time * 8 * effectiveStrumSpeed) * 0.3;
  }

  // Left hand pointing animation (pi-d7w.2)
  if (beachBum.userData.leftHand) {
    if (idleBehavior === 'point' && !beachBumState.speedUpActive && !beachBumState.guitarSoloActive) {
      // Extend left hand outward with smooth transition
      const pointTarget = -0.3;
      beachBum.userData.leftHand.position.x += ((-1.2) - beachBum.userData.leftHand.position.x) * 0.05;
      beachBum.userData.leftHand.position.y += ((0.9) - beachBum.userData.leftHand.position.y) * 0.05;
      beachBum.userData.leftHand.rotation.z += (pointTarget - beachBum.userData.leftHand.rotation.z) * 0.05;
    } else {
      // Return to default position
      beachBum.userData.leftHand.position.x += ((-0.7) - beachBum.userData.leftHand.position.x) * 0.05;
      beachBum.userData.leftHand.position.y += ((0.5) - beachBum.userData.leftHand.position.y) * 0.05;
      beachBum.userData.leftHand.rotation.z += (0 - beachBum.userData.leftHand.rotation.z) * 0.05;
    }
  }

  // Head animation combining tilt interaction + idle behavior
  if (beachBum.userData.head) {
    const interactionHeadTilt = beachBumState.headTiltActive ? Math.sin(beachBumState.headTiltTimer * 4) * 0.15 : 0;
    // Combine interaction tilt with idle head position
    beachBum.userData.head.rotation.x = interactionHeadTilt + idleHeadRotX;
    beachBum.userData.head.rotation.y = idleHeadRotY;
  }

  // pi-d7w.7: Beach Bum reaction animations
  if (beachBumState.reactionActive) {
    beachBumState.reactionTimer += deltaTime;
    const rt = beachBumState.reactionTimer;
    const rd = beachBumState.reactionDuration;

    if (rt >= rd) {
      // End reaction
      beachBumState.reactionActive = false;
      beachBumState.reactionType = null;
    } else if (beachBumState.reactionType === 'thumbsUp') {
      // Raise left hand: rotation change over 1.5s
      if (beachBum.userData.leftHand) {
        const raisePhase = Math.min(rt / 0.3, 1.0); // quick raise
        const lowerPhase = rt > rd - 0.3 ? (rt - (rd - 0.3)) / 0.3 : 0; // quick lower at end
        const handRotZ = raisePhase * (-0.8) * (1.0 - lowerPhase);
        beachBum.userData.leftHand.rotation.z = handRotZ;
        beachBum.userData.leftHand.position.y = 0.5 + raisePhase * 0.5 * (1.0 - lowerPhase);
      }
    } else if (beachBumState.reactionType === 'applause') {
      // Both hands raise: override strum animation for 2s
      const raisePhase = Math.min(rt / 0.3, 1.0);
      const lowerPhase = rt > rd - 0.3 ? (rt - (rd - 0.3)) / 0.3 : 0;
      const intensity = raisePhase * (1.0 - lowerPhase);
      if (beachBum.userData.leftHand) {
        beachBum.userData.leftHand.rotation.z = -0.8 * intensity;
        beachBum.userData.leftHand.position.y = 0.5 + 0.5 * intensity;
      }
      if (beachBum.userData.rightHand) {
        beachBum.userData.rightHand.rotation.z = 0.8 * intensity + Math.sin(rt * 12) * 0.15 * intensity;
        beachBum.userData.rightHand.position.y = 0.55 + 0.5 * intensity;
      }
    }
  }

  // Auto speech bubble on player idle (pi-d7w.2)
  const currentTime = performance.now() / 1000;
  if (!idleSpeechBubbleShown && lastPlayerClickTime > 0 && (currentTime - lastPlayerClickTime) >= INTERACTION.IDLE_SPEECH_THRESHOLD) {
    idleSpeechBubbleShown = true;
    const idleBumPos = new THREE.Vector3();
    beachBum.getWorldPosition(idleBumPos);
    idleBumPos.y += 3;
    showSpeechBubble("Try clicking something... anything!", idleBumPos, camera, 5000);
  }
  // Also trigger idle bubble if player has never clicked (after 30s of game time)
  if (!idleSpeechBubbleShown && lastPlayerClickTime === 0 && time >= INTERACTION.IDLE_SPEECH_THRESHOLD) {
    idleSpeechBubbleShown = true;
    const idleBumPos = new THREE.Vector3();
    beachBum.getWorldPosition(idleBumPos);
    idleBumPos.y += 3;
    showSpeechBubble("Try clicking something... anything!", idleBumPos, camera, 5000);
  }

  // pi-d7w.3: Beach bum gesture cues toward undiscovered content
  // Trigger when player is idle (15+ seconds) and cooldown (30s) has elapsed
  const idleSeconds = lastPlayerClickTime > 0
    ? (currentTime - lastPlayerClickTime)
    : time;  // If player never clicked, use game time
  const gestureCooldownElapsed = (currentTime - lastGestureCueTime) >= GESTURE_CUE_COOLDOWN;

  if (idleSeconds >= GESTURE_CUE_IDLE_THRESHOLD && gestureCooldownElapsed
      && !beachBumState.speedUpActive && !beachBumState.guitarSoloActive
      && !beachBumState.reactionActive) {
    const undiscovered = getDiscoverablePositions();
    if (undiscovered.length > 0) {
      // Find nearest undiscovered item to the beach bum
      const bumWorldPos = new THREE.Vector3();
      beachBum.getWorldPosition(bumWorldPos);
      let nearest = undiscovered[0];
      let nearestDist = bumWorldPos.distanceTo(nearest.position);
      for (let i = 1; i < undiscovered.length; i++) {
        const dist = bumWorldPos.distanceTo(undiscovered[i].position);
        if (dist < nearestDist) {
          nearest = undiscovered[i];
          nearestDist = dist;
        }
      }

      // Rotate beach bum head slightly toward the undiscovered item
      const dirToTarget = nearest.position.clone().sub(bumWorldPos).normalize();
      const targetAngle = Math.atan2(dirToTarget.x, dirToTarget.z);
      const baseRotY = Math.PI / 3;  // Original beach bum rotation.y
      beachBumState.gestureCueTargetRotY = baseRotY + (targetAngle - baseRotY) * 0.3; // Subtle turn
      beachBumState.gestureCueActive = true;
      beachBumState.gestureCueTimer = 0;
      beachBumState.gestureCueDuration = INTERACTION.GESTURE_CUE_DURATION;

      // Pick a random hint for this category
      const category = nearest.category;
      const hints = gestureCueHints[category] || gestureCueHints.objects;
      const hint = hints[Math.floor(Math.random() * hints.length)];

      // Show speech bubble with hint
      const cueBumPos = bumWorldPos.clone();
      cueBumPos.y += 3;
      showSpeechBubble(hint, cueBumPos, camera, 4000);

      lastGestureCueTime = currentTime;
    }
  }

  // Animate the gesture cue head turn (pi-d7w.3)
  if (beachBumState.gestureCueActive) {
    beachBumState.gestureCueTimer += deltaTime;
    if (beachBumState.gestureCueTimer >= beachBumState.gestureCueDuration) {
      beachBumState.gestureCueActive = false;
    } else if (beachBum.userData.head) {
      // Smooth head turn toward undiscovered item, then ease back
      const t = beachBumState.gestureCueTimer / beachBumState.gestureCueDuration;
      // Ease in-out: ramp up first 25%, hold 50%, ease down last 25%
      let intensity;
      if (t < 0.25) {
        intensity = t / 0.25;
      } else if (t < 0.75) {
        intensity = 1.0;
      } else {
        intensity = 1.0 - (t - 0.75) / 0.25;
      }
      const baseRotY = Math.PI / 3;
      const headTurnY = (beachBumState.gestureCueTargetRotY - baseRotY) * intensity;
      beachBum.userData.head.rotation.y += headTurnY;
    }
  }

  // Update speech bubble position each frame (pi-d7w.1)
  const beachBumHeadPos = beachBum.position.clone();
  beachBumHeadPos.y += 3;
  updateSpeechBubble(beachBumHeadPos, camera);

  // Spawn music notes periodically (with beach bum interaction multiplier)
  // Sleep state suppresses note spawning (pi-d7w.2)
  const sleepSuppression = (idleBehavior === 'sleep' && !beachBumState.speedUpActive && !beachBumState.guitarSoloActive);
  if (!sleepSuppression && Math.random() < BEACH_BUM.NOTE_SPAWN_CHANCE * beachBumState.noteSpawnMultiplier) {
    const note = createMusicNote();

    // Position near guitar
    const guitarWorldPos = new THREE.Vector3();
    beachBum.userData.guitar.getWorldPosition(guitarWorldPos);

    note.position.set(
      guitarWorldPos.x + (Math.random() - 0.5) * 2,
      guitarWorldPos.y,
      guitarWorldPos.z + (Math.random() - 0.5) * 2
    );

    // Store velocity and lifetime — slow drift, long lifetime (pi-nzq.7)
    note.userData.velocity = new THREE.Vector3(
      (Math.random() - 0.5) * 0.1,
      0.15 + Math.random() * 0.1,    // 0.15-0.25 upward velocity
      (Math.random() - 0.5) * 0.1
    );
    note.userData.life = 0;
    note.userData.maxLife = BEACH_BUM.NOTE_MAX_LIFE_MIN + Math.random() * (BEACH_BUM.NOTE_MAX_LIFE_MAX - BEACH_BUM.NOTE_MAX_LIFE_MIN);  // 8-10 seconds
    note.userData.rotationSpeed = (Math.random() - 0.5) * 0.5;

    scene.add(note);
    musicNotes.push(note);

    // Register as interactable for click catching (pi-nzq.7)
    registerInteractable(note, {
      type: 'object',
      id: 'music_note',
      boundingRadius: 0.8,
      cooldown: 100,
      onClick: () => handleMusicNoteCatch(note),
    });
  }

  // Update music notes (pi-nzq.7: magnetism + catching)
  // Compute pointer ray for magnetism
  const pointerNDC = getPointerNDC();
  magnetismRaycaster.setFromCamera(pointerNDC, camera);
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const magnetRadius = isTouchDevice ? INTERACTION.MUSIC_NOTE_MAGNET_RADIUS_TOUCH : INTERACTION.MUSIC_NOTE_MAGNET_RADIUS_MOUSE;
  const magnetSpeed = INTERACTION.MUSIC_NOTE_MAGNET_SPEED;

  for (let i = musicNotes.length - 1; i >= 0; i--) {
    const note = musicNotes[i];
    note.userData.life += deltaTime;

    // Magnetism: find closest point on pointer ray to this note
    const ray = magnetismRaycaster.ray;
    ray.closestPointToPoint(note.position, _tv1);
    const distToPointer = note.position.distanceTo(_tv1);

    if (distToPointer < magnetRadius && distToPointer > 0.1) {
      // Drift toward the closest point on the ray
      _tv2.copy(_tv1).sub(note.position).normalize();
      note.position.add(_tv2.multiplyScalar(magnetSpeed * deltaTime));
    }

    // Move note upward with drift (slow)
    note.position.addScaledVector(note.userData.velocity, deltaTime);

    // Gentle floating motion
    note.position.x += Math.sin(time * 0.5 + i) * 0.005;
    note.position.z += Math.cos(time * 0.5 + i) * 0.005;

    // Rotate note
    note.rotation.y += note.userData.rotationSpeed * deltaTime;

    // Fade out as it rises
    const lifeFraction = note.userData.life / note.userData.maxLife;
    const opacity = 1.0 - lifeFraction;
    note.traverse((child) => {
      if (child.material) {
        child.material.opacity = opacity;
        child.material.transparent = true;
      }
    });

    // Remove when life expires
    if (note.userData.life >= note.userData.maxLife) {
      unregisterInteractable(note);
      scene.remove(note);
      musicNotes.splice(i, 1);
    }
  }

  // --- Object interaction animations (M3) ---

  // Treasure chest wobble (3 cycles over 0.5s)
  if (chestState.wobbleActive) {
    chestState.wobbleTimer += deltaTime;
    if (chestState.wobbleTimer < 0.5) {
      const wobblePhase = chestState.wobbleTimer / 0.5 * Math.PI * 6; // 3 full cycles
      const decay = 1.0 - chestState.wobbleTimer / 0.5;
      treasureChest.rotation.z = Math.PI / 4 + Math.sin(wobblePhase) * 0.08 * decay;
      treasureChest.rotation.x = Math.cos(wobblePhase * 0.7) * 0.05 * decay;
    } else {
      chestState.wobbleActive = false;
      treasureChest.rotation.z = Math.PI / 4;
      treasureChest.rotation.x = 0;
    }
  }

  // Floating gems (rise + fade)
  for (let g = chestState.floatingGems.length - 1; g >= 0; g--) {
    const fg = chestState.floatingGems[g];
    fg.elapsed += deltaTime;
    const t = fg.elapsed / fg.duration;
    fg.mesh.position.y = fg.startY + t * 3.0;
    fg.mesh.rotation.y += deltaTime * 3;
    fg.mesh.rotation.x += deltaTime * 2;
    fg.mesh.material.opacity = 1.0 - t;
    if (t >= 1.0) {
      scene.remove(fg.mesh);
      chestState.floatingGems.splice(g, 1);
    }
  }

  // Welcome sign wobble (2 cycles over 0.8s)
  if (signWobbleActive) {
    signWobbleTimer += deltaTime;
    if (signWobbleTimer < 0.8) {
      const wobblePhase = signWobbleTimer / 0.8 * Math.PI * 4;
      const decay = 1.0 - signWobbleTimer / 0.8;
      welcomeSign.rotation.z = Math.sin(wobblePhase) * 0.06 * decay;
    } else {
      signWobbleActive = false;
      welcomeSign.rotation.z = 0;
    }
  }

  // Beer bottle tip/roll animations (pi-nzq.8: enhanced with flash, emissive pulse, recycling)
  bottleState.forEach((state, i) => {
    const bottle = beerBottles[i];

    // Roll animation
    if (state.rollActive) {
      state.rollTimer += deltaTime;
      if (state.rollTimer < 0.8) {
        // Smooth interpolation toward target tilt
        const t = state.rollTimer / 0.8;
        bottle.rotation.x += (state.tiltTarget - bottle.rotation.x) * 0.1;
        // Add slight roll on Y for fallen bottles
        if (!state.isStanding) {
          bottle.rotation.y += Math.sin(state.rollTimer * 10) * 0.02 * (1.0 - t);
        }
      } else {
        state.rollActive = false;
        bottle.rotation.x = state.tiltTarget;
      }
    }

    // Specular flash animation (0.3s restore)
    if (state.flashActive) {
      state.flashTimer += deltaTime;
      if (state.flashTimer >= 0.3) {
        state.flashActive = false;
        const glassMesh = bottle.children[0];
        if (glassMesh && glassMesh.material) {
          glassMesh.material.metalness = state.originalMetalness;
          glassMesh.material.roughness = state.originalRoughness;
        }
      }
    }

    // Emissive pulse for recycler discovery
    if (state.emissivePulseActive) {
      const glassMesh = bottle.children[0];
      if (glassMesh && glassMesh.material) {
        const pulse = (Math.sin(time * 6) + 1) * 0.5; // 0-1 oscillation
        glassMesh.material.emissive = _bottleEmissiveColor;
        glassMesh.material.emissiveIntensity = pulse * 0.8;
      }
    }
  });

  // Floating glass shards (rise + fade, same pattern as gem float)
  for (let s = floatingShards.length - 1; s >= 0; s--) {
    const fs = floatingShards[s];
    fs.elapsed += deltaTime;
    const t = fs.elapsed / fs.duration;
    fs.mesh.position.y = fs.startY + t * 3.0;
    fs.mesh.rotation.y += deltaTime * 3;
    fs.mesh.rotation.x += deltaTime * 2;
    fs.mesh.material.opacity = 0.8 * (1.0 - t);
    if (t >= 1.0) {
      scene.remove(fs.mesh);
      floatingShards.splice(s, 1);
    }
  }

  // Bottle reset: remaining bottles slowly return to standing over 120 seconds
  if (bottleResetActive) {
    bottleResetTimer += deltaTime;
    const resetProgress = Math.min(bottleResetTimer / ANIMATION.BOTTLE_RESET_DURATION, 1.0);
    bottleState.forEach((state, i) => {
      const bottle = beerBottles[i];
      if (bottle.parent === scene) { // Only reset bottles still in scene
        // Lerp tilt toward upright (0)
        bottle.rotation.x = state.tiltTarget * (1.0 - resetProgress);
        if (resetProgress >= 1.0) {
          state.isStanding = true;
          state.tiltTarget = 0;
          state.emissivePulseActive = false;
          // Turn off emissive
          const glassMesh = bottle.children[0];
          if (glassMesh && glassMesh.material) {
            glassMesh.material.emissive = new THREE.Color(0x000000);
            glassMesh.material.emissiveIntensity = 0;
          }
        }
      }
    });
    if (resetProgress >= 1.0) {
      bottleResetActive = false;
    }
  }

  // Update Meshy model animations
  updateAnimations(meshyModels, deltaTime);

  // Update water splash particles (GPU InstancedMesh - single draw call)
  let splashVisibleCount = 0;
  for (let i = 0; i < splashParticleData.count; i++) {
    if (!splashParticleData.active[i]) continue;

    // Update life
    splashParticleData.lives[i] += deltaTime;

    // Apply gravity
    splashParticleData.velocities[i * 3 + 1] += -15 * deltaTime;

    // Update position
    splashParticleData.positions[i * 3] += splashParticleData.velocities[i * 3] * deltaTime;
    splashParticleData.positions[i * 3 + 1] += splashParticleData.velocities[i * 3 + 1] * deltaTime;
    splashParticleData.positions[i * 3 + 2] += splashParticleData.velocities[i * 3 + 2] * deltaTime;

    // Check if expired or below water
    if (splashParticleData.lives[i] >= splashParticleData.maxLives[i] ||
        splashParticleData.positions[i * 3 + 1] < -0.5) {
      splashParticleData.active[i] = 0;
      continue;
    }

    // Calculate opacity-based scale (fade out by shrinking)
    const lifeFraction = splashParticleData.lives[i] / splashParticleData.maxLives[i];
    const scale = splashParticleData.scales[i] * (1.0 - lifeFraction * 0.7);

    // Update instance matrix
    splashPosition.set(
      splashParticleData.positions[i * 3],
      splashParticleData.positions[i * 3 + 1],
      splashParticleData.positions[i * 3 + 2]
    );
    splashScale.setScalar(scale);
    splashMatrix.compose(splashPosition, splashQuaternion, splashScale);
    splashInstancedMesh.setMatrixAt(splashVisibleCount, splashMatrix);
    splashVisibleCount++;
  }
  splashInstancedMesh.count = splashVisibleCount;
  if (splashVisibleCount > 0) {
    splashInstancedMesh.instanceMatrix.needsUpdate = true;
  }

  // Update sand particles (GPU InstancedMesh - single draw call)
  let sandVisibleCount = 0;
  for (let i = 0; i < sandParticleData.count; i++) {
    if (!sandParticleData.active[i]) continue;

    // Update life
    sandParticleData.lives[i] += deltaTime;

    // Apply gravity
    sandParticleData.velocities[i * 3 + 1] += -8 * deltaTime;

    // Update position
    sandParticleData.positions[i * 3] += sandParticleData.velocities[i * 3] * deltaTime;
    sandParticleData.positions[i * 3 + 1] += sandParticleData.velocities[i * 3 + 1] * deltaTime;
    sandParticleData.positions[i * 3 + 2] += sandParticleData.velocities[i * 3 + 2] * deltaTime;

    // Check if expired
    if (sandParticleData.lives[i] >= sandParticleData.maxLives[i]) {
      sandParticleData.active[i] = 0;
      continue;
    }

    // Calculate opacity-based scale (fade out by shrinking)
    const lifeFraction = sandParticleData.lives[i] / sandParticleData.maxLives[i];
    const scale = sandParticleData.scales[i] * (1.0 - lifeFraction);

    // Update instance matrix
    sandPosition.set(
      sandParticleData.positions[i * 3],
      sandParticleData.positions[i * 3 + 1],
      sandParticleData.positions[i * 3 + 2]
    );
    sandScale.setScalar(scale);
    sandMatrix.compose(sandPosition, splashQuaternion, sandScale);
    sandInstancedMesh.setMatrixAt(sandVisibleCount, sandMatrix);
    sandVisibleCount++;
  }
  sandInstancedMesh.count = sandVisibleCount;
  if (sandVisibleCount > 0) {
    sandInstancedMesh.instanceMatrix.needsUpdate = true;
  }

  // Update sparkle particles (GPU InstancedMesh - single draw call)
  let sparkleVisibleCount = 0;
  for (let i = 0; i < sparkleParticleData.count; i++) {
    if (!sparkleParticleData.active[i]) continue;

    // Update life
    sparkleParticleData.lives[i] += deltaTime;

    // Update position (no gravity — sparkles float upward)
    sparkleParticleData.positions[i * 3] += sparkleParticleData.velocities[i * 3] * deltaTime;
    sparkleParticleData.positions[i * 3 + 1] += sparkleParticleData.velocities[i * 3 + 1] * deltaTime;
    sparkleParticleData.positions[i * 3 + 2] += sparkleParticleData.velocities[i * 3 + 2] * deltaTime;

    // Check if expired
    if (sparkleParticleData.lives[i] >= sparkleParticleData.maxLives[i]) {
      sparkleParticleData.active[i] = 0;
      continue;
    }

    // Calculate scale (fade out by shrinking) with gentle spin oscillation
    const lifeFraction = sparkleParticleData.lives[i] / sparkleParticleData.maxLives[i];
    const spinOscillation = 1.0 + Math.sin(sparkleParticleData.lives[i] * 12) * 0.15;
    const scale = sparkleParticleData.scales[i] * (1.0 - lifeFraction) * spinOscillation;

    // Update instance matrix
    sparklePosition.set(
      sparkleParticleData.positions[i * 3],
      sparkleParticleData.positions[i * 3 + 1],
      sparkleParticleData.positions[i * 3 + 2]
    );
    sparkleScale.setScalar(scale);
    sparkleMatrix.compose(sparklePosition, splashQuaternion, sparkleScale);
    sparkleInstancedMesh.setMatrixAt(sparkleVisibleCount, sparkleMatrix);
    sparkleVisibleCount++;
  }
  sparkleInstancedMesh.count = sparkleVisibleCount;
  if (sparkleVisibleCount > 0) {
    sparkleInstancedMesh.instanceMatrix.needsUpdate = true;
  }

  // Update musical burst particles (GPU InstancedMesh - single draw call)
  let musicVisibleCount = 0;
  for (let i = 0; i < musicParticleData.count; i++) {
    if (!musicParticleData.active[i]) continue;

    // Update life
    musicParticleData.lives[i] += deltaTime;

    // Spiral motion
    const angle = musicParticleData.spiralAngles[i] + musicParticleData.lives[i] * 8;
    const spiralRadius = 0.5;
    musicParticleData.positions[i * 3] += Math.cos(angle) * spiralRadius * deltaTime;
    musicParticleData.positions[i * 3 + 1] += musicParticleData.velocities[i * 3 + 1] * deltaTime;
    musicParticleData.positions[i * 3 + 2] += Math.sin(angle) * spiralRadius * deltaTime;

    // Check if expired
    if (musicParticleData.lives[i] >= musicParticleData.maxLives[i]) {
      musicParticleData.active[i] = 0;
      continue;
    }

    // Calculate scale (fade out by shrinking)
    const lifeFraction = musicParticleData.lives[i] / musicParticleData.maxLives[i];
    const scale = musicParticleData.scales[i] * (1.0 - lifeFraction);

    // Update instance matrix
    musicPosition.set(
      musicParticleData.positions[i * 3],
      musicParticleData.positions[i * 3 + 1],
      musicParticleData.positions[i * 3 + 2]
    );
    musicScale.setScalar(scale);
    musicMatrix.compose(musicPosition, splashQuaternion, musicScale);
    musicInstancedMesh.setMatrixAt(musicVisibleCount, musicMatrix);
    musicVisibleCount++;
  }
  musicInstancedMesh.count = musicVisibleCount;
  if (musicVisibleCount > 0) {
    musicInstancedMesh.instanceMatrix.needsUpdate = true;
  }

  // --- pi-t0j.10: Update butterfly orbits ---
  updateButterflies(time);

  // --- pi-d7w.4: Beach bum hum proximity check (every 60 frames) ---
  humFrameCounter++;
  if (humFrameCounter >= HUM_CHECK_INTERVAL) {
    humFrameCounter = 0;
    checkHumProximity();
  }

  // --- Hidden secrets animate updates ---

  // pi-3rt.1: Seashell camera-distance visibility
  if (seashell) {
    const distToSeashell = camera.position.distanceTo(seashell.position);
    seashell.visible = distToSeashell <= INTERACTION.SECRET_CAMERA_DISTANCE;
  }

  // pi-3rt.2: Hermit crab visibility (same camera-distance rule as seashell)
  if (hermitCrabGroup) {
    const distToHermit = camera.position.distanceTo(hermitCrabGroup.position);
    hermitCrabGroup.visible = distToHermit <= INTERACTION.SECRET_CAMERA_DISTANCE;
  }

  // pi-3rt.4: Message bottle bobbing (only while in ocean, before washed ashore)
  if (messageBottleGroup && messageBottleClickCount === 0) {
    messageBottleGroup.position.y = 0.1 + Math.sin(time * 1.5) * 0.15;
  }

  // pi-3rt.4: Message paper faces camera after unfurling
  if (messagePaper && messageBottleClickCount === 2 && !messageBottleAnimating) {
    messagePaper.lookAt(camera.position);
  }

  // Update resource spawner (timer-based spawns, max 2 per frame)
  updateResourceSpawner();

  // Update interaction system (hover raycasting, throttled to every 3 frames)
  updateInteraction();

  controls.update();
  composer.render();
}

// ============================================================================
// WINDOW RESIZE
// ============================================================================

window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
  composer.setSize(window.innerWidth, window.innerHeight);
  bloomPass.resolution.set(
    Math.round(window.innerWidth / 2),
    Math.round(window.innerHeight / 2)
  );
});

// ============================================================================
// START
// ============================================================================

animate();

// Performance monitoring (WebGL 2.0 stats)
function logPerformanceStats() {
  const info = renderer.info;
  console.log('=== WebGL 2.0 Performance Stats ===');
  console.log(`Draw calls: ${info.render.calls}`);
  console.log(`Triangles: ${info.render.triangles}`);
  console.log(`Points: ${info.render.points}`);
  console.log(`Lines: ${info.render.lines}`);
  console.log(`Geometries: ${info.memory.geometries}`);
  console.log(`Textures: ${info.memory.textures}`);
  console.log('===================================');
}

// Log stats after first few frames have rendered
requestAnimationFrame(() => requestAnimationFrame(logPerformanceStats));

// Make it available globally for debugging
window.logPerformanceStats = logPerformanceStats;

console.log('Welcome to Palm Island!');
